import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useSSE } from "../hooks/useSSE";
import { Link, useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import {
  ArrowLeft, Plus, Edit2, Trash2, Search, X, Save,
  Package, AlertTriangle, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, RotateCcw, BarChart3, Tag, FileText, Printer, Ruler, ScanBarcode, DollarSign,
  Settings, Layers, Zap, RefreshCcw, Factory, FlaskConical, CalendarClock, ChevronLeft, ToggleLeft, ToggleRight,
  Image as ImageIcon, Upload, Building2, Globe, EyeOff, Eye, Warehouse,
} from "lucide-react";
import { formatMoneyInput, normalizeMoneyValue } from "../components/ui/MoneyInput";
import { resolveLogoForContext } from "../lib/logoUtils";
import DateInput from "../components/ui/DateInput";
import TransferenciaStockModal from "../components/TransferenciaStockModal";
import StockGrupoModal from "../components/StockGrupoModal";
import useDepositos from "../hooks/useDepositos";
import DepositosPanel from "../components/DepositosPanel";
import TransferenciaDepositoModal from "../components/TransferenciaDepositoModal";
import StockDepositosTabla from "../components/StockDepositosTabla";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Categoría estándar de servicios (sin stock): acepta variantes ("SERVICIO", "servicios"…)
const esCatServicio = (n) => /^\s*servicios?\s*$/i.test(String(n || "").normalize("NFD").replace(/[\u0300-\u036f]/g, ""));

const LOGOS = [
  { value: "todas",     label: "Todas" },
  { value: "arandujar", label: "Arandu&JAR" },
  { value: "arandu",    label: "Arandu" },
  { value: "jar",       label: "JAR" },
];
const LOGO_CHIP = {
  arandujar: "bg-blue-600/20 text-blue-300 border-blue-600/30",
  arandu:    "bg-emerald-600/20 text-emerald-300 border-emerald-600/30",
  jar:       "bg-red-600/20 text-red-300 border-red-600/30",
};
const LOGO_LABEL = { arandujar: "A&JAR", arandu: "Arandu", jar: "JAR" };

const IVA_OPCIONES = [
  { value: "exenta", label: "Exenta (0%)" },
  { value: "5",      label: "IVA 5%" },
  { value: "10",     label: "IVA 10%" },
];

const emptyForm = {
  nombre: "", descripcion: "", sku: "", codigo_barras: "", categoria: "",
  precio_costo: "", ganancia_pct: "", precio_venta: "", moneda: "PYG",
  stock_actual: "0", stock_inicial_view: "", stock_minimo: "0",
  logo_tipo: "arandujar", activo: true,
  iva_tipo: "10",
  descuento_maximo: "0", descuento_vence: "",
  unidad_medida_id: null,
  subcategoria_id: null,
  atributos: {},
  variantes_habilitadas: false,
  variante_atributos: [],
  variante_opciones: {},
  variantes: [],
  var_costo_general: "",
  var_ganancia_general: "",
  // Trazabilidad
  codigo_fabricante: "", lote: "", fecha_vencimiento: "",
  imagen_url: "",
  proveedor_id: "",
  // Recetario agronómico
  requiere_receta: false, ficha_ingrediente: "", ficha_clase_tox: "", ficha_carencia: "", ficha_dosis: "", ficha_unidad_dosis: "",
};

const emptyCatForm = { nombre: "", tiene_codigo_fabricante: false, tiene_lote: false, tiene_vencimiento: false, atributos_habilitados: [] };

const emptySub = { categoria: "", nombre: "", prefijo: "", activo: true };
const emptyAttrForm = { nombre: "", opciones: [], nuevaOpcion: "", activo: true };
const emptyConfigSku = { modo_codigo: "manual", sep: "-", longitud_seq: 3, usar_subcategorias: false, usar_atributos: false, permitir_nombres_duplicados: false };
const emptyBarcodeConfig = {
  barcode_tipo_default: "ean13_interno",
  barcode_prefijo_ean13: "200",
  barcode_quiet_zone_mm: 5,
  barcode_permitir_elegir_tipo: true,
  barcode_recomendado: "ean13_interno",
  barcode_recomendado_mensaje: "Recomendado: EAN-13 interno con prefijo 200, 13 dígitos exactos, dígito verificador automático y quiet zone mínima de 5mm.",
  etiqueta_modelo_impresion: "a4",
};

const emptyMov = { tipo: "entrada", cantidad: "", motivo: "ajuste_manual", precio_unitario: "", notas: "", tipo_factura_id: "" };

// Motivos seleccionables para Ajuste de stock manual (válidos tanto para Entrada como para Salida)
const MOTIVOS_AJUSTE_STOCK = [
  { value: "ajuste_manual",         label: "Ajuste de inventario" },
  { value: "donacion",              label: "Donación" },
  { value: "rotura_merma",          label: "Rotura / Merma" },
  { value: "vencimiento",           label: "Vencimiento" },
  { value: "uso_interno",           label: "Uso interno / Consumo propio" },
  { value: "correccion_inventario", label: "Corrección de inventario físico" },
  { value: "otro",                  label: "Otro" },
];
const motivoAjusteLabel = (value) => MOTIVOS_AJUSTE_STOCK.find(m => m.value === value)?.label || (value || "").replace(/_/g, " ");

const emptyUnidadForm = {
  codigo: "", nombre: "", sigla: "", cantidad: "1", peso_kg: "", cod_dnit: ""
};

function fmtPYG(n) {
  if (!n && n !== 0) return "-";
  if (n >= 1_000_000) return `₲ ${(n / 1_000_000).toFixed(2)}M`;
  return `₲ ${Math.round(Number(n)).toLocaleString("es-PY")}`;
}

const MONEDA_SIMBOLO = { PYG: "₲", USD: "US$", BRL: "R$" };

// Igual que fmtPYG pero respeta la moneda propia del producto (₲, USD, R$).
function fmtMoneda(n, moneda = "PYG") {
  if (!n && n !== 0) return "-";
  const m = moneda || "PYG";
  if (m === "PYG") return fmtPYG(n);
  const sym = MONEDA_SIMBOLO[m] || m;
  return `${sym} ${Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Redimensiona/comprime una imagen en el navegador antes de subirla (máx. 800px
// de lado más largo, JPEG calidad 0.82) — pensado para no guardar archivos
// pesados en la base de datos ni en el catálogo completo que usan otras pantallas.
function comprimirImagenProducto(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) { reject(new Error("El archivo debe ser una imagen")); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onerror = () => reject(new Error("No se pudo procesar la imagen"));
      img.onload = () => {
        const MAX_LADO = 800;
        let { width, height } = img;
        if (width > MAX_LADO || height > MAX_LADO) {
          const ratio = Math.min(MAX_LADO / width, MAX_LADO / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Los valores de los campos numéricos se guardan en el estado en formato "máquina":
// punto decimal, sin separador de miles ("22.50", "150000"). Si llega con coma
// (formato es-PY, "22,50" o "1.250,50") se interpreta como decimal con punto de miles.
// Un valor con varios puntos ("1.250.000") se toma como miles.
function parseNumberInput(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") return value;
  let t = String(value).trim().replace(/\s/g, "");
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  else if ((t.match(/\./g) || []).length > 1) t = t.replace(/\./g, "");
  return parseFloat(t);
}

function formatPYGInputValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = parseNumberInput(value);
  if (Number.isNaN(n)) return String(value || "");
  return Math.round(n).toLocaleString("es-PY");
}

function normalizePYGTyping(value) {
  const raw = String(value || "").replace(/[^\d.,]/g, "");
  return formatPYGInputValue(raw);
}

function calcSugerido(costo, pct, moneda = "PYG") {
  const c = parseNumberInput(costo);
  const p = parseNumberInput(pct);
  if (!c || isNaN(c) || c <= 0 || isNaN(p) || p < 0) return null;
  const v = c * (1 + p / 100);
  // Guaraníes: entero. USD / R$: 2 decimales.
  return (moneda || "PYG") === "PYG" ? Math.round(v) : Math.round(v * 100) / 100;
}

function calcGananciaPct(costo, venta) {
  const c = parseNumberInput(costo);
  const v = parseNumberInput(venta);
  if (!c || isNaN(c) || c <= 0 || isNaN(v)) return "";
  return ((v - c) / c * 100).toFixed(2).replace(/\.00$/, "");
}

function variantSearchText(v = {}) {
  const detalle = Array.isArray(v.atributos_detalle)
    ? v.atributos_detalle.flatMap(d => [
        d.atributo_nombre,
        d.opcion_nombre,
        `${d.atributo_nombre || ""} ${d.opcion_nombre || ""}`,
        `${d.atributo_nombre || ""}: ${d.opcion_nombre || ""}`,
      ])
    : [];
  return [
    v.atributos_texto,
    ...detalle,
    String(v.stock_actual ?? ""),
    String(v.stock_minimo ?? ""),
    String(v.precio_costo ?? ""),
    String(v.precio_venta ?? ""),
    v.activo === false ? "inactivo" : "activo",
  ].filter(Boolean).join(" ");
}

function buildVariantRows(attrs, selectedAttrIds, currentRows = [], defaults = {}, selectedOptionsByAttr = {}) {
  const selected = selectedAttrIds.map(id => attrs.find(a => a.id === id)).filter(Boolean);
  if (selected.length === 0) return [];
  const optionsByAttr = {};
  for (const attr of selected) {
    const selectedOptions = selectedOptionsByAttr?.[attr.id] || [];
    const opts = (attr.opciones || [])
      .filter(o => o.activo !== false)
      .filter(o => selectedOptions.includes(o.id));
    if (opts.length === 0) return [];
    optionsByAttr[attr.id] = opts;
  }
  const combos = selected.reduce((acc, attr) => {
    const opts = optionsByAttr[attr.id] || [];
    return acc.flatMap(base => opts.map(opt => ({ ...base, [attr.id]: opt.id })));
  }, [{}]);
  const currentByKey = new Map((currentRows || []).map(row => [
    selectedAttrIds.map(id => row.atributos?.[id] || "").join("|"),
    row,
  ]));
  return combos.map(combo => {
    const key = selectedAttrIds.map(id => combo[id] || "").join("|");
    const old = currentByKey.get(key) || {};
    const detalle = selected.map(attr => {
      const opt = (attr.opciones || []).find(o => o.id === combo[attr.id]);
      return { atributo_id: attr.id, atributo_nombre: attr.nombre, opcion_id: opt?.id, opcion_nombre: opt?.nombre };
    });
    return {
      id: old.id || null,
      atributos: combo,
      atributos_detalle: detalle,
      atributos_texto: detalle.map(d => `${d.atributo_nombre}: ${d.opcion_nombre}`).join(" · "),
      stock_actual: old.stock_actual ?? "0",
      stock_minimo: old.stock_minimo ?? "0",
      precio_costo: old.precio_costo ?? (defaults.var_costo || defaults.precio_costo) ?? "",
      precio_venta: old.precio_venta ?? (() => {
        const c = old.precio_costo ?? (defaults.var_costo || defaults.precio_costo);
        const g = defaults.var_ganancia;
        const sug = g !== undefined && g !== "" ? calcSugerido(c, g, defaults.moneda) : null;
        return sug !== null ? String(sug) : (defaults.precio_venta ?? "");
      })(),
      ganancia_pct: old.ganancia_pct ?? ((defaults.var_ganancia !== undefined && defaults.var_ganancia !== "") ? defaults.var_ganancia : calcGananciaPct(old.precio_costo ?? defaults.precio_costo, old.precio_venta ?? defaults.precio_venta)),
      costo_manual: !!old.costo_manual,
      ganancia_manual: !!old.ganancia_manual,
      activo: old.activo !== false,
    };
  });
}

function variantOptionsFromRows(rows = [], selectedAttrIds = []) {
  const result = {};
  selectedAttrIds.forEach(attrId => { result[attrId] = []; });
  (rows || []).forEach(row => {
    selectedAttrIds.forEach(attrId => {
      const optionId = row.atributos?.[attrId];
      if (optionId && !result[attrId].includes(optionId)) result[attrId].push(optionId);
    });
  });
  return result;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const BARCODE_TYPES = {
  ean13_interno: "EAN-13 interno",
  code128_interno: "Code 128 interno",
  manual: "Manual / comercial",
};
const BARCODE_LABELS_MAX = 15;

function ean13CheckDigit(base12) {
  if (!/^\d{12}$/.test(base12)) return null;
  const total = base12.split("").reduce((sum, ch, idx) => sum + Number(ch) * (idx % 2 === 0 ? 1 : 3), 0);
  return String((10 - (total % 10)) % 10);
}

function isValidEan13(code) {
  return /^\d{13}$/.test(code || "") && ean13CheckDigit(code.slice(0, 12)) === code.slice(12);
}

const EAN_L = {
  0: "0001101", 1: "0011001", 2: "0010011", 3: "0111101", 4: "0100011",
  5: "0110001", 6: "0101111", 7: "0111011", 8: "0110111", 9: "0001011",
};
const EAN_G = {
  0: "0100111", 1: "0110011", 2: "0011011", 3: "0100001", 4: "0011101",
  5: "0111001", 6: "0000101", 7: "0010001", 8: "0001001", 9: "0010111",
};
const EAN_R = {
  0: "1110010", 1: "1100110", 2: "1101100", 3: "1000010", 4: "1011100",
  5: "1001110", 6: "1010000", 7: "1000100", 8: "1001000", 9: "1110100",
};
const EAN_PARITY = {
  0: "LLLLLL", 1: "LLGLGG", 2: "LLGGLG", 3: "LLGGGL", 4: "LGLLGG",
  5: "LGGLLG", 6: "LGGGLL", 7: "LGLGLG", 8: "LGLGGL", 9: "LGGLGL",
};

function ean13Bits(code) {
  if (!isValidEan13(code)) return "";
  const digits = code.split("").map(Number);
  const parity = EAN_PARITY[digits[0]];
  let bits = "101";
  for (let i = 1; i <= 6; i += 1) bits += parity[i - 1] === "L" ? EAN_L[digits[i]] : EAN_G[digits[i]];
  bits += "01010";
  for (let i = 7; i <= 12; i += 1) bits += EAN_R[digits[i]];
  bits += "101";
  return bits;
}

function ean13Svg(code, { height = 74, module = 2, quietMm = 5, showText = true, fontSize = 14 } = {}) {
  const bits = ean13Bits(code);
  if (!bits) return "";
  const quietModules = Math.max(10, Math.ceil((Number(quietMm) || 5) * 3.78 / module));
  const width = (bits.length + quietModules * 2) * module;
  const textHeight = showText ? 18 : 0;
  const bars = bits.split("").map((b, idx) => b === "1"
    ? `<rect x="${(quietModules + idx) * module}" y="0" width="${module}" height="${height}" fill="#000"/>`
    : "").join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height + textHeight}" viewBox="0 0 ${width} ${height + textHeight}">
    <rect width="100%" height="100%" fill="#fff"/>
    ${bars}
    ${showText ? `<text x="${width / 2}" y="${height + textHeight - 4}" text-anchor="middle" font-family="monospace" font-size="${fontSize}" fill="#111">${code}</text>` : ""}
  </svg>`;
}


// Code 128 (set B, ASCII 32-126) — para códigos manuales que no son EAN-13.
const C128_PATTERNS = ["212222","222122","222221","121223","121322","131222","122213","122312","132212","221213","221312","231212","112232","122132","122231","113222","123122","123221","223211","221132","221231","213212","223112","312131","311222","321122","321221","312212","322112","322211","212123","212321","232121","111323","131123","131321","112313","132113","132311","211313","231113","231311","112133","112331","132131","113123","113321","133121","313121","211331","231131","213113","213311","213131","311123","311321","331121","312113","312311","332111","314111","221411","431111","111224","111422","121124","121421","141122","141221","112214","112412","122114","122411","142112","142211","241211","221114","413111","241112","134111","111242","121142","121241","114212","124112","124211","411212","421112","421211","212141","214121","412121","111143","111341","131141","114113","114311","411113","411311","113141","114131","311141","411131","211412","211214","211232","2331112"];
function code128Bits(code) {
  const str = String(code || "");
  if (!str || !/^[\x20-\x7e]+$/.test(str)) return "";
  const vals = [104];
  let sum = 104;
  for (let i = 0; i < str.length; i += 1) {
    const v = str.charCodeAt(i) - 32;
    vals.push(v);
    sum += v * (i + 1);
  }
  vals.push(sum % 103, 106);
  let bits = "";
  vals.forEach(v => {
    const pat = C128_PATTERNS[v];
    pat.split("").forEach((w, i) => { bits += (i % 2 === 0 ? "1" : "0").repeat(Number(w)); });
  });
  return bits;
}

function code128Svg(code, { height = 74, module = 2, showText = true, fontSize = 14 } = {}) {
  const bits = code128Bits(code);
  if (!bits) return "";
  const quietModules = 10;
  const width = (bits.length + quietModules * 2) * module;
  const textHeight = showText ? 18 : 0;
  let rects = "";
  let i = 0;
  while (i < bits.length) {
    if (bits[i] === "1") {
      let j = i;
      while (j < bits.length && bits[j] === "1") j += 1;
      rects += `<rect x="${(quietModules + i) * module}" y="0" width="${(j - i) * module}" height="${height}" fill="#000"/>`;
      i = j;
    } else i += 1;
  }
  const safe = String(code).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height + textHeight}" viewBox="0 0 ${width} ${height + textHeight}">
    <rect width="100%" height="100%" fill="#fff"/>
    ${rects}
    ${showText ? `<text x="${width / 2}" y="${height + textHeight - 4}" text-anchor="middle" font-family="monospace" font-size="${fontSize}" fill="#111">${safe}</text>` : ""}
  </svg>`;
}

// EAN-13 si el código es válido; si no, Code 128.
function barcodeSvg(code, opts = {}) {
  return isValidEan13(code) ? ean13Svg(code, opts) : code128Svg(code, opts);
}

// Diseño de etiqueta vacío — ancho/alto en mm, igual que lo que pide el
// (nota: ya no hay modelos fijos — ver colección disenos_etiqueta / componente de abajo)
// backend (routes/disenos_etiqueta.py). Valores default razonables para
// arrancar un diseño nuevo desde cero.
const emptyDisenoEtiqueta = {
  nombre: "",
  ancho_hoja_mm: 110,
  ancho_etiqueta_mm: 33,
  alto_etiqueta_mm: 22,
  columnas: 3,
  espacio_h_mm: 4,
  espacio_v_mm: 4,
  margen_inicial_mm: 4,
  margen_superior_mm: 4,
  mostrar_empresa: true,
  mostrar_logo: false,
  logo_alto_mm: 0,     // 0 = automático (chico)
  barras_alto_mm: 0,   // 0 = automático según alto de etiqueta
  mostrar_producto: true,
  mostrar_sku: true,
  mostrar_precio: true,
};

// Alturas efectivas (mm) del logo y de las barras — una sola fórmula para
// imprimir y para la vista previa. Las barras tienen alto FIJO, igual para
// EAN-13 y Code 128, así el nombre del producto nunca queda cortado.
function etiquetaAlturasMm(diseno) {
  const alto = Number(diseno.alto_etiqueta_mm) || 22;
  const logo = Number(diseno.logo_alto_mm) > 0 ? Number(diseno.logo_alto_mm) : Math.max(3, alto * 0.14);
  const barras = Number(diseno.barras_alto_mm) > 0 ? Number(diseno.barras_alto_mm) : Math.max(5, alto * 0.28);
  return { logo, barras };
}

// CSS de la hoja/rollo de etiquetas a partir de un diseño — una sola fuente
// de verdad, usada tanto para imprimir de verdad como para la vista previa
// en vivo del editor de diseños.
function buildEtiquetaCss(diseno, fonts) {
  const anchoEtq = Number(diseno.ancho_etiqueta_mm) || 33;
  const altoEtq = Number(diseno.alto_etiqueta_mm) || 22;
  const columnas = Math.max(1, Number(diseno.columnas) || 1);
  const espacioH = Number(diseno.espacio_h_mm) || 0;
  const espacioV = Number(diseno.espacio_v_mm) || 0;
  const margenIni = Number(diseno.margen_inicial_mm) || 0;
  const margenSup = Number(diseno.margen_superior_mm) || 0;
  return `
    @page { size: auto; margin: ${margenSup}mm 0 0 ${margenIni}mm; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #fff; font-family: Arial, sans-serif; color: #000; }
    .sheet {
      display: grid;
      grid-template-columns: repeat(${columnas}, ${anchoEtq}mm);
      column-gap: ${espacioH}mm;
      row-gap: ${espacioV}mm;
      padding: ${margenSup}mm 0 0 ${margenIni}mm;
    }
    .label {
      width: ${anchoEtq}mm;
      height: ${altoEtq}mm;
      overflow: hidden;
      padding: 0.6mm 1mm;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      break-inside: avoid;
    }
    .logo { height: ${etiquetaAlturasMm(diseno).logo}mm; max-width: 90%; width: auto; object-fit: contain; margin-bottom: 0.3mm; flex-shrink: 0; }
    .empresa {
      font-size: ${fonts.empresa}px; font-weight: 800; color: #000;
      text-transform: uppercase; letter-spacing: 0.2px; width: 100%;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .barcode-box { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; flex-shrink: 0; }
    .barcode-box svg { width: 100%; height: ${etiquetaAlturasMm(diseno).barras}mm; display: block; }
    .barcode-box .code-text { line-height: 1; margin-top: 0.2mm; }
    .code-text { font-size: ${fonts.sku}px; font-family: monospace; color: #000; }
    .nombre {
      font-size: ${fonts.producto}px; line-height: 1.1; font-weight: 700; color: #000;
      margin-top: 0.3mm; width: 100%; overflow: hidden; word-break: break-word;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    }
    .sku { font-size: ${fonts.sku}px; color: #555; margin-top: 0.2mm; font-family: monospace; }
    .precio { font-size: ${fonts.precio}px; font-weight: 800; color: #000; margin-top: 0.3mm; }
    @media print { body { margin: 0; } }
  `;
}

// Tamaño de letra automático según el tamaño de la etiqueta (misma fórmula
// usada al imprimir — ver autoFontSizesPx dentro del componente — pero esta
// copia a nivel de módulo es la que usa la vista previa en vivo del editor,
// para no depender de ningún estado del componente).
function autoFontSizesPxPreview(anchoMm, altoMm) {
  const base = Math.max(4, Math.min(Number(anchoMm) || 33, Number(altoMm) || 22));
  return {
    empresa: Math.max(5, Math.min(12, base * 0.26)),
    producto: Math.max(6, Math.min(14, base * 0.30)),
    sku: Math.max(5, Math.min(10, base * 0.20)),
    precio: Math.max(7, Math.min(16, base * 0.34)),
  };
}

// Vista previa en vivo del diseño de etiqueta — se re-renderiza al toque
// con cualquier cambio de medida o casilla, mostrando una fila de
// etiquetas a escala con datos de ejemplo (el código de barras se
// representa con un patrón de barras, no hace falta generar uno real
// para ver cómo queda el layout).
function EtiquetaPreview({ diseno, empresaNombre }) {
  const PX_PER_MM = 3.2;
  const ancho = Math.max(5, Number(diseno.ancho_etiqueta_mm) || 33);
  const alto = Math.max(5, Number(diseno.alto_etiqueta_mm) || 22);
  const gap = Math.max(0, Number(diseno.espacio_h_mm) || 0);
  const cols = Math.max(1, Math.min(8, Number(diseno.columnas) || 1));
  const fonts = autoFontSizesPxPreview(ancho, alto);
  const alturas = etiquetaAlturasMm(diseno);
  const anchoPx = ancho * PX_PER_MM;
  const altoPx = alto * PX_PER_MM;
  const gapPx = gap * PX_PER_MM;

  return (
    <div className="bg-white rounded-lg p-4 overflow-x-auto">
      <div style={{ display: "flex", gap: `${gapPx}px` }}>
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} style={{
            width: `${anchoPx}px`, height: `${altoPx}px`,
            border: "1px dashed #aaa", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", overflow: "hidden",
            padding: "2px", textAlign: "center", background: "#fff", flexShrink: 0,
          }}>
            {diseno.mostrar_logo && (
              <div style={{ height: `${alturas.logo * PX_PER_MM}px`, width: `${alturas.logo * PX_PER_MM * 2}px`, maxWidth: "90%", fontSize: 7, color: "#999", border: "1px solid #ccc", borderRadius: 2, marginBottom: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>logo</div>
            )}
            {diseno.mostrar_empresa && (
              <div style={{ fontSize: fonts.empresa, fontWeight: 800, color: "#000", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                {empresaNombre}
              </div>
            )}
            <div style={{
              width: "80%", height: `${alturas.barras * PX_PER_MM}px`, margin: "2px 0 0", flexShrink: 0,
              background: "repeating-linear-gradient(90deg, #000 0px, #000 1px, #fff 1px, #fff 2px)",
            }} />
            <div style={{ fontSize: fonts.sku, fontFamily: "monospace", lineHeight: 1 }}>7890000000012</div>
            {diseno.mostrar_producto && (
              <div style={{ fontSize: fonts.producto, fontWeight: 700, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                Producto Ejemplo
              </div>
            )}
            {diseno.mostrar_sku && (
              <div style={{ fontSize: fonts.sku, color: "#555", fontFamily: "monospace" }}>SKU-0001</div>
            )}
            {diseno.mostrar_precio && (
              <div style={{ fontSize: fonts.precio, fontWeight: 800, color: "#000" }}>₲ 15.000</div>
            )}
          </div>
        ))}
      </div>
      <p className="text-slate-500 text-[11px] mt-2">
        {cols} columna(s) de {ancho}×{alto}mm — vista previa a escala, con datos de ejemplo.
      </p>
    </div>
  );
}

export default function ProductosPage() {
  const { token, hasPermission, hasModule, user, activeEmpresaPropia } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [chips, setChips] = useState([]);
  const [soloConStock, setSoloConStock] = useState(() => { try { return localStorage.getItem("productos_solo_con_stock") === "1"; } catch { return false; } });
  // Ver el stock de UN depósito (solo si la empresa usa depósitos). "" = stock total.
  const [depFiltro, setDepFiltro] = useState("");
  const [stockOp, setStockOp] = useState("");        // "" | eq | gte | lte  → filtro por cantidad de stock
  const [stockCant, setStockCant] = useState("");
  const toggleSoloConStock = (v) => { setSoloConStock(v); try { localStorage.setItem("productos_solo_con_stock", v ? "1" : "0"); } catch {} };
  // Catálogo del Productos — página actual (búsqueda y paginación resueltas en el servidor)
  const PRODUCTOS_POR_PAGINA = 100;
  const [productosPagina, setProductosPagina] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginasProductos, setTotalPaginasProductos] = useState(1);
  const [totalProductosFiltrados, setTotalProductosFiltrados] = useState(0);
  const primerFiltroRef = useRef(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [procesandoImagen, setProcesandoImagen] = useState(false);
  const imagenInputRef = useRef(null);

  const handleImagenProducto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("La imagen debe pesar menos de 8 MB"); return; }
    setProcesandoImagen(true);
    try {
      const dataUrl = await comprimirImagenProducto(file);
      setFormData(f => ({ ...f, imagen_url: dataUrl }));
    } catch (err) {
      toast.error(err.message || "No se pudo procesar la imagen");
    } finally {
      setProcesandoImagen(false);
    }
  };

  // Movimientos modal
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [movimientos, setMovimientos]   = useState([]);
  const [showMovModal, setShowMovModal] = useState(false);
  const [showMovForm, setShowMovForm]   = useState(false);
  const [movForm, setMovForm]           = useState(emptyMov);
  const [savingMov, setSavingMov]       = useState(false);
  const [loadingMov, setLoadingMov]     = useState(false);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [tiposFactura, setTiposFactura] = useState([]); // Obras (Tipo de Factura) para imputar consumo de stock

  // Tabs y catálogos
  const [activeTab, setActiveTab] = useState("productos");
  const [unidadesLista, setUnidadesLista] = useState([]);
  const [unidadesAll, setUnidadesAll] = useState([]);
  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [showUnidadForm, setShowUnidadForm] = useState(false);
  const [editingUnidadId, setEditingUnidadId] = useState(null);
  const [unidadForm, setUnidadForm] = useState(emptyUnidadForm);
  const [savingUnidad, setSavingUnidad] = useState(false);

  // Historial de costos
  const [historialCostos, setHistorialCostos] = useState([]);
  const [loadingHistCostos, setLoadingHistCostos] = useState(false);
  const [histCostoChips, setHistCostoChips] = useState([]);
  const [histCostoInput, setHistCostoInput] = useState("");
  const [histModalProducto, setHistModalProducto] = useState(null);
  const [histModalData, setHistModalData] = useState([]);
  const [loadingHistModal, setLoadingHistModal] = useState(false);

  // Códigos de barras
  const [barcodeFilter, setBarcodeFilter] = useState("todos");
  const [barcodeSearch, setBarcodeSearch] = useState("");
  const [generatingCode, setGeneratingCode] = useState(null);
  const [barcodeGenerateType, setBarcodeGenerateType] = useState("");
  const barcodeInputRef = useRef(null);

  // Subcategorías y config productos
  const [subcategorias, setSubcategorias] = useState([]);
  const [configSku, setConfigSku] = useState(emptyConfigSku);
  const [barcodeConfig, setBarcodeConfig] = useState(emptyBarcodeConfig);
  const [savingBarcodeConfig, setSavingBarcodeConfig] = useState(false);
  // Diseños de etiqueta (plantillas de impresión configurables, por empresa)
  const [disenosEtiqueta, setDisenosEtiqueta] = useState([]);
  const [showDisenoForm, setShowDisenoForm] = useState(false);
  const [editingDisenoId, setEditingDisenoId] = useState(null);
  const [disenoForm, setDisenoForm] = useState(emptyDisenoEtiqueta);
  const [savingDiseno, setSavingDiseno] = useState(false);
  const [savingConfigSku, setSavingConfigSku] = useState(false);
  const [showSubForm, setShowSubForm] = useState(false);
  const [editingSubId, setEditingSubId] = useState(null);
  const [subForm, setSubForm] = useState(emptySub);
  const [savingSub, setSavingSub] = useState(false);
  const [skuPreview, setSkuPreview] = useState(null);
  const [loadingSkuPreview, setLoadingSkuPreview] = useState(false);

  // Categorías de productos
  const [categorias, setCategorias] = useState([]);
  const [configSubTab, setConfigSubTab] = useState(null); // null | "categorias" | "subcategorias" | "atributos" | "sku" | "barcode"
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [catForm, setCatForm] = useState(emptyCatForm);
  const [savingCat, setSavingCat] = useState(false);
  const [atributosProductos, setAtributosProductos] = useState([]);
  const [showAttrForm, setShowAttrForm] = useState(false);
  const [editingAttrId, setEditingAttrId] = useState(null);
  const [attrForm, setAttrForm] = useState(emptyAttrForm);
  const [savingAttr, setSavingAttr] = useState(false);
  const [variantOptionFilters, setVariantOptionFilters] = useState({});
  const [showTransferencia, setShowTransferencia] = useState(false);
  const dep = useDepositos(token, activeEmpresaPropia?.slug);
  const [depTransf, setDepTransf] = useState(null);        // null | { inicial? }
  const [depRefresh, setDepRefresh] = useState(0);
  const [showStockGrupo, setShowStockGrupo] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // Proveedores del Catastro (para el campo "Proveedor" de la ficha del producto)
  const [proveedoresList, setProveedoresList] = useState([]);
  useEffect(() => {
    if (!token) return;
    const q = new URLSearchParams({ es_proveedor: "true" });
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    fetch(`${API}/admin/entidades?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setProveedoresList((Array.isArray(d) ? d : []).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))))
      .catch(() => setProveedoresList([]));
  }, [token, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Catálogo completo, sin paginar — usado solo para el indicador "bajo mínimo" del
  // header y para la pestaña Códigos de Barra (necesita ver TODO el catálogo, no una página).
  const fetchProductos = async () => {
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/productos?${q}`, { headers });
    if (res.ok) setProductos(await res.json());
  };

  // Tabla de Productos — paginada (100 por página) con búsqueda resuelta en el servidor.
  // Los "chips" + lo que esté tipeado se mandan como `terminos` y el backend los combina
  // con AND (cada uno matcheando cualquiera de los campos de siempre), igual que antes
  // se hacía en el navegador, pero ahora contra el catálogo completo sin traerlo entero.
  const fetchProductosPagina = async (pagina = 1) => {
    setLoading(true);
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    q.set("page", String(pagina));
    q.set("page_size", String(PRODUCTOS_POR_PAGINA));
    [...chips, search.trim()].filter(Boolean).forEach(t => q.append("terminos", t));
    if (soloConStock) q.set("con_stock", "true");
    if (stockOp && stockCant !== "" && !isNaN(Number(stockCant))) { q.set("stock_op", stockOp); q.set("stock_cantidad", String(Number(stockCant))); }
    if (depFiltro) q.set("deposito_id", depFiltro);
    const res = await fetch(`${API}/admin/productos?${q}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setProductosPagina(data.productos || []);
      setTotalPaginasProductos(data.pages || 1);
      setTotalProductosFiltrados(data.total || 0);
    }
    setLoading(false);
  };

  useEffect(() => { fetchProductos(); }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  // Cambios de chips/búsqueda: esperar 300ms y volver a la página 1
  useEffect(() => {
    if (primerFiltroRef.current) { primerFiltroRef.current = false; return; }
    const t = setTimeout(() => {
      if (paginaActual !== 1) setPaginaActual(1); // dispara el fetch vía el efecto de abajo
      else fetchProductosPagina(1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chips, search, soloConStock, stockOp, stockCant, depFiltro, activeEmpresaPropia?.slug]);

  // Cambio de página (Anterior/Siguiente): fetch inmediato, sin debounce
  useEffect(() => {
    fetchProductosPagina(paginaActual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginaActual]);

  // ── Unidades de medida ────────────────────────────────────────
  const canVerUnidades = hasPermission("unidades_medida.ver");
  const canCrearUnidades = hasPermission("unidades_medida.crear");
  const canEditarUnidades = hasPermission("unidades_medida.editar");
  const canEliminarUnidades = hasPermission("unidades_medida.eliminar");

  const fetchUnidadesLista = async () => {
    const res = await fetch(`${API}/admin/unidades-medida/lista`, { headers });
    if (res.ok) setUnidadesLista(await res.json());
  };

  const fetchUnidadesAll = async () => {
    setLoadingUnidades(true);
    const res = await fetch(`${API}/admin/unidades-medida/`, { headers });
    if (res.ok) setUnidadesAll(await res.json());
    setLoadingUnidades(false);
  };

  useEffect(() => { fetchUnidadesLista(); }, []); // eslint-disable-line

  // Cargar fuente de códigos de barras
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Libre+Barcode+128+Text&display=swap";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  // ── Subcategorías y categorías ────────────────────────────────
  const canVerConfig  = hasPermission("configuracion_productos.ver");
  const canGestSubs   = hasPermission("configuracion_productos.gestionar_subcategorias");
  const canGestCats   = hasPermission("configuracion_productos.gestionar_categorias");
  const canDeshabCats = hasPermission("configuracion_productos.deshabilitar_categoria");
  const canHacerGlobalCat = hasPermission("configuracion_productos.hacer_global_categoria");
  const canGestAttrs  = hasPermission("configuracion_productos.gestionar_atributos");
  const canConfigSku  = hasPermission("configuracion_productos.codigo_automatico");
  const canToggleClasificaciones = hasPermission("configuracion_productos.habilitar_clasificaciones");
  const canGestionarUnicidad = hasPermission("configuracion_productos.gestionar_unicidad");
  const canVerDisenos = hasPermission("configuracion_productos.ver_disenos_etiqueta") || canVerConfig;
  const canCrearDisenos = hasPermission("configuracion_productos.crear_disenos_etiqueta");
  const canEditarDisenos = hasPermission("configuracion_productos.editar_disenos_etiqueta");
  const canEliminarDisenos = hasPermission("configuracion_productos.eliminar_disenos_etiqueta");
  const usaSubcategorias = configSku.usar_subcategorias !== false;
  const usaAtributos = configSku.usar_atributos !== false;
  const subcategoriasActivas = usaSubcategorias ? subcategorias.filter(s => s.activo !== false) : [];
  const atributosActivos = usaAtributos ? atributosProductos.filter(a => a.activo !== false) : [];

  const fetchSubcategorias = useCallback(async () => {
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/subcategorias-productos?${q}`, { headers });
    if (res.ok) setSubcategorias(await res.json());
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const fetchCategorias = useCallback(async () => {
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    q.set("incluir_deshabilitadas", "true");
    const res = await fetch(`${API}/admin/categorias-productos?${q}`, { headers });
    if (res.ok) setCategorias(await res.json());
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const fetchAtributos = useCallback(async () => {
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/atributos-productos?${q}`, { headers });
    if (res.ok) setAtributosProductos(await res.json());
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const fetchTiposFactura = useCallback(async () => {
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/tipos-factura?${q}`, { headers });
    if (res.ok) setTiposFactura(await res.json());
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  useEffect(() => { fetchTiposFactura(); }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const fetchConfigSku = useCallback(async () => {
    if (!(canVerConfig || canConfigSku || canToggleClasificaciones || canGestionarUnicidad)) return;
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/config-productos?${q}`, { headers });
    if (res.ok) {
      const cfg = await res.json();
      setConfigSku(cfg);
      setBarcodeConfig({ ...emptyBarcodeConfig, ...cfg });
      setBarcodeGenerateType(cfg.barcode_tipo_default || "ean13_interno");
    }
  }, [activeEmpresaPropia?.slug, canVerConfig, canConfigSku, canToggleClasificaciones, canGestionarUnicidad]); // eslint-disable-line

  const fetchDisenosEtiqueta = useCallback(async () => {
    if (!canVerDisenos) return;
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/disenos-etiqueta?${q}`, { headers });
    if (res.ok) setDisenosEtiqueta(await res.json());
  }, [activeEmpresaPropia?.slug, canVerDisenos]); // eslint-disable-line

  useEffect(() => {
    fetchSubcategorias();
    fetchCategorias();
    fetchAtributos();
    fetchConfigSku();
    fetchDisenosEtiqueta();
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const handleSaveConfigSku = async (e) => {
    e.preventDefault();
    setSavingConfigSku(true);
    const payload = { ...configSku, logo_tipo: activeEmpresaPropia?.slug || "" };
    const res = await fetch(`${API}/admin/config-productos`, { method: "PUT", headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success("Configuración guardada");
      setConfigSku(await res.json());
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSavingConfigSku(false);
  };

  const handleSaveBarcodeConfig = async (e) => {
    e.preventDefault();
    setSavingBarcodeConfig(true);
    const payload = { ...configSku, ...barcodeConfig, logo_tipo: activeEmpresaPropia?.slug || "" };
    const res = await fetch(`${API}/admin/config-productos`, { method: "PUT", headers, body: JSON.stringify(payload) });
    if (res.ok) {
      const cfg = await res.json();
      setConfigSku(cfg);
      setBarcodeConfig({ ...emptyBarcodeConfig, ...cfg });
      setBarcodeGenerateType(cfg.barcode_tipo_default || "ean13_interno");
      toast.success("Configuración de códigos guardada");
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSavingBarcodeConfig(false);
  };

  // ── Diseños de etiqueta (CRUD) ──────────────────────────────────
  const openNuevoDiseno = () => {
    setEditingDisenoId(null);
    setDisenoForm(emptyDisenoEtiqueta);
    setShowDisenoForm(true);
  };

  const openEditarDiseno = (d) => {
    setEditingDisenoId(d.id);
    setDisenoForm({ ...emptyDisenoEtiqueta, ...d });
    setShowDisenoForm(true);
  };

  const handleGuardarDiseno = async (e) => {
    e.preventDefault();
    setSavingDiseno(true);
    const payload = { ...disenoForm, logo_tipo: activeEmpresaPropia?.slug || "" };
    const url = editingDisenoId ? `${API}/admin/disenos-etiqueta/${editingDisenoId}` : `${API}/admin/disenos-etiqueta`;
    const method = editingDisenoId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingDisenoId ? "Diseño actualizado" : "Diseño creado");
      setShowDisenoForm(false);
      await fetchDisenosEtiqueta();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar el diseño");
    }
    setSavingDiseno(false);
  };

  const handleEliminarDiseno = async (d) => {
    if (!window.confirm(`¿Eliminar el diseño "${d.nombre}"? Esta acción no se puede deshacer.`)) return;
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/disenos-etiqueta/${d.id}?${q}`, { method: "DELETE", headers });
    if (res.ok) {
      toast.success("Diseño eliminado");
      await fetchDisenosEtiqueta();
      await fetchConfigSku(); // por si ese diseño era el seleccionado, refrescar la config
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "No se pudo eliminar el diseño");
    }
  };

  const handleSaveUsoClasificaciones = async (changes) => {
    if (!canToggleClasificaciones) {
      toast.error("No tenés permiso para habilitar o deshabilitar clasificaciones");
      return;
    }
    const next = { ...configSku, ...changes, logo_tipo: activeEmpresaPropia?.slug || "" };
    const res = await fetch(`${API}/admin/config-productos`, { method: "PUT", headers, body: JSON.stringify(next) });
    if (res.ok) {
      const cfg = await res.json();
      setConfigSku(cfg);
      setBarcodeConfig({ ...emptyBarcodeConfig, ...cfg });
      toast.success("Uso de clasificaciones actualizado");
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar configuración");
    }
  };

  const handleSaveUnicidadProductos = async (permitir) => {
    if (!canGestionarUnicidad) {
      toast.error("No tenés permiso para cambiar la regla de nombres duplicados");
      return;
    }
    const next = { permitir_nombres_duplicados: !!permitir, logo_tipo: activeEmpresaPropia?.slug || configSku.logo_tipo || "" };
    const res = await fetch(`${API}/admin/config-productos`, { method: "PUT", headers, body: JSON.stringify(next) });
    if (res.ok) {
      const cfg = await res.json();
      setConfigSku(cfg);
      setBarcodeConfig({ ...emptyBarcodeConfig, ...cfg });
      fetchConfigSku();
      toast.success("Regla de nombres actualizada");
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar configuración");
    }
  };

  const openNewSub = () => {
    setEditingSubId(null);
    setSubForm({ ...emptySub, categoria: formData.categoria || "" });
    setShowSubForm(true);
  };
  const openEditSub = (s) => {
    setEditingSubId(s.id);
    setSubForm({ categoria: s.categoria, nombre: s.nombre, prefijo: s.prefijo || "", activo: s.activo !== false });
    setShowSubForm(true);
  };
  const handleSaveSub = async (e) => {
    e.preventDefault();
    setSavingSub(true);
    const payload = { ...subForm, logo_tipo: activeEmpresaPropia?.slug || "" };
    const url = editingSubId
      ? `${API}/admin/subcategorias-productos/${editingSubId}`
      : `${API}/admin/subcategorias-productos`;
    const method = editingSubId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingSubId ? "Subcategoría actualizada" : "Subcategoría creada");
      setShowSubForm(false);
      fetchSubcategorias();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSavingSub(false);
  };
  const handleDeleteSub = async (s) => {
    if (!window.confirm(`¿Eliminar la subcategoría "${s.nombre}"? Los productos que la usen quedarán sin subcategoría.`)) return;
    const res = await fetch(`${API}/admin/subcategorias-productos/${s.id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Subcategoría eliminada"); fetchSubcategorias(); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar");
    }
  };

  // ── Categorías CRUD ───────────────────────────────────────────
  const openNewCat = () => {
    setEditingCatId(null);
    setCatForm(emptyCatForm);
    setShowCatForm(true);
  };
  const openEditCat = (c) => {
    setEditingCatId(c.id);
    setCatForm({
      nombre: c.nombre,
      tiene_codigo_fabricante: !!c.tiene_codigo_fabricante,
      tiene_lote: !!c.tiene_lote,
      tiene_vencimiento: !!c.tiene_vencimiento,
      atributos_habilitados: c.atributos_habilitados || [],
    });
    setShowCatForm(true);
  };
  const handleSaveCat = async (e) => {
    e.preventDefault();
    setSavingCat(true);
    const payload = { ...catForm, logo_tipo: activeEmpresaPropia?.slug || "" };
    const url    = editingCatId ? `${API}/admin/categorias-productos/${editingCatId}` : `${API}/admin/categorias-productos`;
    const method = editingCatId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingCatId ? "Categoría actualizada" : "Categoría creada");
      setShowCatForm(false);
      fetchCategorias();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSavingCat(false);
  };
  const handleToggleDeshabilitarCat = async (c) => {
    const nuevo = !c.deshabilitada;
    const res = await fetch(`${API}/admin/categorias-productos/${c.id}/deshabilitada`, {
      method: "PATCH", headers, body: JSON.stringify({ deshabilitada: nuevo }),
    });
    if (res.ok) { toast.success(nuevo ? `"${c.nombre}" deshabilitada para esta empresa` : `"${c.nombre}" habilitada`); fetchCategorias(); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "No se pudo cambiar el estado");
    }
  };
  const handleHacerGlobalCat = async (c) => {
    if (!window.confirm(`¿Convertir "${c.nombre}" en categoría GLOBAL?\n\nPasará a existir en TODAS las empresas. Cada empresa podrá deshabilitarla, pero no eliminarla.`)) return;
    const res = await fetch(`${API}/admin/categorias-productos/${c.id}/hacer-global`, { method: "POST", headers });
    if (res.ok) { toast.success(`"${c.nombre}" ahora es global`); fetchCategorias(); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "No se pudo convertir en global");
    }
  };
  const handleDeleteCat = async (c) => {
    if (!window.confirm(`¿Eliminar la categoría "${c.nombre}"?`)) return;
    const res = await fetch(`${API}/admin/categorias-productos/${c.id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Categoría eliminada"); fetchCategorias(); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar");
    }
  };

  // ── Atributos CRUD ───────────────────────────────────────────
  const openNewAttr = () => {
    setEditingAttrId(null);
    setAttrForm(emptyAttrForm);
    setShowAttrForm(true);
  };
  const openEditAttr = (a) => {
    setEditingAttrId(a.id);
    setAttrForm({
      nombre: a.nombre || "",
      opciones: (a.opciones || []).map(o => ({ id: o.id, nombre: o.nombre || "", activo: o.activo !== false })),
      nuevaOpcion: "",
      activo: a.activo !== false,
    });
    setShowAttrForm(true);
  };
  const addAttrOption = () => {
    const nombre = (attrForm.nuevaOpcion || "").trim();
    if (!nombre) return;
    if ((attrForm.opciones || []).some(o => (o.nombre || "").trim().toLowerCase() === nombre.toLowerCase())) {
      toast.error("Esa opción ya existe");
      return;
    }
    setAttrForm(f => ({
      ...f,
      opciones: [...(f.opciones || []), { id: `tmp-${Date.now()}`, nombre, activo: true }],
      nuevaOpcion: "",
    }));
  };
  const updateAttrOption = (idx, nombre) => {
    setAttrForm(f => ({
      ...f,
      opciones: (f.opciones || []).map((o, i) => i === idx ? { ...o, nombre } : o),
    }));
  };
  const removeAttrOption = (idx) => {
    setAttrForm(f => ({ ...f, opciones: (f.opciones || []).filter((_, i) => i !== idx) }));
  };
  const handleSaveAttr = async (e) => {
    e.preventDefault();
    const opciones = (attrForm.opciones || [])
      .map(o => ({ ...o, id: String(o.id || "").startsWith("tmp-") ? "" : o.id, nombre: (o.nombre || "").trim() }))
      .filter(o => o.nombre);
    if (opciones.length === 0) {
      toast.error("Agregá al menos una opción para el atributo");
      return;
    }
    setSavingAttr(true);
    const payload = { nombre: attrForm.nombre, opciones, activo: attrForm.activo !== false, logo_tipo: activeEmpresaPropia?.slug || "" };
    const url = editingAttrId ? `${API}/admin/atributos-productos/${editingAttrId}` : `${API}/admin/atributos-productos`;
    const method = editingAttrId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingAttrId ? "Atributo actualizado" : "Atributo creado");
      setShowAttrForm(false);
      fetchAtributos();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSavingAttr(false);
  };
  const handleDeleteAttr = async (a) => {
    if (a.sistema) {
      toast.error("Los atributos predeterminados no se pueden eliminar");
      return;
    }
    if (!window.confirm(`¿Eliminar el atributo "${a.nombre}"?`)) return;
    const res = await fetch(`${API}/admin/atributos-productos/${a.id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Atributo eliminado"); fetchAtributos(); fetchCategorias(); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar");
    }
  };

  const attrById = (id) => atributosProductos.find(a => a.id === id);
  const attrActivoById = (id) => atributosActivos.find(a => a.id === id);
  const categoriaActual = categorias.find(c => c.nombre === formData.categoria);
  // Selectores: ocultan las categorías deshabilitadas (salvo la que ya tiene el producto en edición)
  const categoriasSelect = categorias.filter(c => !c.deshabilitada || c.nombre === formData.categoria);
  const atributosCategoria = usaAtributos
    ? (categoriaActual?.atributos_habilitados || []).map(attrActivoById).filter(Boolean)
    : [];
  const loadSkuPreview = async (subId) => {
    if (!subId || configSku.modo_codigo !== "automatico") { setSkuPreview(null); return; }
    setLoadingSkuPreview(true);
    const q = new URLSearchParams({ subcategoria_id: subId });
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/productos/preview-sku?${q}`, { headers });
    if (res.ok) setSkuPreview(await res.json());
    setLoadingSkuPreview(false);
  };

  // ── Derived from form ─────────────────────────────────────────
  const esServicio = esCatServicio(formData.categoria);
  const puedeCrearServicio = hasPermission("inventario_productos.crear_servicio");
  const puedeStockInicial = hasPermission("inventario_productos.stock_inicial");
  const puedeAjustarStock = hasPermission("inventario_productos.ajustar_stock");
  const puedeVerCostoCompra = hasPermission("inventario_productos.ver_costo_compra");
  // El costo se carga a mano solo al crear; luego viene de las compras (o requiere permiso granular)
  const costoBloqueado = !!editingId && !hasPermission("inventario_productos.editar_costo");
  const costoBloqueadoProps = costoBloqueado ? { disabled: true, title: "El costo se actualiza desde las compras. Requiere el permiso 'Editar costo' para modificarlo a mano." } : {};
  const puedeGestionarDescuentos = hasPermission("inventario_productos.gestionar_descuentos");
  const canGenerarEtiquetas = hasPermission("inventario_productos.generar_etiquetas");
  const canGenerarBarcode = hasPermission("inventario_productos.generar_codigo_barras") || canGenerarEtiquetas;
  const canEditarBarcode = hasPermission("inventario_productos.editar_codigo_barras");
  const canEliminarBarcode = hasPermission("inventario_productos.eliminar_codigo_barras");
  const canConfigBarcode = hasPermission("configuracion_productos.configurar_codigos_barras");
  const canVerHistorialCostos = hasPermission("historial_costos.ver");

  const precioSugerido = calcSugerido(formData.precio_costo, formData.ganancia_pct, formData.moneda);

  // Auto-fill precio_venta when ganancia_pct or precio_costo changes
  const handleGananciaChange = (val) => {
    const sugerido = calcSugerido(formData.precio_costo, val, formData.moneda);
    setFormData(f => ({
      ...f,
      ganancia_pct: val,
      ...(sugerido !== null ? { precio_venta: String(sugerido) } : {}),
    }));
  };
  const handleCostoChange = (val) => {
    const sugerido = calcSugerido(val, formData.ganancia_pct, formData.moneda);
    setFormData(f => ({
      ...f,
      precio_costo: val,
      ...(sugerido !== null ? { precio_venta: String(sugerido) } : {}),
    }));
  };
  const handleCategoriaChange = (val) => {
    const cat = categorias.find(c => c.nombre === val);
    const habilitados = new Set(cat?.atributos_habilitados || []);
    setFormData(f => ({
      ...f,
      categoria: val,
      subcategoria_id: null,
      atributos: Object.fromEntries(Object.entries(f.atributos || {}).filter(([attrId]) => habilitados.has(attrId))),
      variante_atributos: (f.variante_atributos || []).filter(attrId => habilitados.has(attrId)),
      variante_opciones: Object.fromEntries(Object.entries(f.variante_opciones || {}).filter(([attrId]) => habilitados.has(attrId))),
      variantes: [],
      // Si es Servicios, limpiar stock
      ...(esCatServicio(val) ? { stock_actual: "0", stock_minimo: "0", variantes_habilitadas: false, variante_atributos: [], variantes: [] } : {}),
    }));
    setVariantOptionFilters(filters => Object.fromEntries(Object.entries(filters || {}).filter(([attrId]) => habilitados.has(attrId))));
    setSkuPreview(null);
  };
  const toggleVarianteAtributo = (attrId, checked) => {
    if (!checked) {
      setVariantOptionFilters(filters => {
        const next = { ...(filters || {}) };
        delete next[attrId];
        return next;
      });
    }
    setFormData(f => {
      const selected = checked
        ? [...(f.variante_atributos || []), attrId]
        : (f.variante_atributos || []).filter(id => id !== attrId);
      const atributos = { ...(f.atributos || {}) };
      const varianteOpciones = { ...(f.variante_opciones || {}) };
      if (checked) delete atributos[attrId];
      if (!checked) delete varianteOpciones[attrId];
      if (checked && !Array.isArray(varianteOpciones[attrId])) varianteOpciones[attrId] = [];
      return {
        ...f,
        atributos,
        variante_opciones: varianteOpciones,
        variante_atributos: selected,
        variantes: buildVariantRows(atributosCategoria, selected, f.variantes || [], {
          moneda: f.moneda,
          precio_costo: f.precio_costo,
          precio_venta: f.precio_venta,
          var_costo: f.var_costo_general,
          var_ganancia: f.var_ganancia_general,
        }, varianteOpciones),
      };
    });
  };
  const toggleVarianteOpcion = (attrId, optionId, checked) => {
    setFormData(f => {
      const varianteOpciones = { ...(f.variante_opciones || {}) };
      const current = new Set(varianteOpciones[attrId] || []);
      if (checked) current.add(optionId);
      else current.delete(optionId);
      varianteOpciones[attrId] = Array.from(current);
      return {
        ...f,
        variante_opciones: varianteOpciones,
        variantes: buildVariantRows(atributosCategoria, f.variante_atributos || [], f.variantes || [], {
          moneda: f.moneda,
          precio_costo: f.precio_costo,
          precio_venta: f.precio_venta,
          var_costo: f.var_costo_general,
          var_ganancia: f.var_ganancia_general,
        }, varianteOpciones),
      };
    });
  };
  const regenerateVariantes = () => {
    setFormData(f => ({
      ...f,
      variantes: buildVariantRows(atributosCategoria, f.variante_atributos || [], f.variantes || [], {
        moneda: f.moneda,
          precio_costo: f.precio_costo,
        precio_venta: f.precio_venta,
        var_costo: f.var_costo_general,
        var_ganancia: f.var_ganancia_general,
      }, f.variante_opciones || {}),
    }));
  };
  const updateVarianteRow = (idx, patch) => {
    setFormData(f => ({
      ...f,
      variantes: (f.variantes || []).map((v, i) => i === idx ? { ...v, ...patch } : v),
    }));
  };
  // Costo / % ganancia: valor general que se aplica a todas las variantes que no
  // fueron personalizadas. Si el general está vacío, tipear en una variante lo
  // define y se propaga al resto; si ya hay general, la que difiere queda "manual".
  const aplicarGeneralVariantes = (f, campo, val) => {
    const manualKey = campo === "costo" ? "costo_manual" : "ganancia_manual";
    return (f.variantes || []).map(v => {
      if (v[manualKey]) return v;
      const costo = campo === "costo" ? val : v.precio_costo;
      const pct = campo === "ganancia" ? val : v.ganancia_pct;
      const venta = calcSugerido(costo, pct, f.moneda);
      return {
        ...v,
        ...(campo === "costo" ? { precio_costo: val } : { ganancia_pct: val }),
        ...(venta !== null ? { precio_venta: String(venta) } : {}),
      };
    });
  };
  const setVarGeneral = (campo, val) => {
    const v = campo === "ganancia" ? String(val ?? "").replace(",", ".") : val;
    const gKey = campo === "costo" ? "var_costo_general" : "var_ganancia_general";
    setFormData(f => ({ ...f, [gKey]: v, variantes: aplicarGeneralVariantes(f, campo, v) }));
  };
  const updateVarianteCosto = (idx, costo) => {
    setFormData(f => {
      if (!f.var_costo_general) {
        return { ...f, var_costo_general: costo, variantes: aplicarGeneralVariantes(f, "costo", costo) };
      }
      return {
        ...f,
        variantes: (f.variantes || []).map((v, i) => {
          if (i !== idx) return v;
          const venta = calcSugerido(costo, v.ganancia_pct, f.moneda);
          return {
            ...v,
            precio_costo: costo,
            costo_manual: String(costo) !== String(f.var_costo_general),
            ...(venta !== null ? { precio_venta: String(venta) } : {}),
          };
        }),
      };
    });
  };
  const updateVarianteGanancia = (idx, pct) => {
    const normalizedPct = String(pct ?? "").replace(",", ".");
    setFormData(f => {
      if (f.var_ganancia_general === "" || f.var_ganancia_general == null) {
        return { ...f, var_ganancia_general: normalizedPct, variantes: aplicarGeneralVariantes(f, "ganancia", normalizedPct) };
      }
      return {
        ...f,
        variantes: (f.variantes || []).map((v, i) => {
          if (i !== idx) return v;
          const venta = calcSugerido(v.precio_costo, normalizedPct, f.moneda);
          return {
            ...v,
            ganancia_pct: normalizedPct,
            ganancia_manual: normalizedPct !== String(f.var_ganancia_general),
            ...(venta !== null ? { precio_venta: String(venta) } : {}),
          };
        }),
      };
    });
  };
  const updateVarianteVenta = (idx, venta) => {
    setFormData(f => ({
      ...f,
      variantes: (f.variantes || []).map((v, i) => {
        if (i !== idx) return v;
        const pct = calcGananciaPct(v.precio_costo, venta);
        return {
          ...v,
          precio_venta: venta,
          ganancia_pct: pct,
          ganancia_manual: pct !== "" && String(pct) !== String(f.var_ganancia_general ?? ""),
        };
      }),
    }));
  };
  const restablecerVarianteGeneral = (idx) => {
    setFormData(f => ({
      ...f,
      variantes: (f.variantes || []).map((v, i) => {
        if (i !== idx) return v;
        const costo = f.var_costo_general || v.precio_costo;
        const pct = f.var_ganancia_general !== "" ? f.var_ganancia_general : v.ganancia_pct;
        const venta = calcSugerido(costo, pct, f.moneda);
        return { ...v, precio_costo: costo, ganancia_pct: pct, costo_manual: false, ganancia_manual: false,
          ...(venta !== null ? { precio_venta: String(venta) } : {}) };
      }),
    }));
  };

  // ── Form ─────────────────────────────────────────────────────
  const openNew = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, logo_tipo: activeEmpresaPropia?.slug || "arandujar" });
    setVariantOptionFilters({});
    setShowForm(true);
  };

  useEffect(() => {
    if (searchParams.get("nuevo") !== "producto") return;
    if (hasPermission("inventario_productos.crear")) openNew();
    const next = new URLSearchParams(searchParams);
    next.delete("nuevo");
    setSearchParams(next, { replace: true });
  }, [searchParams]); // eslint-disable-line

  const openEdit = (p) => {
    // Guaraníes: el costo va sin decimales. Si llega 4303.67 como texto, el campo lo leería
    // como 430367 (el punto es separador de miles) → se redondea ANTES de cargar el formulario.
    const _monedaEdit = p.moneda || "PYG";
    const _redCosto = (x) => (x == null || x === "") ? x : (_monedaEdit === "PYG" ? Math.round(Number(x)) : Number(x));
    p = {
      ...p,
      precio_costo: _redCosto(p.precio_costo),
      variantes: (p.variantes || []).map(v => ({ ...v, precio_costo: _redCosto(v.precio_costo) })),
    };
    setEditingId(p.id);
    setVariantOptionFilters({});
    setFormData({
      nombre: p.nombre,
      descripcion: p.descripcion || "",
      sku: p.sku || "",
      codigo_barras: p.codigo_barras || "",
      categoria: p.categoria || "",
      precio_costo: String(p.precio_costo || ""),
      ganancia_pct: "",
      precio_venta: String(p.precio_venta || ""),
      moneda: p.moneda || "PYG",
      stock_actual: String(p.stock_actual || 0),
      stock_inicial_view: "",
      stock_minimo: String(p.stock_minimo || 0),
      logo_tipo: p.logo_tipo || "arandujar",
      activo: p.activo,
      iva_tipo: p.iva_tipo || "10",
      descuento_maximo: String(p.descuento_maximo ?? 0),
      descuento_vence: p.descuento_vence || "",
      unidad_medida_id: p.unidad_medida_id || null,
      subcategoria_id: p.subcategoria_id || null,
      atributos: p.atributos || {},
      variantes_habilitadas: !!p.variantes_habilitadas,
      variante_atributos: p.variante_atributos || [],
      variante_opciones: variantOptionsFromRows(p.variantes || [], p.variante_atributos || []),
      ...(() => {
        const rows = p.variantes || [];
        const moda = (arr) => {
          const cnt = {};
          arr.filter(x => x !== "" && x != null).forEach(x => { cnt[x] = (cnt[x] || 0) + 1; });
          return Object.entries(cnt).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
        };
        const costos = rows.map(v => (v.precio_costo == null || v.precio_costo === "") ? "" : String(v.precio_costo));
        const pcts = rows.map(v => { const g = calcGananciaPct(v.precio_costo, v.precio_venta); return g === "" ? "" : String(g); });
        const cg = moda(costos), gg = moda(pcts);
        return {
          var_costo_general: cg,
          var_ganancia_general: gg,
          variantes: rows.map((v, i) => ({ ...v, costo_manual: costos[i] !== "" && costos[i] !== cg, ganancia_manual: pcts[i] !== "" && pcts[i] !== gg })),
        };
      })(),
      codigo_fabricante: p.codigo_fabricante || "",
      lote: p.lote || "",
      fecha_vencimiento: p.fecha_vencimiento || "",
      imagen_url: p.imagen_url || "",
      proveedor_id: p.proveedor_id || "",
      requiere_receta: !!p.requiere_receta,
      ficha_ingrediente: p.ficha_agronomica?.ingrediente_activo || "",
      ficha_clase_tox: p.ficha_agronomica?.clase_toxicologica || "",
      ficha_carencia: p.ficha_agronomica?.carencia_dias ?? "",
      ficha_dosis: p.ficha_agronomica?.dosis_recomendada ?? "",
      ficha_unidad_dosis: p.ficha_agronomica?.unidad_dosis || "",
    });
    setShowForm(true);
    fetch(`${API}/admin/productos/${p.id}/movimientos`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(movs => {
        const inicial = (movs || [])
          .filter(m => m.motivo === "stock_inicial")
          .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")))[0];
        setFormData(f => ({
          ...f,
          stock_inicial_view: inicial ? String(inicial.cantidad || 0) : "0",
        }));
      })
      .catch(() => setFormData(f => ({ ...f, stock_inicial_view: "0" })));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validación frontend: Servicios requiere permiso
    if (esCatServicio(formData.categoria) && !puedeCrearServicio) {
      toast.error("No tenés permiso para crear productos de tipo Servicios");
      return;
    }
    // Categoría requerida
    if (!formData.categoria) {
      toast.error("Seleccioná una categoría para el producto");
      return;
    }
    // SKU requerido solo si modo es manual — en automático lo genera el backend
    const skuAutoGenerado = !editingId && configSku.modo_codigo === "automatico";
    if (!skuAutoGenerado && !formData.sku?.trim()) {
      toast.error("El SKU es obligatorio");
      return;
    }
    if (usaAtributos && formData.variantes_habilitadas) {
      if ((formData.variante_atributos || []).length === 0) {
        toast.error("Elegí qué atributos separan el stock por variante");
        return;
      }
      if ((formData.variantes || []).length === 0) {
        toast.error("Elegí las opciones que se usarán en las variantes");
        return;
      }
    }

    setSaving(true);
    const isServicio = esCatServicio(formData.categoria);
    const unidadItem = unidadesLista.find(u => u.id === formData.unidad_medida_id);
    const payload = {
      nombre: formData.nombre,
      descripcion: formData.descripcion || null,
      sku: formData.sku.trim(),
      codigo_barras: normalizeBarcode(formData.codigo_barras) || null,
      categoria: formData.categoria || null,
      precio_costo: parseNumberInput(formData.precio_costo) || 0,
      precio_venta: parseNumberInput(formData.precio_venta) || 0,
      moneda: formData.moneda || "PYG",
      stock_minimo: isServicio ? 0 : (parseNumberInput(formData.stock_minimo) || 0),
      unidad: unidadItem ? unidadItem.sigla : "UN",
      logo_tipo: activeEmpresaPropia?.slug || formData.logo_tipo,
      activo: formData.activo,
      iva_tipo: formData.iva_tipo || "10",
      descuento_maximo: parseNumberInput(formData.descuento_maximo) || 0,
      descuento_vence: formData.descuento_vence || null,
      unidad_medida_id: formData.unidad_medida_id || null,
      ...(usaSubcategorias ? { subcategoria_id: formData.subcategoria_id || null } : {}),
      ...(usaAtributos ? {
        atributos: Object.fromEntries(Object.entries(formData.atributos || {}).filter(([attrId]) => !formData.variantes_habilitadas || !(formData.variante_atributos || []).includes(attrId))),
        variantes_habilitadas: !!formData.variantes_habilitadas,
        variante_atributos: formData.variantes_habilitadas ? (formData.variante_atributos || []) : [],
        variantes: formData.variantes_habilitadas ? (formData.variantes || []).map(({ costo_manual, ganancia_manual, ...v }) => ({
          ...v,
          stock_actual: parseNumberInput(v.stock_actual) || 0,
          stock_minimo: parseNumberInput(v.stock_minimo) || 0,
          precio_costo: v.precio_costo === "" || v.precio_costo == null ? null : (parseNumberInput(v.precio_costo) || 0),
          precio_venta: v.precio_venta === "" || v.precio_venta == null ? null : (parseNumberInput(v.precio_venta) || 0),
        })) : [],
      } : {}),
      codigo_fabricante: formData.codigo_fabricante?.trim() || null,
      lote: formData.lote?.trim() || null,
      fecha_vencimiento: formData.fecha_vencimiento || null,
      imagen_url: formData.imagen_url || "",
      proveedor_id: formData.proveedor_id || "",
      // Recetario agronómico: solo si el módulo está activo y el usuario puede marcarlo
      ...((hasModule?.("recetario") && hasPermission("inventario_productos.marcar_receta")) ? {
        requiere_receta: !!formData.requiere_receta,
        ficha_agronomica: (formData.requiere_receta || formData.ficha_ingrediente || formData.ficha_clase_tox || formData.ficha_carencia !== "" || formData.ficha_dosis !== "") ? {
          ingrediente_activo: formData.ficha_ingrediente?.trim() || "",
          clase_toxicologica: formData.ficha_clase_tox?.trim() || "",
          carencia_dias: formData.ficha_carencia === "" ? null : Number(formData.ficha_carencia),
          dosis_recomendada: formData.ficha_dosis === "" ? null : Number(formData.ficha_dosis),
          unidad_dosis: formData.ficha_unidad_dosis?.trim() || "",
        } : null,
      } : {}),
      ...(!editingId && { stock_actual: isServicio ? 0 : (parseFloat(formData.stock_actual) || 0) }),
      ...(!editingId && dep.activo && formData.deposito_id ? { deposito_id: formData.deposito_id } : {}),
    };
    const url    = editingId ? `${API}/admin/productos/${editingId}` : `${API}/admin/productos`;
    const method = editingId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingId ? "Producto actualizado" : "Producto creado");
      setShowForm(false);
      fetchProductos();
      fetchProductosPagina(paginaActual);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSaving(false);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar producto "${p.nombre}"? Se eliminarán sus movimientos de stock.`)) return;
    const res = await fetch(`${API}/admin/productos/${p.id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Producto eliminado"); fetchProductos(); fetchProductosPagina(paginaActual); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar");
    }
  };

  // ── Unidades de Medida CRUD ───────────────────────────────────
  const openNewUnidad = () => {
    setEditingUnidadId(null);
    setUnidadForm(emptyUnidadForm);
    setShowUnidadForm(true);
  };

  const openEditUnidad = (u) => {
    setEditingUnidadId(u.id);
    setUnidadForm({
      codigo: String(u.codigo),
      nombre: u.nombre,
      sigla: u.sigla,
      cantidad: String(u.cantidad),
      peso_kg: u.peso_kg != null ? String(u.peso_kg) : "",
      cod_dnit: String(u.cod_dnit),
    });
    setShowUnidadForm(true);
  };

  const handleSaveUnidad = async (e) => {
    e.preventDefault();
    setSavingUnidad(true);
    const payload = {
      codigo: parseInt(unidadForm.codigo) || 0,
      nombre: unidadForm.nombre,
      sigla: unidadForm.sigla,
      cantidad: parseFloat(unidadForm.cantidad) || 1,
      peso_kg: unidadForm.peso_kg ? parseFloat(unidadForm.peso_kg) : null,
      cod_dnit: parseInt(unidadForm.cod_dnit) || 0,
    };
    const url = editingUnidadId
      ? `${API}/admin/unidades-medida/${editingUnidadId}`
      : `${API}/admin/unidades-medida/`;
    const method = editingUnidadId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingUnidadId ? "Unidad actualizada" : "Unidad creada");
      setShowUnidadForm(false);
      fetchUnidadesAll();
      fetchUnidadesLista();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
    setSavingUnidad(false);
  };

  const handleDeleteUnidad = async (u) => {
    if (!window.confirm(`¿Eliminar "${u.nombre}" (${u.sigla})?`)) return;
    const res = await fetch(`${API}/admin/unidades-medida/${u.id}`, { method: "DELETE", headers });
    if (res.status === 204 || res.ok) {
      toast.success("Unidad eliminada");
      fetchUnidadesAll();
      fetchUnidadesLista();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar");
    }
  };

  // ── Historial de costos ───────────────────────────────────────
  const fetchHistorialCostos = async () => {
    setLoadingHistCostos(true);
    const q = new URLSearchParams({ limit: "500" });
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/productos/historial-costos?${q}`, { headers });
    if (res.ok) setHistorialCostos(await res.json());
    setLoadingHistCostos(false);
  };

  const openHistorialModal = async (p) => {
    setHistModalProducto(p);
    setHistModalData([]);
    setLoadingHistModal(true);
    const q = new URLSearchParams({ producto_id: p.id, limit: "100" });
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    const res = await fetch(`${API}/admin/productos/historial-costos?${q}`, { headers });
    if (res.ok) setHistModalData(await res.json());
    setLoadingHistModal(false);
  };

  // ── Códigos de barras ─────────────────────────────────────────
  const generateCode = () => {
    const prefijo = String(barcodeConfig.barcode_prefijo_ean13 || "200").replace(/\D/g, "").slice(0, 6) || "200";
    const seqLen = 12 - prefijo.length;
    const seq = String(Date.now()).slice(-seqLen).padStart(seqLen, "0");
    const base12 = `${prefijo}${seq}`;
    return `${base12}${ean13CheckDigit(base12)}`;
  };

  const normalizeBarcode = (raw) => String(raw || "").replace(/[\r\n\t]/g, "").trim();

  const handleBarcodeInput = (value) => {
    setFormData(f => ({ ...f, codigo_barras: normalizeBarcode(value) }));
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = normalizeBarcode(e.currentTarget.value);
    setFormData(f => ({ ...f, codigo_barras: code }));
    if (code) {
      toast.success("Código leído por escáner");
    }
  };

  const saveProductoBarcode = async (p, code) => {
    setGeneratingCode(p.id);
    const res = await fetch(`${API}/admin/productos/${p.id}/barcode`, {
      method: "PUT", headers, body: JSON.stringify({ codigo_barras: code })
    });
    if (res.ok) {
      toast.success(code ? "Código de barras actualizado" : "Código de barras eliminado");
      fetchProductos();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar código");
    }
    setGeneratingCode(null);
  };

  const generarBarcodeProducto = async (p, tipo = null) => {
    setGeneratingCode(p.id);
    const tipoGenerar = tipo || barcodeGenerateType || barcodeConfig.barcode_tipo_default || "ean13_interno";
    const res = await fetch(`${API}/admin/productos/${p.id}/barcode/generar`, {
      method: "POST",
      headers,
      body: JSON.stringify({ tipo: tipoGenerar === "manual" ? "ean13_interno" : tipoGenerar }),
    });
    if (res.ok) {
      toast.success("Código de barras generado");
      fetchProductos();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al generar código");
    }
    setGeneratingCode(null);
  };

  const editBarcodePrompt = async (p) => {
    const code = window.prompt("Código de barras", p.codigo_barras || "");
    if (code === null) return;
    await saveProductoBarcode(p, code.trim());
  };

  const deleteBarcode = async (p) => {
    if (!window.confirm(`¿Quitar solamente el código de barras de ${p.nombre}?`)) return;
    setGeneratingCode(p.id);
    const res = await fetch(`${API}/admin/productos/${p.id}/barcode`, { method: "DELETE", headers });
    if (res.ok) {
      toast.success("Código de barras eliminado");
      fetchProductos();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar código");
    }
    setGeneratingCode(null);
  };

  // Precio a mostrar en la etiqueta: si el producto usa variantes, precio_venta
  // de la CABECERA queda en 0 (el precio real vive en cada variante, como ya
  // hace la columna "Precio venta" del catálogo — ver ventaMin/ventaMax más
  // abajo). Sin este fallback la etiqueta imprimía siempre ₲0 para cualquier
  // producto con variantes habilitadas.
  const precioParaEtiqueta = (p) => {
    if (p?.variantes_habilitadas) {
      const activas = (p.variantes || []).filter(v => v.activo !== false);
      const ventas = activas.map(v => Number(v.precio_venta || 0)).filter(n => n > 0);
      if (ventas.length) return Math.min(...ventas);
    }
    return Number(p?.precio_venta || 0);
  };

  // Tamaño de letra automático según el tamaño de la etiqueta — ningún
  // diseño pide tamaños de fuente por campo, se calculan solos a partir del
  // lado más chico de la etiqueta para que siempre entren bien.
  const autoFontSizesPx = (anchoMm, altoMm) => {
    const base = Math.max(4, Math.min(anchoMm, altoMm));
    return {
      empresa: Math.max(5, Math.min(12, base * 0.26)),
      producto: Math.max(6, Math.min(14, base * 0.30)),
      sku: Math.max(5, Math.min(10, base * 0.20)),
      precio: Math.max(7, Math.min(16, base * 0.34)),
    };
  };

  // Motor único de impresión de etiquetas: toma un diseño (medidas + qué
  // campos mostrar) y arma el HTML/CSS de la hoja/rollo dinámicamente, en
  // vez de tener un modelo fijo por cada tamaño de etiqueta.
  const printBarcodeConDiseno = (producto, cantidad, diseno) => {
    if (!producto?.codigo_barras) {
      toast.error("El producto no tiene código de barras");
      return;
    }
    const qty = Math.max(1, Math.min(BARCODE_LABELS_MAX, Number(cantidad) || 1));
    const printable = Array.from({ length: qty }, () => producto);
    const empresaNombre = activeEmpresaPropia?.nombre_fantasia || activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "";
    const logoUrl = diseno.mostrar_logo ? resolveLogoForContext(activeEmpresaPropia, "docs") : null;
    const anchoEtq = Number(diseno.ancho_etiqueta_mm) || 33;
    const altoEtq = Number(diseno.alto_etiqueta_mm) || 22;
    const fonts = autoFontSizesPx(anchoEtq, altoEtq);
    const labelsHtml = printable.map(p => {
      const code = p.codigo_barras;
      // Barras sin texto, estiradas al alto fijo del diseño (preserveAspectRatio none):
      // EAN-13 y Code 128 quedan con la misma altura. El número va como texto HTML aparte.
      const eanSvg = barcodeSvg(code, { height: 60, module: 1, quietMm: Math.min(2, Number(barcodeConfig.barcode_quiet_zone_mm) || 2), showText: false })
        .replace("<svg ", '<svg preserveAspectRatio="none" ');
      return `
        <div class="label">
          ${diseno.mostrar_logo && logoUrl ? `<img class="logo" src="${logoUrl}" alt="" />` : ""}
          ${diseno.mostrar_empresa && empresaNombre ? `<div class="empresa">${escapeHtml(empresaNombre)}</div>` : ""}
          <div class="barcode-box">
            ${eanSvg}
            <span class="code-text">${escapeHtml(code)}</span>
          </div>
          ${diseno.mostrar_producto ? `<div class="nombre">${escapeHtml(p.nombre)}</div>` : ""}
          ${diseno.mostrar_sku ? `<div class="sku">${escapeHtml(p.sku || "")}</div>` : ""}
          ${diseno.mostrar_precio ? `<div class="precio">${escapeHtml(fmtMoneda(precioParaEtiqueta(p), p.moneda))}</div>` : ""}
        </div>`;
    }).join("");

    const title = qty === 1 ? `Etiqueta — ${producto.nombre}` : `${qty} etiquetas — ${producto.nombre}`;
    const win = window.open("", "_blank", "width=700,height=700");
    if (!win) {
      toast.error("El navegador bloqueó la ventana de impresión");
      return;
    }
    const css = buildEtiquetaCss(diseno, fonts);
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(title)}</title>
  <style>${css}</style>
</head>
<body onload="window.print()">
  <main class="sheet">${labelsHtml}</main>
</body>
</html>`);
    win.document.close();
  };

  const printBarcode = (p) => {
    const input = window.prompt(`Cantidad de etiquetas para imprimir (1-${BARCODE_LABELS_MAX})`, "1");
    if (input === null) return;
    const cantidad = parseInt(String(input).replace(/\D/g, ""), 10);
    if (!cantidad || cantidad < 1) {
      toast.error("Cantidad inválida");
      return;
    }
    if (cantidad > BARCODE_LABELS_MAX) {
      toast.info(`Máximo ${BARCODE_LABELS_MAX} etiquetas por hoja`);
    }
    const diseno = disenosEtiqueta.find(d => d.id === barcodeConfig.etiqueta_modelo_impresion) || disenosEtiqueta[0];
    if (!diseno) {
      toast.error("No hay ningún diseño de etiqueta configurado — creá uno en Configuración > Códigos de barra");
      return;
    }
    printBarcodeConDiseno(p, cantidad, diseno);
  };

  // ── Movimientos ───────────────────────────────────────────────
  const openMovimientos = async (p) => {
    setSelectedProducto(p);
    setShowMovModal(true);
    setShowMovForm(false);
    const primeraVariante = (p.variantes_habilitadas && (p.variantes || []).length > 0) ? p.variantes[0] : null;
    setMovForm({
      ...emptyMov,
      variante_id: primeraVariante?.id || "",
      precio_unitario: primeraVariante?.precio_costo ? String(primeraVariante.precio_costo) : (p.precio_costo ? String(p.precio_costo) : ""),
    });
    setLoadingMov(true);
    const res = await fetch(`${API}/admin/productos/${p.id}/movimientos`, { headers });
    if (res.ok) setMovimientos(await res.json());
    setLoadingMov(false);
  };

  const toggleMovForm = () => {
    setShowMovForm(v => {
      const next = !v;
      if (next) {
        setMovForm(f => ({
          ...f,
          precio_unitario: f.precio_unitario || (selectedProducto?.precio_costo ? String(selectedProducto.precio_costo) : ""),
        }));
      }
      return next;
    });
  };

  const handleSaveMov = async (e) => {
    e.preventDefault();
    const cantidad = parseFloat(movForm.cantidad);
    if (Number.isNaN(cantidad) || cantidad < 0 || (movForm.tipo !== "ajuste" && cantidad <= 0)) {
      toast.error("Ingresá una cantidad válida"); return;
    }
    const varianteMov = selectedProducto?.variantes_habilitadas
      ? (selectedProducto.variantes || []).find(v => v.id === movForm.variante_id)
      : null;
    if (selectedProducto?.variantes_habilitadas && !varianteMov) {
      toast.error("Seleccioná una variante"); return;
    }
    const stockDisponible = varianteMov ? Number(varianteMov.stock_actual || 0) : Number(selectedProducto.stock_actual || 0);
    if (movForm.tipo === "salida" && cantidad > stockDisponible) {
      toast.error("La salida no puede dejar el stock por debajo de 0"); return;
    }
    setSavingMov(true);
    const tipoFacSel = movForm.tipo_factura_id ? tiposFactura.find(t => t.id === movForm.tipo_factura_id) : null;
    const payload = {
      tipo: movForm.tipo,
      cantidad,
      motivo: movForm.motivo,
      precio_unitario: movForm.precio_unitario ? parseFloat(movForm.precio_unitario) : null,
      notas: movForm.notas || null,
      variante_id: movForm.variante_id || null,
      tipo_factura_id: tipoFacSel?.id || null,
      tipo_factura_nombre: tipoFacSel?.nombre || null,
      ...(dep.activo && movForm.deposito_id ? { deposito_id: movForm.deposito_id } : {}),
    };
    const res = await fetch(`${API}/admin/productos/${selectedProducto.id}/movimiento`, {
      method: "POST", headers, body: JSON.stringify(payload),
    });
    if (res.ok) {
      toast.success("Movimiento registrado");
      setShowMovForm(false);
      setMovForm({ ...emptyMov, precio_unitario: selectedProducto?.precio_costo ? String(selectedProducto.precio_costo) : "" });
      const logoParam = activeEmpresaPropia?.slug ? `logo_tipo=${activeEmpresaPropia.slug}` : "";
      const [rP, rM] = await Promise.all([
        fetch(`${API}/admin/productos?${logoParam}`, { headers }),
        fetch(`${API}/admin/productos/${selectedProducto.id}/movimientos`, { headers }),
      ]);
      if (rP.ok) setProductos(await rP.json());
      if (rM.ok) {
        const movs = await rM.json();
        setMovimientos(movs);
        const updated = await fetch(`${API}/admin/productos/${selectedProducto.id}`, { headers })
          .then(r => r.json()).catch(() => null);
        if (updated) setSelectedProducto(updated);
      }
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al registrar movimiento");
    }
    setSavingMov(false);
  };

  const comprobanteNumero = (m) => m?.numero_comprobante || `MOV-${String(m?.id || "").slice(0, 8).toUpperCase()}`;
  const productoComprobanteNombre = (mov) => mov?.producto_nombre || selectedProducto?.nombre || "Producto";
  const productoComprobanteSku = (mov) => mov?.sku || selectedProducto?.sku || "-";

  const printComprobante = (mov) => {
    const numero = comprobanteNumero(mov);
    const win = window.open("", "_blank", "width=820,height=900");
    if (!win) return;
    const motivo = motivoAjusteLabel(mov.motivo);
    const productoNombre = productoComprobanteNombre(mov);
    const productoSku = productoComprobanteSku(mov);
    win.document.write(`
      <html>
        <head>
          <title>${escapeHtml(numero)}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #111827; margin: 0; padding: 32px; }
            .sheet { max-width: 760px; margin: 0 auto; border: 1px solid #d1d5db; padding: 28px; }
            .top { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #111827; padding-bottom: 18px; }
            h1 { margin: 0; font-size: 22px; }
            .muted { color: #6b7280; font-size: 12px; }
            .num { text-align: right; font-weight: 700; font-size: 18px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 22px; }
            .box { border: 1px solid #e5e7eb; padding: 12px; min-height: 62px; }
            .label { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
            .value { margin-top: 5px; font-size: 15px; font-weight: 700; }
            table { width: 100%; border-collapse: collapse; margin-top: 24px; }
            th, td { border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 13px; }
            th { background: #f3f4f6; color: #374151; }
            .footer { margin-top: 36px; display: flex; justify-content: space-between; gap: 28px; }
            .sign { flex: 1; border-top: 1px solid #9ca3af; padding-top: 8px; text-align: center; color: #6b7280; font-size: 12px; }
            @media print { body { padding: 0; } .sheet { border: 0; } }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="top">
              <div>
                <h1>Comprobante de Ajuste de Stock</h1>
                <div class="muted">Inventario Productos</div>
              </div>
              <div class="num">${escapeHtml(numero)}<div class="muted">${escapeHtml(mov.fecha || "")}</div></div>
            </div>
            <div class="grid">
              <div class="box"><div class="label">Producto</div><div class="value">${escapeHtml(productoNombre)}</div></div>
              <div class="box"><div class="label">SKU</div><div class="value">${escapeHtml(productoSku)}</div></div>
              <div class="box"><div class="label">Tipo</div><div class="value">${escapeHtml(mov.tipo)}</div></div>
              <div class="box"><div class="label">Usuario</div><div class="value">${escapeHtml(mov.usuario_nombre || "-")}</div></div>
            </div>
            <table>
              <thead><tr><th>Cantidad</th><th>Stock anterior</th><th>Stock nuevo</th><th>Motivo</th></tr></thead>
              <tbody><tr><td>${escapeHtml(mov.cantidad)} ${escapeHtml(selectedProducto.unidad)}</td><td>${escapeHtml(mov.stock_anterior)}</td><td>${escapeHtml(mov.stock_nuevo)}</td><td>${escapeHtml(motivo)}</td></tr></tbody>
            </table>
            <div class="box" style="margin-top: 18px;"><div class="label">Notas</div><div class="value">${escapeHtml(mov.notas || "-")}</div></div>
            <div class="footer"><div class="sign">Entregado por</div><div class="sign">Recibido / verificado por</div></div>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `);
    win.document.close();
  };

  // ── Derived ───────────────────────────────────────────────────
  const stockBajo = productos.filter(p =>
    !esCatServicio(p.categoria) && p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo
  ).length;
  const addChip = () => {
    const v = search.trim();
    if (v && !chips.includes(v)) setChips(prev => [...prev, v]);
    setSearch("");
  };

  // Se sacó el auto-refresh a ciegas cada 30s (recargaba el catálogo entero y ponía
  // lento hasta el tipeo). Ahora solo se refresca por evento (SSE) cuando de verdad
  // cambió algo — producto nuevo, modificación o movimiento de stock/compra — más el
  // botón de "Actualizar" manual.
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo !== "modulo_actualizado") return;
      if (["productos", "stock", "compras"].includes(event.data?.modulo)) {
        fetchProductos();
        fetchProductosPagina(paginaActual);
        if (activeTab === "historial_costos") fetchHistorialCostos();
      }
      if (event.data?.modulo === "productos" && event.data?.accion === "config") {
        fetchConfigSku();
        fetchCategorias();
        fetchSubcategorias();
        fetchAtributos();
      }
    }, [activeTab, paginaActual]), // eslint-disable-line
  });

  const handleRefreshManual = () => {
    if (activeTab === "productos") {
      fetchProductos();
      fetchProductosPagina(paginaActual);
    } else if (activeTab === "codigos") {
      fetchProductos();
    } else if (activeTab === "unidades") {
      fetchUnidadesAll();
    } else if (activeTab === "historial_costos") {
      fetchHistorialCostos();
    } else if (activeTab === "configuracion") {
      fetchConfigSku();
      fetchCategorias();
      fetchSubcategorias();
      fetchAtributos();
    }
  };

  const ivaLabel = { exenta: "Exenta", "5": "IVA 5%", "10": "IVA 10%" };

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/sistema" className="text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-heading text-2xl text-white flex items-center gap-2">
              <Package className="w-6 h-6 text-cyan-400" />
              Inventario Productos
            </h1>
            <p className="text-slate-400 text-sm font-body">Catálogo · Stock · Precios</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefreshManual}
            disabled={loading || loadingHistCostos}
            className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCcw className={`w-4 h-4 ${(loading || loadingHistCostos) ? "animate-spin" : ""}`} />
          </button>
          {stockBajo > 0 && (
            <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-orange-500/15 text-orange-300 border border-orange-500/25 font-body">
              <AlertTriangle className="w-3.5 h-3.5" />
              {stockBajo} bajo mínimo
            </span>
          )}
          {activeTab === "productos" && (hasPermission("inventario_productos.ver_stock_madre") || hasPermission("inventario_productos.ver_stock_sucursales")) && (
            <button onClick={() => setShowStockGrupo(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors font-body text-sm">
              <Building2 className="w-4 h-4" /> Stock madre / sucursales
            </button>
          )}
          {activeTab === "productos" && hasPermission("inventario_productos.transferir_stock") && (
            <button onClick={() => setShowTransferencia(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors font-body text-sm">
              <RotateCcw className="w-4 h-4" /> Transferir mercadería
            </button>
          )}
          {activeTab === "productos" && dep.activo && hasPermission("inventario_productos.transferir_depositos") && (
            <button onClick={() => setDepTransf({})}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors font-body text-sm">
              <Warehouse className="w-4 h-4" /> Transferir entre depósitos
            </button>
          )}
          {activeTab === "productos" && hasPermission("inventario_productos.crear") && (
            <button onClick={openNew}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-sm">
              <Plus className="w-4 h-4" /> Nuevo producto
            </button>
          )}
          {activeTab === "unidades" && canCrearUnidades && (
            <button onClick={openNewUnidad}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-sm">
              <Plus className="w-4 h-4" /> Nueva unidad
            </button>
          )}
        </div>
      </div>

      {showStockGrupo && <StockGrupoModal onClose={() => setShowStockGrupo(false)} />}
      {depTransf && <TransferenciaDepositoModal token={token} logo={activeEmpresaPropia?.slug} depositos={dep.depositos} inicial={depTransf.inicial}
        onClose={() => setDepTransf(null)} onDone={() => { setDepRefresh(x => x + 1); dep.recargar(); fetchProductosPagina(1); fetchProductos(); }} />}
      {showTransferencia && <TransferenciaStockModal onClose={() => setShowTransferencia(false)} onDone={() => { fetchProductosPagina(1); fetchProductos(); }} />}

      {/* Tabs */}
      <div className="border-b border-white/10 px-6">
        <div className="flex">
          <button
            onClick={() => setActiveTab("productos")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-body border-b-2 transition-colors ${
              activeTab === "productos"
                ? "border-cyan-500 text-cyan-300"
                : "border-transparent text-slate-400 hover:text-slate-300"
            }`}
          >
            <Package className="w-4 h-4" /> Productos
          </button>
          {canVerUnidades && (
            <button
              onClick={() => { setActiveTab("unidades"); fetchUnidadesAll(); }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-body border-b-2 transition-colors ${
                activeTab === "unidades"
                  ? "border-cyan-500 text-cyan-300"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
            >
              <Ruler className="w-4 h-4" /> Unidades de Medida
            </button>
          )}
          {canGenerarEtiquetas && (
            <button
              onClick={() => setActiveTab("codigos")}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-body border-b-2 transition-colors ${
                activeTab === "codigos"
                  ? "border-cyan-500 text-cyan-300"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
            >
              <ScanBarcode className="w-4 h-4" /> Códigos de Barra
            </button>
          )}
          {canVerHistorialCostos && (
            <button
              onClick={() => { setActiveTab("historial_costos"); fetchHistorialCostos(); }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-body border-b-2 transition-colors ${
                activeTab === "historial_costos"
                  ? "border-cyan-500 text-cyan-300"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
            >
              <DollarSign className="w-4 h-4" /> Historial de Costos
            </button>
          )}
          {canVerConfig && (
            <button
              onClick={() => { setActiveTab("configuracion"); setConfigSubTab(null); fetchSubcategorias(); fetchConfigSku(); fetchCategorias(); fetchAtributos(); }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-body border-b-2 transition-colors ${
                activeTab === "configuracion"
                  ? "border-violet-500 text-violet-300"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
            >
              <Settings className="w-4 h-4" /> Configuración
            </button>
          )}
        </div>
      </div>

      {activeTab === "productos" && <div className="p-6 space-y-4">
        {/* Filtros */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px] bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <div className="flex flex-wrap gap-2 mb-2">
              {chips.map(ch => (
                <span key={ch} className="inline-flex items-center gap-1 bg-cyan-500/15 text-cyan-300 border border-cyan-500/25 rounded-full px-2 py-0.5 text-xs">
                  {ch}
                  <button onClick={() => setChips(prev => prev.filter(x => x !== ch))}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <Search className="absolute left-3 bottom-2.5 w-4 h-4 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addChip(); } }}
              placeholder="Buscar por nombre, SKU, categoría, código de barras... (Enter para agregar filtro)"
              className="w-full bg-transparent pl-6 text-white text-sm font-body focus:outline-none" />
          </div>
          {dep.activo && dep.depositos.length > 0 && (
            <select value={depFiltro} onChange={e => setDepFiltro(e.target.value)} style={{ colorScheme: "dark" }}
              title="Ver el stock de un depósito"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none">
              <option value="">Stock: todos los depósitos</option>
              {dep.depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
            </select>
          )}
          <label className="flex items-center gap-2 text-slate-300 text-sm font-body cursor-pointer select-none bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <input type="checkbox" checked={soloConStock} onChange={e => toggleSoloConStock(e.target.checked)} className="accent-cyan-500" />
            Solo con stock
          </label>
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1" title="Mostrar solo los productos con esa cantidad de stock">
            <select value={stockOp} onChange={e => setStockOp(e.target.value)} style={{ colorScheme: "dark" }}
              className="bg-transparent text-sm text-slate-200 focus:outline-none">
              <option value="">Cantidad de stock…</option>
              <option value="eq">Stock igual a</option>
              <option value="gte">Stock al menos</option>
              <option value="lte">Stock como máximo</option>
            </select>
            {stockOp && (
              <>
                <input type="number" min="0" step="any" value={stockCant} onChange={e => setStockCant(e.target.value)} placeholder="Cant."
                  className="w-20 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-sm text-white text-right focus:outline-none" />
                <button type="button" onClick={() => { setStockOp(""); setStockCant(""); }} className="text-slate-500 hover:text-white text-xs px-1">✕</button>
              </>
            )}
          </div>
          <span className="text-slate-500 text-sm font-body ml-auto">
            {totalProductosFiltrados} producto{totalProductosFiltrados !== 1 ? "s" : ""}
          </span>
          {totalPaginasProductos > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                disabled={paginaActual <= 1 || loading}
                className="px-3 py-1.5 rounded-lg text-xs font-body bg-white/5 text-slate-300 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Anterior
              </button>
              <span className="text-slate-500 text-xs font-body whitespace-nowrap">
                Pág. {paginaActual}/{totalPaginasProductos}
              </span>
              <button
                onClick={() => setPaginaActual(p => Math.min(totalPaginasProductos, p + 1))}
                disabled={paginaActual >= totalPaginasProductos || loading}
                className="px-3 py-1.5 rounded-lg text-xs font-body bg-white/5 text-slate-300 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-12 text-slate-500 animate-pulse font-body">Cargando productos...</div>
        ) : productosPagina.length === 0 ? (
          <div className="text-center py-16 text-slate-500 font-body">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{(search || chips.length > 0) ? "Sin coincidencias" : "No hay productos registrados"}</p>
            {hasPermission("inventario_productos.crear") && !search && chips.length === 0 && (
              <button onClick={openNew} className="mt-3 text-cyan-400 hover:underline text-sm">
                Agregar el primer producto
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="border-b border-white/10 bg-white/3">
                  <th className="text-center px-2 py-3 text-slate-400 font-medium text-xs w-14"></th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-xs">Producto</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-xs hidden md:table-cell">Categoría</th>
                  {puedeVerCostoCompra && <th className="text-right px-4 py-3 text-slate-400 font-medium text-xs">Precio costo</th>}
                  <th className="text-right px-4 py-3 text-slate-400 font-medium text-xs">Precio venta</th>
                  {puedeVerCostoCompra && <th className="text-right px-4 py-3 text-slate-400 font-medium text-xs">Margen</th>}
                  <th className="text-center px-4 py-3 text-slate-400 font-medium text-xs">Stock</th>
                  <th className="text-center px-4 py-3 text-slate-400 font-medium text-xs">Empresa</th>
                  <th className="text-center px-4 py-3 text-slate-400 font-medium text-xs">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {productosPagina.map(p => {
                  const variantesActivas = (p.variantes || []).filter(v => v.activo !== false);
                  const costosVar = variantesActivas.map(v => Number(v.precio_costo || 0)).filter(n => n > 0);
                  const ventasVar = variantesActivas.map(v => Number(v.precio_venta || 0)).filter(n => n > 0);
                  const costoMin = costosVar.length ? Math.min(...costosVar) : 0;
                  const costoMax = costosVar.length ? Math.max(...costosVar) : 0;
                  const ventaMin = ventasVar.length ? Math.min(...ventasVar) : 0;
                  const ventaMax = ventasVar.length ? Math.max(...ventasVar) : 0;
                  const costoDisplay = p.variantes_habilitadas && costoMin > 0
                    ? (costoMin === costoMax ? fmtMoneda(costoMin, p.moneda) : `${fmtMoneda(costoMin, p.moneda)} - ${fmtMoneda(costoMax, p.moneda)}`)
                    : fmtMoneda(p.precio_costo, p.moneda);
                  const ventaDisplay = p.variantes_habilitadas && ventaMin > 0
                    ? (ventaMin === ventaMax ? fmtMoneda(ventaMin, p.moneda) : `${fmtMoneda(ventaMin, p.moneda)} - ${fmtMoneda(ventaMax, p.moneda)}`)
                    : fmtMoneda(p.precio_venta, p.moneda);
                  const margenBase = p.variantes_habilitadas
                    ? (costoMin > 0 && ventaMin > 0 ? ((ventaMin - costoMin) / costoMin * 100).toFixed(0) : null)
                    : (p.precio_costo > 0 ? ((p.precio_venta - p.precio_costo) / p.precio_costo * 100).toFixed(0) : null);
                  const margen = margenBase;
                  const margenRango = p.variantes_habilitadas && costoMax > 0 && ventaMax > 0
                    ? ((ventaMax - costoMax) / costoMax * 100).toFixed(0)
                    : null;
                  const stockVista = depFiltro ? Number(p.stock_deposito || 0) : p.stock_actual;
                  const bajominimo = !esCatServicio(p.categoria) && p.stock_minimo > 0 && stockVista <= p.stock_minimo;
                  const esServ = esCatServicio(p.categoria);
                  if (p.variantes_habilitadas && variantesActivas.length > 0) {
                    return (
                      <React.Fragment key={p.id}>
                        <tr className={`border-b border-emerald-500/10 bg-emerald-500/[0.035] hover:bg-emerald-500/[0.06] transition-colors ${!p.activo ? "opacity-40" : ""}`}>
                          <td className="px-2 py-3">
                            <div className="w-9 h-9 rounded-md border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden mx-auto">
                              {p.imagen_url ? <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-700" />}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{p.nombre}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {p.sku && <span className="text-[10px] text-slate-500 font-mono">#{p.sku}</span>}
                              <span className="text-[10px] text-emerald-300">{variantesActivas.length} variante{variantesActivas.length !== 1 ? "s" : ""} agrupada{variantesActivas.length !== 1 ? "s" : ""}</span>
                              {p.descripcion && <span className="text-slate-500 text-xs truncate max-w-[200px]">{p.descripcion}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            {p.categoria ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-slate-300">{p.categoria}</span>
                            ) : <span className="text-slate-600">-</span>}
                            {p.subcategoria && <p className="text-[10px] text-violet-300 mt-1">{p.subcategoria}</p>}
                          </td>
                          {puedeVerCostoCompra && <td className="px-4 py-3 text-right text-slate-500 text-xs">Por variante</td>}
                          <td className="px-4 py-3 text-right text-slate-500 text-xs">Por variante</td>
                          {puedeVerCostoCompra && <td className="px-4 py-3 text-right text-slate-500">-</td>}
                          <td className="px-4 py-3 text-center">
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                              bajominimo ? "bg-orange-500/15 text-orange-300 border border-orange-500/25"
                              : stockVista === 0 ? "bg-red-500/15 text-red-300 border border-red-500/25"
                              : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                            }`}>
                              {bajominimo && <AlertTriangle className="w-3 h-3" />}
                              {stockVista} {p.unidad}
                            </div>
                            <p className="text-emerald-300 text-[10px] mt-0.5">{depFiltro ? "en el depósito" : "total agrupado"}</p>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {p.logo_tipo && (
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${LOGO_CHIP[p.logo_tipo] || "bg-white/10 text-slate-300 border-white/10"}`}>
                                {LOGO_LABEL[p.logo_tipo] || p.logo_tipo}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => openMovimientos(p)} title="Ver movimientos / ajustar stock"
                                className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors">
                                <BarChart3 className="w-3.5 h-3.5" />
                              </button>
                              {canVerHistorialCostos && (
                                <button onClick={() => openHistorialModal(p)} title="Historial de costos"
                                  className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors">
                                  <DollarSign className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {hasPermission("inventario_productos.editar") && (
                                <button onClick={() => openEdit(p)} title="Editar"
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {hasPermission("inventario_productos.eliminar") && (
                                <button onClick={() => handleDelete(p)} title="Eliminar"
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <AuditoriaRegistroBtn registro={p} />
                            </div>
                          </td>
                        </tr>
                        {variantesActivas.map(v => {
                          const margenVar = calcGananciaPct(v.precio_costo, v.precio_venta);
                          const bajoMinVar = Number(v.stock_minimo || 0) > 0 && (depFiltro ? Number(v.stock_deposito || 0) : Number(v.stock_actual || 0)) <= Number(v.stock_minimo || 0);
                          return (
                            <tr key={`${p.id}-${v.id || v.atributos_texto}`} className="border-b border-white/5 bg-arandu-dark/25 hover:bg-white/3 transition-colors">
                              <td className="px-2 py-3"></td>
                              <td className="px-4 py-3">
                                <div className="pl-5 border-l border-emerald-500/25">
                                  <p className="text-emerald-100 text-sm font-medium">{v.atributos_texto || "Variante"}</p>
                                  <p className="text-[10px] text-slate-500 mt-0.5">Variante de {p.nombre}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 hidden md:table-cell">
                                <div className="flex flex-wrap gap-1">
                                  {(v.atributos_detalle || []).map(a => (
                                    <span key={`${v.id}-${a.atributo_id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-200 border border-emerald-500/15">
                                      {a.atributo_nombre}: {a.opcion_nombre}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              {puedeVerCostoCompra && <td className="px-4 py-3 text-right text-slate-300">{fmtMoneda(v.precio_costo, p.moneda)}</td>}
                              <td className="px-4 py-3 text-right text-white font-medium">{fmtMoneda(v.precio_venta, p.moneda)}</td>
                              {puedeVerCostoCompra && (
                                <td className="px-4 py-3 text-right">
                                  {margenVar !== "" ? (
                                    <span className={`text-xs font-semibold ${Number(margenVar) >= 20 ? "text-emerald-300" : Number(margenVar) >= 0 ? "text-amber-300" : "text-red-300"}`}>
                                      {margenVar}%
                                    </span>
                                  ) : <span className="text-slate-600">-</span>}
                                </td>
                              )}
                              <td className="px-4 py-3 text-center">
                                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                                  bajoMinVar ? "bg-orange-500/15 text-orange-300 border border-orange-500/25"
                                  : (depFiltro ? Number(v.stock_deposito || 0) : Number(v.stock_actual || 0)) === 0 ? "bg-red-500/15 text-red-300 border border-red-500/25"
                                  : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                                }`}>
                                  {bajoMinVar && <AlertTriangle className="w-3 h-3" />}
                                  {depFiltro ? Number(v.stock_deposito || 0) : Number(v.stock_actual || 0)} {p.unidad}
                                </div>
                                {Number(v.stock_minimo || 0) > 0 && (
                                  <p className="text-slate-600 text-[10px] mt-0.5">mín. {v.stock_minimo}</p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {p.logo_tipo ? (
                                  <span className={`text-xs px-2 py-0.5 rounded-full border ${LOGO_CHIP[p.logo_tipo] || "bg-white/10 text-slate-300 border-white/10"}`}>
                                    {LOGO_LABEL[p.logo_tipo] || p.logo_tipo}
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-slate-600">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-[10px] text-slate-600">-</span>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  }
                  return (
                    <tr key={p.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${!p.activo ? "opacity-40" : ""}`}>
                      <td className="px-2 py-3">
                        <div className="w-9 h-9 rounded-md border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden mx-auto">
                          {p.imagen_url ? <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-700" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{p.nombre}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {p.sku && <span className="text-[10px] text-slate-500 font-mono">#{p.sku}</span>}
                          {p.iva_tipo && p.iva_tipo !== "10" && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                              p.iva_tipo === "exenta"
                                ? "bg-slate-500/20 text-slate-400"
                                : "bg-amber-500/20 text-amber-400"
                            }`}>
                              {ivaLabel[p.iva_tipo] || p.iva_tipo}
                            </span>
                          )}
                          {p.descripcion && <span className="text-slate-500 text-xs truncate max-w-[200px]">{p.descripcion}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {p.categoria ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            esServ
                              ? "bg-violet-500/15 text-violet-300 border border-violet-500/25"
                              : "bg-white/10 text-slate-300"
                          }`}>
                            {p.categoria}
                          </span>
                        ) : <span className="text-slate-600">-</span>}
                        {p.subcategoria && (
                          <p className="text-[10px] text-violet-300 mt-1">{p.subcategoria}</p>
                        )}
                        {p.variantes_habilitadas && (
                          <p className="text-[10px] text-emerald-300 mt-1">{(p.variantes || []).length} variante{(p.variantes || []).length !== 1 ? "s" : ""} de stock</p>
                        )}
                        {p.variantes_habilitadas && variantesActivas.length > 0 && (
                          <div className="mt-2 space-y-1 max-w-[420px]">
                            {variantesActivas.slice(0, 4).map(v => (
                              <div key={v.id || v.atributos_texto} className="flex flex-wrap items-center gap-2 text-[10px] text-slate-400">
                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-200 border border-emerald-500/15">
                                  {v.atributos_texto}
                                </span>
                                <span>{depFiltro ? Number(v.stock_deposito || 0) : Number(v.stock_actual || 0)} {p.unidad}</span>
                                {puedeVerCostoCompra && <span>Costo {fmtMoneda(v.precio_costo, p.moneda)}</span>}
                                <span>Venta {fmtMoneda(v.precio_venta, p.moneda)}</span>
                              </div>
                            ))}
                            {variantesActivas.length > 4 && (
                              <p className="text-[10px] text-slate-500">+{variantesActivas.length - 4} variante{variantesActivas.length - 4 !== 1 ? "s" : ""} más</p>
                            )}
                          </div>
                        )}
                        {(p.atributos_detalle || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {(p.atributos_detalle || []).slice(0, 4).map(a => (
                              <span key={`${p.id}-${a.atributo_id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-200 border border-cyan-500/15">
                                {a.atributo_nombre}: {a.opcion_nombre}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      {puedeVerCostoCompra && <td className="px-4 py-3 text-right text-slate-300">{costoDisplay}</td>}
                      <td className="px-4 py-3 text-right text-white font-medium">{ventaDisplay}</td>
                      {puedeVerCostoCompra && (
                        <td className="px-4 py-3 text-right">
                          {margen !== null ? (
                            <span className={`text-xs font-semibold ${Number(margen) >= 20 ? "text-emerald-300" : Number(margen) >= 0 ? "text-amber-300" : "text-red-300"}`}>
                              {margen}%
                              {p.variantes_habilitadas && margenRango !== null && margenRango !== margen && ` - ${margenRango}%`}
                            </span>
                          ) : <span className="text-slate-600">-</span>}
                        </td>
                      )}
                      <td className="px-4 py-3 text-center">
                        {esServ ? (
                          <span className="text-xs text-slate-500 italic">Servicio</span>
                        ) : (
                          <>
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                              bajominimo ? "bg-orange-500/15 text-orange-300 border border-orange-500/25"
                              : stockVista === 0 ? "bg-red-500/15 text-red-300 border border-red-500/25"
                              : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                            }`}>
                              {bajominimo && <AlertTriangle className="w-3 h-3" />}
                              {stockVista} {p.unidad}
                            </div>
                            {p.variantes_habilitadas && (
                              <p className="text-emerald-300 text-[10px] mt-0.5">total por variantes</p>
                            )}
                            {p.stock_minimo > 0 && (
                              <p className="text-slate-600 text-[10px] mt-0.5">mín. {p.stock_minimo}</p>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {p.logo_tipo && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${LOGO_CHIP[p.logo_tipo] || "bg-white/10 text-slate-300 border-white/10"}`}>
                            {LOGO_LABEL[p.logo_tipo] || p.logo_tipo}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {!esServ && (
                            <button onClick={() => openMovimientos(p)} title="Ver movimientos / ajustar stock"
                              className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors">
                              <BarChart3 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canVerHistorialCostos && (
                            <button onClick={() => openHistorialModal(p)} title="Historial de costos"
                              className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors">
                              <DollarSign className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {hasPermission("inventario_productos.editar") && (
                            <button onClick={() => openEdit(p)} title="Editar"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {hasPermission("inventario_productos.eliminar") && (
                            <button onClick={() => handleDelete(p)} title="Eliminar"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <AuditoriaRegistroBtn registro={p} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPaginasProductos > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-white/[0.02]">
                <span className="text-slate-500 text-xs font-body">
                  Página {paginaActual} de {totalPaginasProductos}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                    disabled={paginaActual <= 1 || loading}
                    className="px-3 py-1.5 rounded-lg text-xs font-body bg-white/5 text-slate-300 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Anterior
                  </button>
                  <button
                    onClick={() => setPaginaActual(p => Math.min(totalPaginasProductos, p + 1))}
                    disabled={paginaActual >= totalPaginasProductos || loading}
                    className="px-3 py-1.5 rounded-lg text-xs font-body bg-white/5 text-slate-300 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Siguiente →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* ── Tab: Unidades de Medida ───────────────────────────── */}
      {activeTab === "unidades" && (
        <div className="p-6">
          {!canVerUnidades ? (
            <div className="text-center py-12 text-slate-500 font-body">Sin permiso para ver el catálogo de unidades</div>
          ) : loadingUnidades ? (
            <div className="text-center py-12 text-slate-500 animate-pulse font-body">Cargando unidades...</div>
          ) : unidadesAll.length === 0 ? (
            <div className="text-center py-16 text-slate-500 font-body">
              <Ruler className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay unidades registradas</p>
              {canCrearUnidades && (
                <button onClick={openNewUnidad} className="mt-3 text-cyan-400 hover:underline text-sm">
                  Agregar la primera unidad
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="border-b border-white/10 bg-white/3">
                    <th className="text-left px-4 py-3 text-slate-400 font-medium text-xs">Cód.</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium text-xs">Nombre</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium text-xs">Sigla</th>
                    <th className="text-right px-4 py-3 text-slate-400 font-medium text-xs">Cantidad</th>
                    <th className="text-right px-4 py-3 text-slate-400 font-medium text-xs">Peso Kg</th>
                    <th className="text-center px-4 py-3 text-slate-400 font-medium text-xs">Cod. DNIT</th>
                    <th className="text-center px-4 py-3 text-slate-400 font-medium text-xs">Estado</th>
                    <th className="text-center px-4 py-3 text-slate-400 font-medium text-xs">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {unidadesAll.map(u => (
                    <tr key={u.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${!u.activo ? "opacity-40" : ""}`}>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">{u.codigo}</td>
                      <td className="px-4 py-3 text-white font-medium">{u.nombre}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono bg-white/10 px-2 py-0.5 rounded text-cyan-300">{u.sigla}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300 font-mono text-xs">{u.cantidad}</td>
                      <td className="px-4 py-3 text-right text-slate-400 font-mono text-xs">
                        {u.peso_kg != null ? u.peso_kg : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs font-mono bg-violet-500/15 text-violet-300 border border-violet-500/25 px-2 py-0.5 rounded">{u.cod_dnit}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                          u.activo
                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
                            : "bg-red-500/15 text-red-300 border-red-500/25"
                        }`}>
                          {u.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {canEditarUnidades && (
                            <button onClick={() => openEditUnidad(u)} title="Editar"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canEliminarUnidades && (
                            <button onClick={() => handleDeleteUnidad(u)} title="Eliminar"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <AuditoriaRegistroBtn registro={u} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Códigos de Barra ────────────────────────────────── */}
      {activeTab === "codigos" && canGenerarEtiquetas && (() => {
        const barcodeQ = barcodeSearch.toLowerCase().trim();
        const barcodedProductos = productos.filter(p => {
          if (barcodeFilter === "con_codigo" && !p.codigo_barras) return false;
          if (barcodeFilter === "sin_codigo" && !!p.codigo_barras) return false;
          if (barcodeQ) {
            return p.nombre?.toLowerCase().includes(barcodeQ)
              || p.sku?.toLowerCase().includes(barcodeQ)
              || (p.codigo_barras || "").toLowerCase().includes(barcodeQ);
          }
          return true;
        });
        return (
          <div className="p-6 space-y-4">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-emerald-300 text-sm font-semibold font-body">Recomendado: {BARCODE_TYPES[barcodeConfig.barcode_recomendado] || "EAN-13 interno"}</p>
                  <p className="text-slate-400 text-xs font-body mt-1">{barcodeConfig.barcode_recomendado_mensaje}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-lg bg-white/10 text-slate-300 font-body whitespace-nowrap">
                  Default: {BARCODE_TYPES[barcodeConfig.barcode_tipo_default] || barcodeConfig.barcode_tipo_default}
                </span>
              </div>
            </div>
            {/* Filtros */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
                {[
                  { key: "todos", label: "Todos" },
                  { key: "con_codigo", label: "Con código" },
                  { key: "sin_codigo", label: "Sin código" },
                ].map(opt => (
                  <button key={opt.key} onClick={() => setBarcodeFilter(opt.key)}
                    className={`px-3 py-1.5 rounded-md text-xs font-body transition-colors ${
                      barcodeFilter === opt.key
                        ? "bg-cyan-600 text-white"
                        : "text-slate-400 hover:text-slate-300"
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input
                  value={barcodeSearch}
                  onChange={e => setBarcodeSearch(e.target.value)}
                  placeholder="Buscar por nombre, SKU o código..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                {barcodeSearch && (
                  <button onClick={() => setBarcodeSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <span className="text-slate-500 text-sm font-body">
                {barcodedProductos.length} producto{barcodedProductos.length !== 1 ? "s" : ""}
              </span>
              {barcodeConfig.barcode_permitir_elegir_tipo && canGenerarBarcode && (
                <select
                  value={barcodeGenerateType || barcodeConfig.barcode_tipo_default}
                  onChange={e => setBarcodeGenerateType(e.target.value)}
                  style={{ colorScheme: "dark" }}
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-emerald-500">
                  <option value="ean13_interno">Generar EAN-13 interno</option>
                  <option value="code128_interno">Generar Code 128 interno</option>
                </select>
              )}
              <span className="text-slate-500 text-xs font-body ml-auto">
                Al imprimir, elegís cuántas etiquetas del mismo producto. Máx. {BARCODE_LABELS_MAX} por hoja.
              </span>
            </div>

            {/* Lista */}
            {barcodedProductos.length === 0 ? (
              <div className="text-center py-16 text-slate-500 font-body">
                <ScanBarcode className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Sin productos en esta categoría</p>
              </div>
            ) : (
              <div className="space-y-2">
                {barcodedProductos.map(p => (
                  <div key={p.id} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-body font-medium truncate">{p.nombre}</p>
                      <p className="text-slate-500 text-xs font-mono">SKU: {p.sku}</p>
                    </div>
                    {p.codigo_barras ? (
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          {isValidEan13(p.codigo_barras) ? (
                            <div className="bg-white rounded-md p-1" dangerouslySetInnerHTML={{ __html: ean13Svg(p.codigo_barras, { height: 44, module: 1, quietMm: barcodeConfig.barcode_quiet_zone_mm }) }} />
                          ) : (
                            <span style={{ fontFamily: "'Libre Barcode 128 Text', monospace", fontSize: "40px", lineHeight: 1, color: "white", display: "block" }}>
                              {p.codigo_barras}
                            </span>
                          )}
                          <p className="text-slate-500 text-[10px] font-mono mt-0.5">{p.codigo_barras}</p>
                          <p className={`text-[10px] font-body ${isValidEan13(p.codigo_barras) ? "text-emerald-400" : "text-amber-400"}`}>
                            {isValidEan13(p.codigo_barras) ? "EAN-13 válido" : "Code 128 / manual"}
                          </p>
                        </div>
                        <button onClick={() => printBarcode(p)} title="Imprimir etiqueta"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600/20 text-cyan-300 border border-cyan-600/30 rounded-lg hover:bg-cyan-600/30 transition-colors text-xs font-body whitespace-nowrap">
                          <Printer className="w-3.5 h-3.5" /> Imprimir
                        </button>
                        {canEditarBarcode && (
                          <button onClick={() => editBarcodePrompt(p)} title="Editar solo código"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 text-blue-300 border border-blue-600/30 rounded-lg hover:bg-blue-600/30 transition-colors text-xs font-body whitespace-nowrap">
                            <Edit2 className="w-3.5 h-3.5" /> Editar
                          </button>
                        )}
                        {canEliminarBarcode && (
                          <button onClick={() => deleteBarcode(p)} title="Eliminar solo código"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 text-red-300 border border-red-600/30 rounded-lg hover:bg-red-600/30 transition-colors text-xs font-body whitespace-nowrap">
                            <Trash2 className="w-3.5 h-3.5" /> Quitar
                          </button>
                        )}
                      </div>
                    ) : canGenerarBarcode ? (
                      <button
                        onClick={() => generarBarcodeProducto(p)}
                        disabled={generatingCode === p.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600/20 text-violet-300 border border-violet-600/30 rounded-lg hover:bg-violet-600/30 transition-colors text-xs font-body disabled:opacity-60 whitespace-nowrap">
                        <ScanBarcode className="w-3.5 h-3.5" />
                        {generatingCode === p.id ? "Generando..." : "Generar y asignar"}
                      </button>
                    ) : (
                      <span className="text-slate-500 text-xs font-body">Sin código</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Tab: Historial de Costos ─────────────────────────────── */}
      {activeTab === "historial_costos" && canVerHistorialCostos && (() => {
        const allTerms = [...histCostoChips, ...(histCostoInput.trim() ? [histCostoInput.trim()] : [])];
        const filteredHist = historialCostos.filter(h => {
          const txt = `${h.producto_nombre} ${h.motivo} ${h.usuario_nombre || ""}`.toLowerCase();
          return allTerms.every(t => txt.includes(t.toLowerCase()));
        });
        const addChip = () => {
          const t = histCostoInput.trim().replace(/,$/, "");
          if (t && !histCostoChips.includes(t)) setHistCostoChips(prev => [...prev, t]);
          setHistCostoInput("");
        };
        return (
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="font-heading text-base text-white shrink-0">Historial de Costos</h2>
              <div className="flex items-center gap-2 flex-1">
                <div className="flex-1 space-y-2">
                  {histCostoChips.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                      {histCostoChips.map((chip, idx) => (
                        <span key={idx} className="flex items-center gap-1.5 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 rounded-full px-3 py-1 text-xs font-body">
                          <Search className="w-3 h-3" /> {chip}
                          <button onClick={() => setHistCostoChips(prev => prev.filter((_, i) => i !== idx))} className="ml-1 hover:text-white transition-colors"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                      <button onClick={() => setHistCostoChips([])} className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-full border border-white/10 hover:border-white/20 transition-all font-body">Limpiar</button>
                    </div>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      value={histCostoInput}
                      onChange={e => setHistCostoInput(e.target.value)}
                      onKeyDown={e => {
                        if ((e.key === "Enter" || e.key === ",") && histCostoInput.trim()) { e.preventDefault(); addChip(); }
                        if (e.key === "Backspace" && histCostoInput === "" && histCostoChips.length > 0) setHistCostoChips(prev => prev.slice(0, -1));
                      }}
                      placeholder="Buscar producto, motivo, usuario... (Enter para agregar filtro)"
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-white text-sm font-body focus:outline-none focus:border-cyan-500/60 placeholder-slate-500"
                    />
                  </div>
                </div>
                <button
                  onClick={fetchHistorialCostos}
                  disabled={loadingHistCostos}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white text-xs font-body transition-colors disabled:opacity-50 shrink-0">
                  {loadingHistCostos ? "Cargando..." : "Actualizar"}
                </button>
              </div>
            </div>

            {loadingHistCostos ? (
              <p className="text-center text-slate-500 py-10 font-body text-sm">Cargando historial...</p>
            ) : filteredHist.length === 0 ? (
              <p className="text-center text-slate-500 py-10 font-body text-sm">
                {historialCostos.length === 0 ? "Sin registros aún. Los cambios de costo aparecerán aquí." : "Sin resultados para la búsqueda."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-body">
                  <thead>
                    <tr className="border-b border-white/10 text-left">
                      <th className="pb-2 pr-4 text-slate-400 text-xs font-body">Fecha</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs font-body">Producto</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs font-body text-right">Costo anterior</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs font-body text-right">Costo nuevo</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs font-body text-right">Diferencia</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs font-body">Motivo</th>
                      <th className="pb-2 text-slate-400 text-xs font-body">Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHist.map(h => {
                      const diff = h.diferencia ?? (h.costo_nuevo - h.costo_anterior);
                      const diffColor = diff > 0 ? "text-red-400" : diff < 0 ? "text-emerald-400" : "text-slate-400";
                      const diffSign = diff > 0 ? "+" : "";
                      return (
                        <tr key={h.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-2.5 pr-4 text-slate-400 whitespace-nowrap">{h.fecha}</td>
                          <td className="py-2.5 pr-4 text-white">{h.producto_nombre}{h.variante_texto && <span className="block text-[10px] text-slate-400">{h.variante_texto}</span>}</td>
                          <td className="py-2.5 pr-4 text-slate-300 text-right whitespace-nowrap">
                            ₲ {Math.round(h.costo_anterior).toLocaleString("es-PY")}
                          </td>
                          <td className="py-2.5 pr-4 text-white text-right whitespace-nowrap">
                            ₲ {Math.round(h.costo_nuevo).toLocaleString("es-PY")}
                          </td>
                          <td className={`py-2.5 pr-4 text-right whitespace-nowrap ${diffColor}`}>
                            {diffSign}₲ {Math.round(diff).toLocaleString("es-PY")}
                          </td>
                          <td className="py-2.5 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-body ${h.motivo === "compra" ? "bg-cyan-500/15 text-cyan-300" : h.motivo === "costo_inicial" ? "bg-slate-500/20 text-slate-300" : "bg-amber-500/15 text-amber-300"}`}>
                              {h.motivo === "compra" ? "Compra" : h.motivo === "costo_inicial" ? "Costo inicial" : "Ajuste manual"}
                            </span>
                          </td>
                          <td className="py-2.5 text-slate-400 text-xs">{h.usuario_nombre || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Tab: Configuración ───────────────────────────────── */}
      {activeTab === "configuracion" && (
        <div className="p-6">

          {/* ── Overview: Cards de acceso (como FinancieroPage) ── */}
          {configSubTab === null && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-base text-white mb-1">Configuración de Productos</h2>
                <p className="text-slate-500 text-xs font-body">Administrá categorías, subcategorías y opciones de código.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                {/* Card Categorías */}
                {canGestCats && (
                  <button onClick={() => setConfigSubTab("categorias")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center mb-3 group-hover:bg-cyan-500/25 transition-colors">
                      <Tag className="w-5 h-5 text-cyan-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Categorías</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Creá y gestioná las categorías de productos con sus campos de trazabilidad.</p>
                    <p className="text-cyan-400 text-xs font-body mt-3 group-hover:underline">{categorias.filter(c => !c.deshabilitada).length} categoría{categorias.filter(c => !c.deshabilitada).length !== 1 ? "s" : ""} →</p>
                  </button>
                )}
                {/* Card Subcategorías */}
                {(canGestSubs || canToggleClasificaciones) && (
                  <button onClick={() => setConfigSubTab("subcategorias")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center mb-3 group-hover:bg-violet-500/25 transition-colors">
                      <Layers className="w-5 h-5 text-violet-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Subcategorías</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Clasificación adicional dentro de cada categoría principal.</p>
                    <p className="text-violet-400 text-xs font-body mt-3 group-hover:underline">
                      {usaSubcategorias ? `${subcategoriasActivas.length} activa${subcategoriasActivas.length !== 1 ? "s" : ""}` : "Desactivado"} →
                    </p>
                  </button>
                )}
                {/* Card Atributos */}
                {(canGestAttrs || canToggleClasificaciones) && (
                  <button onClick={() => setConfigSubTab("atributos")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center mb-3 group-hover:bg-blue-500/25 transition-colors">
                      <Tag className="w-5 h-5 text-blue-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Atributos</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Valores controlados para filtros como marca, talle, color o presentación.</p>
                    <p className="text-blue-400 text-xs font-body mt-3 group-hover:underline">
                      {usaAtributos ? `${atributosActivos.length} activo${atributosActivos.length !== 1 ? "s" : ""}` : "Desactivado"} →
                    </p>
                  </button>
                )}
                {/* Card SKU */}
                {canVerConfig && (
                  <button onClick={() => setConfigSubTab("sku")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-amber-500/40 hover:bg-amber-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center mb-3 group-hover:bg-amber-500/25 transition-colors">
                      <Zap className="w-5 h-5 text-amber-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Código SKU</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Configurá el generador automático de códigos de producto.</p>
                    <p className="text-amber-400 text-xs font-body mt-3 group-hover:underline">
                      {configSku.modo_codigo === "automatico" ? "Modo automático" : "Modo manual"} →
                    </p>
                  </button>
                )}
                {(canVerConfig || canConfigBarcode) && (
                  <button onClick={() => setConfigSubTab("barcode")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-3 group-hover:bg-emerald-500/25 transition-colors">
                      <ScanBarcode className="w-5 h-5 text-emerald-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Códigos de barra</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Tipo default, prefijo EAN-13, quiet zone y recomendación visible.</p>
                    <p className="text-emerald-400 text-xs font-body mt-3 group-hover:underline">
                      {BARCODE_TYPES[barcodeConfig.barcode_tipo_default] || "EAN-13 interno"} →
                    </p>
                  </button>
                )}
                {(hasPermission("inventario_productos.gestionar_depositos") || hasPermission("inventario_productos.ver_depositos")) && (
                  <button onClick={() => setConfigSubTab("depositos")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-teal-500/40 hover:bg-teal-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-teal-500/15 flex items-center justify-center mb-3 group-hover:bg-teal-500/25 transition-colors">
                      <Warehouse className="w-5 h-5 text-teal-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Depósitos</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Controlá el stock por depósito, definí el predeterminado y transferí entre ellos.</p>
                    <p className="text-teal-400 text-xs font-body mt-3 group-hover:underline">
                      {dep.activo ? `${dep.depositos.length} depósito${dep.depositos.length !== 1 ? "s" : ""}` : "Sin depósitos"} →
                    </p>
                  </button>
                )}
                {(canVerConfig || canGestionarUnicidad) && (
                  <button onClick={() => setConfigSubTab("unicidad")}
                    className="group text-left border border-white/10 rounded-2xl p-5 hover:border-rose-500/40 hover:bg-rose-500/5 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/15 flex items-center justify-center mb-3 group-hover:bg-rose-500/25 transition-colors">
                      <Package className="w-5 h-5 text-rose-400" />
                    </div>
                    <p className="font-heading text-sm text-white">Nombres duplicados</p>
                    <p className="text-slate-500 text-xs font-body mt-1">Define si el catálogo permite repetir nombres y diferenciar por SKU.</p>
                    <p className="text-rose-400 text-xs font-body mt-3 group-hover:underline">
                      {configSku.permitir_nombres_duplicados ? "Permitidos" : "Bloqueados"} →
                    </p>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Sub-tab: Categorías ──────────────────────────────── */}
          {configSubTab === "categorias" && (
            <div className="space-y-4">
              {/* Header con volver */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={() => setConfigSubTab(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div>
                    <h2 className="font-heading text-base text-white flex items-center gap-2">
                      <Tag className="w-4 h-4 text-cyan-400" /> Categorías de Productos
                    </h2>
                    <p className="text-slate-500 text-xs font-body mt-0.5">Controlá los campos de trazabilidad por categoría</p>
                  </div>
                </div>
                <button
                  onClick={openNewCat}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-sm">
                  <Plus className="w-4 h-4" /> Nueva categoría
                </button>
              </div>

              {/* Tabla de categorías */}
              {categorias.length === 0 ? (
                <div className="text-center py-12 text-slate-500 font-body border border-white/5 rounded-xl">
                  <Tag className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay categorías registradas</p>
                </div>
              ) : (
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <table className="w-full text-sm font-body">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/3">
                        <th className="text-left px-4 py-2.5 text-slate-400 text-xs font-medium">Categoría</th>
                        <th className="text-center px-4 py-2.5 text-slate-400 text-xs font-medium">
                          <Factory className="w-3 h-3 inline mr-1" />Cód. Fabricante
                        </th>
                        <th className="text-center px-4 py-2.5 text-slate-400 text-xs font-medium">
                          <FlaskConical className="w-3 h-3 inline mr-1" />Lote
                        </th>
                        <th className="text-center px-4 py-2.5 text-slate-400 text-xs font-medium">
                          <CalendarClock className="w-3 h-3 inline mr-1" />Vencimiento
                        </th>
                        <th className="text-left px-4 py-2.5 text-slate-400 text-xs font-medium">Atributos</th>
                        <th className="text-center px-4 py-2.5 text-slate-400 text-xs font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorias.map(cat => (
                        <tr key={cat.id} className={`border-b border-white/5 last:border-0 hover:bg-white/3 transition-colors ${cat.deshabilitada ? "opacity-40" : ""}`}>
                          <td className="px-4 py-3 text-white font-medium">
                            {cat.nombre}
                            {cat.es_global && <span title="Categoría global: la tienen todas las empresas" className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-violet-500/15 text-violet-300 align-middle"><Globe className="w-2.5 h-2.5" />Global</span>}
                            {cat.deshabilitada && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-slate-500/20 text-slate-300 align-middle">Deshabilitada</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {cat.tiene_codigo_fabricante
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-cyan-500/15 text-cyan-300">Sí</span>
                              : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {cat.tiene_lote
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-cyan-500/15 text-cyan-300">Sí</span>
                              : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {cat.tiene_vencimiento
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-cyan-500/15 text-cyan-300">Sí</span>
                              : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {(cat.atributos_habilitados || []).length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {(cat.atributos_habilitados || []).map(attrId => attrById(attrId)).filter(Boolean).slice(0, 5).map(a => (
                                  <span key={`${cat.id}-${a.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/15">
                                    {a.nombre}
                                  </span>
                                ))}
                              </div>
                            ) : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <AuditoriaRegistroBtn registro={cat} />
                              <button onClick={() => openEditCat(cat)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              {esCatServicio(cat.nombre) ? (
                                <span title="Categoría estándar del sistema: no se puede eliminar" className="p-1.5 text-slate-600 text-[10px]">Sistema</span>
                              ) : cat.es_global ? (
                              canDeshabCats ? <button onClick={() => handleToggleDeshabilitarCat(cat)}
                                title={cat.deshabilitada ? "Habilitar para esta empresa" : "Deshabilitar para esta empresa (las globales no se pueden eliminar)"}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors">
                                {cat.deshabilitada ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                              </button> : <span title="Categoría global: no se puede eliminar" className="p-1.5 text-slate-600 text-[10px]">Global</span>
                              ) : (
                              <>
                                {canHacerGlobalCat && (
                                  <button onClick={() => handleHacerGlobalCat(cat)} title="Hacer global (todas las empresas)"
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-violet-300 hover:bg-violet-500/10 transition-colors">
                                    <Globe className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button onClick={() => handleDeleteCat(cat)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Sub-tab: Atributos ──────────────────────────────── */}
          {configSubTab === "atributos" && (canGestAttrs || canToggleClasificaciones) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={() => setConfigSubTab(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div>
                    <h2 className="font-heading text-base text-white flex items-center gap-2">
                      <Tag className="w-4 h-4 text-blue-400" /> Atributos de Productos
                    </h2>
                    <p className="text-slate-500 text-xs font-body mt-0.5">Opciones controladas que después se habilitan por categoría</p>
                  </div>
                </div>
                {canGestAttrs && (
                  <button
                    onClick={openNewAttr}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-body text-sm">
                    <Plus className="w-4 h-4" /> Nuevo atributo
                  </button>
                )}
              </div>

              <div className={`border rounded-xl p-4 flex items-center justify-between gap-4 ${
                usaAtributos ? "border-blue-500/25 bg-blue-500/5" : "border-white/10 bg-white/3"
              }`}>
                <div>
                  <p className="text-white text-sm font-heading flex items-center gap-2">
                    {usaAtributos ? <ToggleRight className="w-4 h-4 text-blue-300" /> : <ToggleLeft className="w-4 h-4 text-slate-500" />}
                    Uso de atributos y variantes en esta empresa
                  </p>
                  <p className="text-slate-500 text-xs font-body mt-1">Si está desactivado, no aparecen atributos, variantes ni filtros avanzados.</p>
                </div>
                <button
                  type="button"
                  disabled={!canToggleClasificaciones}
                  onClick={() => handleSaveUsoClasificaciones({ usar_atributos: !usaAtributos })}
                  className={`px-4 py-2 rounded-lg text-sm font-body transition-colors disabled:opacity-50 ${
                    usaAtributos ? "bg-blue-500/15 text-blue-200 border border-blue-500/30" : "bg-white/10 text-slate-300 border border-white/10"
                  }`}
                  title={canToggleClasificaciones ? "Cambiar uso de atributos" : "Sin permiso para habilitar/deshabilitar"}
                >
                  {usaAtributos ? "En uso" : "No se usa"}
                </button>
              </div>

              {atributosProductos.length === 0 ? (
                <div className="text-center py-12 text-slate-500 font-body border border-white/5 rounded-xl">
                  <Tag className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay atributos registrados</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {atributosProductos.map(a => (
                    <div key={a.id} className="border border-white/10 rounded-xl p-4 bg-white/3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-white text-sm font-heading flex items-center gap-2">
                            {a.nombre}
                            {a.sistema && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">Predeterminado</span>}
                            {a.activo === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/10 text-slate-400 border border-slate-500/20">Inactivo</span>}
                          </p>
                          <p className="text-slate-500 text-[10px] font-mono mt-0.5">{a.slug}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <AuditoriaRegistroBtn registro={a} />
                          {canGestAttrs && (
                            <button onClick={() => openEditAttr(a)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canGestAttrs && !a.sistema && (
                            <button onClick={() => handleDeleteAttr(a)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {(a.opciones || []).map(o => (
                          <span key={o.id} className="text-[10px] px-1.5 py-0.5 rounded bg-white/7 text-slate-300 border border-white/10">
                            {o.nombre}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Sub-tab: Subcategorías ───────────────────────────── */}
          {configSubTab === "subcategorias" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={() => setConfigSubTab(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div>
                    <h2 className="font-heading text-base text-white flex items-center gap-2">
                      <Layers className="w-4 h-4 text-violet-400" /> Subcategorías de Productos
                    </h2>
                    <p className="text-slate-500 text-xs font-body mt-0.5">Clasificación adicional dentro de cada categoría principal</p>
                  </div>
                </div>
                {canGestSubs && (
                  <button
                    onClick={() => { setEditingSubId(null); setSubForm(emptySub); setShowSubForm(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-body text-sm">
                    <Plus className="w-4 h-4" /> Nueva subcategoría
                  </button>
                )}
              </div>

              <div className={`border rounded-xl p-4 flex items-center justify-between gap-4 ${
                usaSubcategorias ? "border-violet-500/25 bg-violet-500/5" : "border-white/10 bg-white/3"
              }`}>
                <div>
                  <p className="text-white text-sm font-heading flex items-center gap-2">
                    {usaSubcategorias ? <ToggleRight className="w-4 h-4 text-violet-300" /> : <ToggleLeft className="w-4 h-4 text-slate-500" />}
                    Uso de subcategorías en esta empresa
                  </p>
                  <p className="text-slate-500 text-xs font-body mt-1">Si está desactivado, no aparece en productos, compras, ventas ni reportes.</p>
                </div>
                <button
                  type="button"
                  disabled={!canToggleClasificaciones}
                  onClick={() => handleSaveUsoClasificaciones({ usar_subcategorias: !usaSubcategorias })}
                  className={`px-4 py-2 rounded-lg text-sm font-body transition-colors disabled:opacity-50 ${
                    usaSubcategorias ? "bg-violet-500/15 text-violet-200 border border-violet-500/30" : "bg-white/10 text-slate-300 border border-white/10"
                  }`}
                  title={canToggleClasificaciones ? "Cambiar uso de subcategorías" : "Sin permiso para habilitar/deshabilitar"}
                >
                  {usaSubcategorias ? "En uso" : "No se usa"}
                </button>
              </div>

              {subcategorias.length === 0 ? (
                <div className="text-center py-12 text-slate-500 font-body border border-white/5 rounded-xl">
                  <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay subcategorías registradas</p>
                  {canGestSubs && (
                    <button
                      onClick={() => { setEditingSubId(null); setSubForm(emptySub); setShowSubForm(true); }}
                      className="mt-3 text-violet-400 hover:underline text-sm">
                      Crear la primera subcategoría
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {categorias.map(cat => {
                    const subsDeCategoria = subcategorias.filter(s => s.categoria === cat.nombre);
                    if (subsDeCategoria.length === 0) return null;
                    return (
                      <div key={cat.id || cat.nombre} className="border border-white/10 rounded-xl overflow-hidden">
                        <div className="bg-white/3 px-4 py-2 border-b border-white/10">
                          <span className="text-xs font-body font-semibold text-slate-400 uppercase tracking-wide">{cat.nombre}</span>
                          <span className="ml-2 text-[10px] text-slate-600">{subsDeCategoria.length} subcategoría{subsDeCategoria.length !== 1 ? "s" : ""}</span>
                        </div>
                        <table className="w-full text-sm font-body">
                          <thead>
                            <tr className="border-b border-white/5">
                              <th className="text-left px-4 py-2 text-slate-500 text-xs font-medium">Nombre</th>
                              <th className="text-left px-4 py-2 text-slate-500 text-xs font-medium">Prefijo SKU</th>
                              <th className="text-center px-4 py-2 text-slate-500 text-xs font-medium">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subsDeCategoria.map(s => (
                              <tr key={s.id} className="border-b border-white/5 last:border-0 hover:bg-white/3 transition-colors">
                                <td className="px-4 py-2.5 text-white">
                                  {s.nombre}
                                  {s.activo === false && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-500/10 text-slate-400 border border-slate-500/20">Inactiva</span>}
                                </td>
                                <td className="px-4 py-2.5">
                                  {s.prefijo ? (
                                    <code className="text-xs font-mono bg-violet-500/10 text-violet-300 px-2 py-0.5 rounded">{s.prefijo}</code>
                                  ) : (
                                    <span className="text-slate-600 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center justify-center gap-2">
                                    <AuditoriaRegistroBtn registro={s} />
                                    {canGestSubs && (
                                      <>
                                        <button onClick={() => openEditSub(s)}
                                          className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                                          <Edit2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => handleDeleteSub(s)}
                                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {configSubTab === "depositos" && (
            <div className="space-y-4">
              <button onClick={() => setConfigSubTab(null)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm"><ArrowLeft className="w-4 h-4" /> Volver</button>
              <DepositosPanel token={token} logo={activeEmpresaPropia?.slug}
                puedeGestionar={hasPermission("inventario_productos.gestionar_depositos")}
                puedeTransferir={hasPermission("inventario_productos.transferir_depositos")}
                puedeAnular={hasPermission("inventario_productos.anular_transferencia_deposito")} />
            </div>
          )}

          {configSubTab === "unicidad" && (canVerConfig || canGestionarUnicidad) && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <button onClick={() => setConfigSubTab(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div>
                  <h2 className="font-heading text-base text-white flex items-center gap-2">
                    <Package className="w-4 h-4 text-rose-400" /> Reglas del Catálogo
                  </h2>
                  <p className="text-slate-500 text-xs font-body mt-0.5">El SKU sigue siendo único por empresa, aunque se repita el nombre.</p>
                </div>
              </div>

              <div className={`border rounded-xl p-5 flex items-center justify-between gap-4 ${
                configSku.permitir_nombres_duplicados ? "border-rose-500/25 bg-rose-500/5" : "border-white/10 bg-white/3"
              }`}>
                <div>
                  <p className="text-white text-sm font-heading flex items-center gap-2">
                    {configSku.permitir_nombres_duplicados ? <ToggleRight className="w-4 h-4 text-rose-300" /> : <ToggleLeft className="w-4 h-4 text-slate-500" />}
                    Permitir nombres repetidos
                  </p>
                  <p className="text-slate-500 text-xs font-body mt-1 max-w-xl">
                    Si está activo, podés tener varios productos llamados igual. El sistema los distingue por SKU, código de barras e ID interno para stock, compras, ventas y reportes.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!canGestionarUnicidad}
                  onClick={() => handleSaveUnicidadProductos(!configSku.permitir_nombres_duplicados)}
                  className={`px-4 py-2 rounded-lg text-sm font-body transition-colors disabled:opacity-50 ${
                    configSku.permitir_nombres_duplicados ? "bg-rose-500/15 text-rose-200 border border-rose-500/30" : "bg-white/10 text-slate-300 border border-white/10"
                  }`}
                  title={canGestionarUnicidad ? "Cambiar regla de nombres" : "Sin permiso para cambiar esta regla"}
                >
                  {configSku.permitir_nombres_duplicados ? "Permitidos" : "Bloqueados"}
                </button>
              </div>
            </div>
          )}

          {/* ── Sub-tab: SKU ─────────────────────────────────────── */}
          {configSubTab === "sku" && canVerConfig && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <button onClick={() => setConfigSubTab(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div>
                  <h2 className="font-heading text-base text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" /> Generador de Código SKU
                  </h2>
                  <p className="text-slate-500 text-xs font-body mt-0.5">Configurá el modo y formato del código automático</p>
                </div>
              </div>
              <div className="border border-white/10 rounded-xl overflow-hidden">
                <form onSubmit={handleSaveConfigSku} className="p-5 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {/* Modo */}
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Modo de código</label>
                      <div className="flex flex-col gap-2">
                        {[
                          { val: "manual", label: "Manual", desc: "El usuario ingresa el SKU" },
                          { val: "automatico", label: "Automático", desc: "Se genera según prefijo + secuencia" },
                        ].map(opt => (
                          <label key={opt.val} className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            configSku.modo_codigo === opt.val
                              ? "border-amber-500/40 bg-amber-500/10"
                              : "border-white/10 bg-white/3 hover:bg-white/5"
                          }`}>
                            <input
                              type="radio"
                              name="modo_codigo"
                              value={opt.val}
                              checked={configSku.modo_codigo === opt.val}
                              onChange={() => setConfigSku(c => ({ ...c, modo_codigo: opt.val }))}
                              disabled={!canConfigSku}
                              className="mt-0.5 accent-amber-500"
                            />
                            <div>
                              <p className="text-white text-xs font-medium font-body">{opt.label}</p>
                              <p className="text-slate-500 text-[10px] font-body">{opt.desc}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Separador */}
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Separador</label>
                      <select
                        value={configSku.sep}
                        onChange={e => setConfigSku(c => ({ ...c, sep: e.target.value }))}
                        disabled={!canConfigSku || configSku.modo_codigo === "manual"}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-amber-500 disabled:opacity-50">
                        <option value="-">Guión  ( - )</option>
                        <option value="_">Guión bajo ( _ )</option>
                        <option value=".">Punto ( . )</option>
                        <option value="">Sin separador</option>
                      </select>
                      <p className="text-slate-600 text-[10px] mt-1.5 font-body">
                        Ejemplo: <code className="font-mono">MER{configSku.sep || ""}001</code>
                      </p>
                    </div>

                    {/* Longitud secuencia */}
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Dígitos de secuencia</label>
                      <input
                        type="number"
                        min={1}
                        max={8}
                        value={configSku.longitud_seq}
                        onChange={e => setConfigSku(c => ({ ...c, longitud_seq: parseInt(e.target.value) || 3 }))}
                        disabled={!canConfigSku || configSku.modo_codigo === "manual"}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-amber-500 disabled:opacity-50"
                      />
                      <p className="text-slate-600 text-[10px] mt-1.5 font-body">
                        Ejemplo: {configSku.longitud_seq} dígitos → <code className="font-mono">{String(1).padStart(parseInt(configSku.longitud_seq) || 3, "0")}</code>
                      </p>
                    </div>
                  </div>

                  {canConfigSku && (
                    <div className="flex justify-end pt-1">
                      <button type="submit" disabled={savingConfigSku}
                        className="flex items-center gap-2 px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-body text-sm disabled:opacity-60">
                        <Save className="w-4 h-4" /> {savingConfigSku ? "Guardando..." : "Guardar configuración"}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          )}

          {/* ── Sub-tab: Códigos de barra ───────────────────────── */}
          {configSubTab === "barcode" && canVerConfig && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <button onClick={() => setConfigSubTab(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div>
                  <h2 className="font-heading text-base text-white flex items-center gap-2">
                    <ScanBarcode className="w-4 h-4 text-emerald-400" /> Configuración de códigos de barra
                  </h2>
                  <p className="text-slate-500 text-xs font-body mt-0.5">Definí el formato default, el recomendado y los parámetros de impresión</p>
                </div>
              </div>

              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-emerald-300 text-sm font-semibold font-body">Recomendado actual: {BARCODE_TYPES[barcodeConfig.barcode_recomendado]}</p>
                <p className="text-slate-400 text-xs font-body mt-1">{barcodeConfig.barcode_recomendado_mensaje}</p>
              </div>

              <div className="border border-white/10 rounded-xl overflow-hidden">
                <form onSubmit={handleSaveBarcodeConfig} className="p-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Tipo default al generar</label>
                      <select
                        value={barcodeConfig.barcode_tipo_default}
                        onChange={e => setBarcodeConfig(c => ({ ...c, barcode_tipo_default: e.target.value }))}
                        disabled={!canConfigBarcode}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-emerald-500 disabled:opacity-50">
                        <option value="ean13_interno">EAN-13 interno</option>
                        <option value="code128_interno">Code 128 interno</option>
                        <option value="manual">Manual / comercial</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Tipo recomendado</label>
                      <select
                        value={barcodeConfig.barcode_recomendado}
                        onChange={e => setBarcodeConfig(c => ({ ...c, barcode_recomendado: e.target.value }))}
                        disabled={!canConfigBarcode}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-emerald-500 disabled:opacity-50">
                        <option value="ean13_interno">EAN-13 interno</option>
                        <option value="code128_interno">Code 128 interno</option>
                        <option value="manual">Manual / comercial</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Prefijo EAN-13 interno</label>
                      <input
                        value={barcodeConfig.barcode_prefijo_ean13}
                        onChange={e => setBarcodeConfig(c => ({ ...c, barcode_prefijo_ean13: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                        disabled={!canConfigBarcode}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body font-mono focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                      />
                      <p className="text-slate-600 text-[10px] mt-1.5 font-body">Ejemplo recomendado: 200 para uso interno.</p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-2 font-body">Quiet zone mínima (mm)</label>
                      <input
                        type="number"
                        min={5}
                        value={barcodeConfig.barcode_quiet_zone_mm}
                        onChange={e => setBarcodeConfig(c => ({ ...c, barcode_quiet_zone_mm: Math.max(5, parseInt(e.target.value, 10) || 5) }))}
                        disabled={!canConfigBarcode}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <label className={`flex items-center gap-3 p-3 rounded-lg border ${barcodeConfig.barcode_permitir_elegir_tipo ? "border-emerald-500/30 bg-emerald-500/10" : "border-white/10 bg-white/3"}`}>
                    <input
                      type="checkbox"
                      checked={!!barcodeConfig.barcode_permitir_elegir_tipo}
                      onChange={e => setBarcodeConfig(c => ({ ...c, barcode_permitir_elegir_tipo: e.target.checked }))}
                      disabled={!canConfigBarcode}
                      className="accent-emerald-500"
                    />
                    <span className="text-sm text-slate-300 font-body">Permitir elegir tipo al generar códigos</span>
                  </label>

                  <div>
                    <label className="block text-slate-400 text-xs mb-2 font-body">Mensaje recomendado visible</label>
                    <textarea
                      rows={3}
                      value={barcodeConfig.barcode_recomendado_mensaje}
                      onChange={e => setBarcodeConfig(c => ({ ...c, barcode_recomendado_mensaje: e.target.value }))}
                      disabled={!canConfigBarcode}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-400 text-xs mb-2 font-body">Diseño de etiqueta activo</label>
                    <select
                      value={barcodeConfig.etiqueta_modelo_impresion || ""}
                      onChange={e => setBarcodeConfig(c => ({ ...c, etiqueta_modelo_impresion: e.target.value }))}
                      disabled={!canConfigBarcode}
                      style={{ colorScheme: "dark" }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-emerald-500 disabled:opacity-50">
                      {disenosEtiqueta.length === 0 && <option value="">Sin diseños — creá uno abajo</option>}
                      {disenosEtiqueta.map(d => (
                        <option key={d.id} value={d.id}>{d.nombre} ({d.ancho_etiqueta_mm}×{d.alto_etiqueta_mm}mm, {d.columnas} col.)</option>
                      ))}
                    </select>
                    <p className="text-slate-600 text-[10px] mt-1.5 font-body">
                      Define qué diseño se usa al imprimir etiquetas desde el catálogo. Los diseños se crean y editan abajo.
                    </p>
                  </div>

                  {canConfigBarcode && (
                    <div className="flex justify-end pt-1">
                      <button type="submit" disabled={savingBarcodeConfig}
                        className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-body text-sm disabled:opacity-60">
                        <Save className="w-4 h-4" /> {savingBarcodeConfig ? "Guardando..." : "Guardar configuración"}
                      </button>
                    </div>
                  )}
                </form>
              </div>

              {/* ── Diseños de etiqueta: CRUD + vista previa ─────────── */}
              {canVerDisenos && (
                <div className="border border-white/10 rounded-xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-sm text-white">Diseños de etiqueta</h3>
                      <p className="text-slate-500 text-xs font-body mt-0.5">Creá y editá tus propios modelos: tamaño de hoja/rollo, tamaño de etiqueta, columnas, separaciones y qué mostrar.</p>
                    </div>
                    {canCrearDisenos && (
                      <button onClick={openNuevoDiseno}
                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-body text-xs shrink-0">
                        <Plus className="w-3.5 h-3.5" /> Nuevo diseño
                      </button>
                    )}
                  </div>

                  {disenosEtiqueta.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4 text-center">Todavía no hay diseños — creá el primero.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {disenosEtiqueta.map(d => (
                        <div key={d.id} className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${d.id === barcodeConfig.etiqueta_modelo_impresion ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`}>
                          <div>
                            <p className="text-white text-sm font-medium font-body">{d.nombre}</p>
                            <p className="text-slate-500 text-xs font-body mt-0.5">
                              {d.ancho_etiqueta_mm}×{d.alto_etiqueta_mm}mm · {d.columnas} col. · hoja {d.ancho_hoja_mm}mm
                            </p>
                            {d.id === barcodeConfig.etiqueta_modelo_impresion && (
                              <span className="inline-block mt-1 text-[10px] text-emerald-400 font-body">● Activo</span>
                            )}
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            {canEditarDisenos && (
                              <button onClick={() => openEditarDiseno(d)} title="Editar"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canEliminarDisenos && (
                              <button onClick={() => handleEliminarDiseno(d)} title="Eliminar"
                                className="p-1.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* ── Modal: Diseño de etiqueta (crear/editar) con vista previa ── */}
      {showDisenoForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="font-heading text-white text-base">{editingDisenoId ? "Editar diseño de etiqueta" : "Nuevo diseño de etiqueta"}</h3>
              <button onClick={() => setShowDisenoForm(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleGuardarDiseno} className="p-5 space-y-5">
              <div>
                <label className="block text-slate-400 text-xs mb-1.5 font-body">Nombre del diseño</label>
                <input value={disenoForm.nombre} onChange={e => setDisenoForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Etiqueta 33x22 Triple" required
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-emerald-500" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["ancho_hoja_mm", "Ancho de hoja/rollo (mm)"],
                  ["ancho_etiqueta_mm", "Ancho de etiqueta (mm)"],
                  ["alto_etiqueta_mm", "Alto de etiqueta (mm)"],
                  ["columnas", "Etiquetas por fila"],
                  ["espacio_h_mm", "Separación horizontal (mm)"],
                  ["espacio_v_mm", "Separación vertical entre filas (mm)"],
                  ["margen_inicial_mm", "Margen inicial / izquierdo (mm)"],
                  ["margen_superior_mm", "Margen superior (mm)"],
                  ["logo_alto_mm", "Alto del logo (mm) — 0 = auto"],
                  ["barras_alto_mm", "Alto de las barras (mm) — 0 = auto"],
                ].map(([campo, label]) => (
                  <div key={campo}>
                    <label className="block text-slate-500 text-[11px] mb-1 font-body">{label}</label>
                    <input type="number" step="0.1" min="0" value={disenoForm[campo]}
                      onChange={e => setDisenoForm(f => ({ ...f, [campo]: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-sm font-body focus:outline-none focus:border-emerald-500" />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-slate-400 text-xs mb-2 font-body">Qué mostrar en cada etiqueta</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    ["mostrar_empresa", "Nombre empresa"],
                    ["mostrar_logo", "Logo"],
                    ["mostrar_producto", "Nombre producto"],
                    ["mostrar_sku", "SKU"],
                    ["mostrar_precio", "Precio"],
                  ].map(([campo, label]) => (
                    <label key={campo} className={`flex items-center gap-2 p-2 rounded-lg border text-xs font-body cursor-pointer ${disenoForm[campo] ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/3 text-slate-400"}`}>
                      <input type="checkbox" checked={!!disenoForm[campo]}
                        onChange={e => setDisenoForm(f => ({ ...f, [campo]: e.target.checked }))}
                        className="accent-emerald-500" />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="text-slate-600 text-[10px] mt-1.5 font-body">El código de barras siempre se imprime — es la etiqueta en sí.</p>
              </div>

              <div>
                <label className="block text-slate-400 text-xs mb-2 font-body">Vista previa (a escala, con datos de ejemplo)</label>
                <EtiquetaPreview diseno={disenoForm} empresaNombre={activeEmpresaPropia?.nombre_fantasia || activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "Mi Empresa"} />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowDisenoForm(false)}
                  className="px-4 py-2 rounded-lg text-slate-300 text-sm font-body hover:bg-white/5 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={savingDiseno}
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-body text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" /> {savingDiseno ? "Guardando..." : "Guardar diseño"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Formulario Subcategoría ───────────────────── */}
      {showSubForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-base text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-violet-400" />
                {editingSubId ? "Editar subcategoría" : "Nueva subcategoría"}
              </h2>
              <button onClick={() => setShowSubForm(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveSub} className="p-5 space-y-4">
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Categoría principal *</label>
                <select required value={subForm.categoria}
                  onChange={e => setSubForm(f => ({ ...f, categoria: e.target.value }))}
                  style={{ colorScheme: "dark" }}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-violet-500">
                  <option value="" className="bg-arandu-dark">— Seleccioná —</option>
                  {(categoriasSelect.length > 0 ? categoriasSelect : []).filter(c => !esCatServicio(c.nombre)).map(c => (
                    <option key={c.id || c.nombre} value={c.nombre} className="bg-arandu-dark">{c.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Nombre *</label>
                <input required value={subForm.nombre}
                  onChange={e => setSubForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Laptops, Impresoras, Tintas..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-violet-500" />
              </div>
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">
                  Prefijo SKU <span className="text-slate-600">(opcional — para generación automática)</span>
                </label>
                <input value={subForm.prefijo}
                  onChange={e => setSubForm(f => ({ ...f, prefijo: e.target.value.toUpperCase() }))}
                  placeholder="Ej: LAP, IMP, TIN"
                  maxLength={8}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body font-mono focus:outline-none focus:border-violet-500" />
                {subForm.prefijo && (
                  <p className="text-slate-500 text-[10px] mt-1 font-body">
                    SKU de ejemplo: <code className="font-mono text-violet-300">{subForm.prefijo}{configSku.sep}{String(1).padStart(parseInt(configSku.longitud_seq) || 3, "0")}</code>
                  </p>
                )}
              </div>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                subForm.activo !== false ? "border-violet-500/35 bg-violet-500/10" : "border-white/10 bg-white/3"
              }`}>
                <input
                  type="checkbox"
                  checked={subForm.activo !== false}
                  onChange={e => setSubForm(f => ({ ...f, activo: e.target.checked }))}
                  className="mt-0.5 accent-violet-500"
                />
                <div>
                  <p className="text-white text-xs font-medium font-body">Subcategoría activa</p>
                  <p className="text-slate-500 text-[10px] font-body mt-0.5">Si se desactiva, deja de aparecer para seleccionar en productos y reportes.</p>
                </div>
              </label>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={savingSub}
                  className="flex items-center gap-2 px-5 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-body text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" /> {savingSub ? "Guardando..." : "Guardar"}
                </button>
                <button type="button" onClick={() => setShowSubForm(false)}
                  className="px-5 py-2 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors font-body text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Formulario Atributo ───────────────────────── */}
      {showAttrForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-base text-white flex items-center gap-2">
                <Tag className="w-4 h-4 text-blue-400" />
                {editingAttrId ? "Editar atributo" : "Nuevo atributo"}
              </h2>
              <button onClick={() => setShowAttrForm(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveAttr} className="p-5 space-y-4">
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Nombre *</label>
                <input required value={attrForm.nombre}
                  onChange={e => setAttrForm(f => ({ ...f, nombre: e.target.value }))}
                  disabled={!!atributosProductos.find(a => a.id === editingAttrId)?.sistema}
                  placeholder="Ej: Marca, Talle, Color"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-blue-500 disabled:opacity-60" />
              </div>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                attrForm.activo !== false ? "border-blue-500/35 bg-blue-500/10" : "border-white/10 bg-white/3"
              }`}>
                <input
                  type="checkbox"
                  checked={attrForm.activo !== false}
                  onChange={e => setAttrForm(f => ({ ...f, activo: e.target.checked }))}
                  className="mt-0.5 accent-blue-500"
                />
                <div>
                  <p className="text-white text-xs font-medium font-body">Atributo activo</p>
                  <p className="text-slate-500 text-[10px] font-body mt-0.5">Si se desactiva, deja de aparecer en productos, variantes y reportes.</p>
                </div>
              </label>
              <div className="border border-white/10 rounded-xl p-4 bg-white/3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-slate-200 text-xs font-semibold font-body">Opciones del atributo *</p>
                    <p className="text-slate-500 text-[10px] font-body mt-0.5">Agregá valores controlados para evitar texto libre.</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20">
                    {(attrForm.opciones || []).filter(o => (o.nombre || "").trim()).length}
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    value={attrForm.nuevaOpcion}
                    onChange={e => setAttrForm(f => ({ ...f, nuevaOpcion: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addAttrOption();
                      }
                    }}
                    placeholder="Ej: Nike"
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={addAttrOption}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-body text-sm">
                    <Plus className="w-4 h-4" /> Agregar
                  </button>
                </div>
                {(attrForm.opciones || []).length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-white/10 rounded-lg text-slate-500 text-xs font-body">
                    Todavía no agregaste opciones
                  </div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {(attrForm.opciones || []).map((opcion, idx) => (
                      <div key={opcion.id || idx} className="flex items-center gap-2 p-2 rounded-lg border border-white/10 bg-arandu-dark/40">
                        <span className="text-slate-500 text-xs font-mono w-6">{idx + 1}</span>
                        <input
                          value={opcion.nombre}
                          onChange={e => updateAttrOption(idx, e.target.value)}
                          className="flex-1 bg-transparent text-slate-100 text-sm font-body focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttrOption(idx)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Eliminar opción">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={savingAttr}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-body text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" /> {savingAttr ? "Guardando..." : "Guardar"}
                </button>
                <button type="button" onClick={() => setShowAttrForm(false)}
                  className="px-5 py-2 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors font-body text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Formulario Categoría ──────────────────────── */}
      {showCatForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-base text-white flex items-center gap-2">
                <Tag className="w-4 h-4 text-cyan-400" />
                {editingCatId ? "Editar categoría" : "Nueva categoría"}
              </h2>
              <button onClick={() => setShowCatForm(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveCat} className="p-5 space-y-4">
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Nombre *</label>
                <input required value={catForm.nombre}
                  onChange={e => setCatForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Electrónica, Alimentos, Repuestos..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
              </div>

              <div className="border border-cyan-500/20 rounded-xl p-4 space-y-3 bg-cyan-500/5">
                <p className="text-cyan-400 text-xs font-body font-semibold flex items-center gap-1.5">
                  <FlaskConical className="w-3.5 h-3.5" /> Campos de trazabilidad
                </p>
                <p className="text-slate-500 text-[11px] font-body">
                  Los campos habilitados aparecerán en el formulario del producto y en las facturas.
                </p>
                <div className="space-y-2">
                  {[
                    { key: "tiene_codigo_fabricante", icon: Factory, label: "Código de Fabricante", desc: "Para identificar el código asignado por el fabricante" },
                    { key: "tiene_lote", icon: FlaskConical, label: "Lote", desc: "Para trazabilidad por número de lote" },
                    { key: "tiene_vencimiento", icon: CalendarClock, label: "Fecha de Vencimiento", desc: "Para productos con fecha de caducidad" },
                  ].map(({ key, icon: Icon, label, desc }) => (
                    <label key={key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      catForm[key]
                        ? "border-cyan-500/40 bg-cyan-500/10"
                        : "border-white/10 bg-white/3 hover:bg-white/5"
                    }`}>
                      <input
                        type="checkbox"
                        checked={catForm[key]}
                        onChange={e => setCatForm(f => ({ ...f, [key]: e.target.checked }))}
                        className="mt-0.5 accent-cyan-500"
                      />
                      <div className="flex-1">
                        <p className="text-white text-xs font-medium font-body flex items-center gap-1.5">
                          <Icon className="w-3 h-3 text-cyan-400" /> {label}
                        </p>
                        <p className="text-slate-500 text-[10px] font-body mt-0.5">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border border-blue-500/20 rounded-xl p-4 space-y-3 bg-blue-500/5">
                <p className="text-blue-300 text-xs font-body font-semibold flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> Atributos habilitados
                </p>
                <p className="text-slate-500 text-[11px] font-body">
                  Estos atributos aparecerán como selectores controlados en los productos de esta categoría.
                </p>
                {atributosProductos.length === 0 ? (
                  <p className="text-slate-500 text-xs font-body">No hay atributos configurados todavía.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {atributosProductos.map(attr => {
                      const checked = (catForm.atributos_habilitados || []).includes(attr.id);
                      return (
                        <label key={attr.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          checked ? "border-blue-500/40 bg-blue-500/10" : "border-white/10 bg-white/3 hover:bg-white/5"
                        }`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => setCatForm(f => ({
                              ...f,
                              atributos_habilitados: e.target.checked
                                ? [...(f.atributos_habilitados || []), attr.id]
                                : (f.atributos_habilitados || []).filter(id => id !== attr.id),
                            }))}
                            className="accent-blue-500"
                          />
                          <span className="text-slate-200 text-xs font-body">
                            {attr.nombre}
                            {attr.activo === false && <span className="ml-1 text-slate-500">(inactivo)</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={savingCat}
                  className="flex items-center gap-2 px-5 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" /> {savingCat ? "Guardando..." : "Guardar"}
                </button>
                <button type="button" onClick={() => setShowCatForm(false)}
                  className="px-5 py-2 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors font-body text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Historial de costos por producto ───────────── */}
      {histModalProducto && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
              <div>
                <h2 className="font-heading text-base text-white">Historial de Costos</h2>
                <p className="text-slate-400 text-xs font-body mt-0.5">{histModalProducto.nombre}</p>
              </div>
              <button onClick={() => setHistModalProducto(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5">
              {loadingHistModal ? (
                <p className="text-center text-slate-500 py-10 font-body text-sm">Cargando...</p>
              ) : histModalData.length === 0 ? (
                <p className="text-center text-slate-500 py-10 font-body text-sm">Sin cambios de costo registrados para este producto.</p>
              ) : (
                <table className="w-full text-sm font-body">
                  <thead>
                    <tr className="border-b border-white/10 text-left">
                      <th className="pb-2 pr-4 text-slate-400 text-xs">Fecha</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs text-right">Costo anterior</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs text-right">Costo nuevo</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs text-right">Diferencia</th>
                      <th className="pb-2 pr-4 text-slate-400 text-xs">Motivo</th>
                      <th className="pb-2 text-slate-400 text-xs">Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {histModalData.map(h => {
                      const diff = h.diferencia ?? (h.costo_nuevo - h.costo_anterior);
                      const diffColor = diff > 0 ? "text-red-400" : diff < 0 ? "text-emerald-400" : "text-slate-400";
                      return (
                        <tr key={h.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-2.5 pr-4 text-slate-400 whitespace-nowrap">{h.fecha}</td>
                          <td className="py-2.5 pr-4 text-slate-300 text-right whitespace-nowrap">₲ {Math.round(h.costo_anterior).toLocaleString("es-PY")}</td>
                          <td className="py-2.5 pr-4 text-white text-right whitespace-nowrap">₲ {Math.round(h.costo_nuevo).toLocaleString("es-PY")}</td>
                          <td className={`py-2.5 pr-4 text-right whitespace-nowrap ${diffColor}`}>
                            {diff > 0 ? "+" : ""}₲ {Math.round(diff).toLocaleString("es-PY")}
                          </td>
                          <td className="py-2.5 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${h.motivo === "compra" ? "bg-cyan-500/15 text-cyan-300" : h.motivo === "costo_inicial" ? "bg-slate-500/20 text-slate-300" : "bg-amber-500/15 text-amber-300"}`}>
                              {h.motivo === "compra" ? "Compra" : h.motivo === "costo_inicial" ? "Costo inicial" : "Ajuste manual"}
                            </span>
                            {h.variante_texto && <span className="ml-1 text-[10px] text-slate-400">{h.variante_texto}</span>}
                          </td>
                          <td className="py-2.5 text-slate-400 text-xs">{h.usuario_nombre || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Formulario ─────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-[min(96vw,1180px)] max-w-none max-h-[92vh] overflow-y-auto overflow-x-hidden shadow-2xl">
            <div className="sticky top-0 z-10 bg-arandu-dark-light/95 backdrop-blur flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-base text-white">{editingId ? "Editar producto" : "Nuevo producto"}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 sm:p-6 space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Nombre *</label>
                <input required value={formData.nombre}
                  onChange={e => setFormData(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
              </div>

              {/* Imagen del producto + Proveedor (misma fila) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Imagen del producto</label>
                  <div className="flex items-center gap-3">
                    <div className="w-20 h-20 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                      {formData.imagen_url ? (
                        <img src={formData.imagen_url} alt="Producto" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-slate-600" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <input ref={imagenInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagenProducto} />
                      <button type="button" onClick={() => imagenInputRef.current?.click()} disabled={procesandoImagen}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-body border border-white/10 disabled:opacity-50">
                        <Upload className="w-3.5 h-3.5" /> {procesandoImagen ? "Procesando..." : formData.imagen_url ? "Cambiar imagen" : "Subir imagen"}
                      </button>
                      {formData.imagen_url && (
                        <button type="button" onClick={() => setFormData(f => ({ ...f, imagen_url: "" }))}
                          className="text-red-400 hover:underline text-[11px] font-body text-left">
                          Quitar imagen
                        </button>
                      )}
                      <p className="text-slate-600 text-[10px] font-body">JPG o PNG. Se redimensiona automáticamente.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Proveedor</label>
                  <select value={formData.proveedor_id || ""} onChange={e => setFormData(f => ({ ...f, proveedor_id: e.target.value }))}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500">
                    <option value="">— Sin proveedor —</option>
                    {proveedoresList.map(pv => <option key={pv.id} value={pv.id}>{pv.nombre}</option>)}
                  </select>
                  {proveedoresList.length === 0 && (
                    <p className="text-amber-400/80 text-[11px] mt-1">No hay proveedores en el Catastro de esta empresa (o falta el permiso Proveedores · ver). Registralos en Catastro con el rol Proveedor.</p>
                  )}
                </div>
              </div>

              {/* Recetario agronómico: requiere receta + ficha (solo con el módulo activo) */}
              {hasModule?.("recetario") && hasPermission("inventario_productos.marcar_receta") && (
                <div className="border border-lime-500/20 bg-lime-500/5 rounded-xl p-3 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={!!formData.requiere_receta} onChange={e => setFormData(f => ({ ...f, requiere_receta: e.target.checked }))} className="accent-lime-500" />
                    <span className="text-white text-sm font-body">Requiere receta agronómica</span>
                    <span className="text-slate-500 text-xs">(al venderlo se exige emitir la receta)</span>
                  </label>
                  {formData.requiere_receta && (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                      {[["ficha_ingrediente", "Ingrediente activo", "text"], ["ficha_clase_tox", "Clase toxicológica", "text"], ["ficha_carencia", "Carencia (días)", "number"], ["ficha_dosis", "Dosis recomendada / ha", "number"], ["ficha_unidad_dosis", "Unidad de dosis", "text"]].map(([k, l, t]) => (
                        <div key={k}>
                          <label className="block text-slate-400 text-xs mb-1 font-body">{l}</label>
                          <input type={t} min={t === "number" ? "0" : undefined} step={t === "number" ? "any" : undefined} value={formData[k]} onChange={e => setFormData(f => ({ ...f, [k]: e.target.value }))}
                            placeholder={k === "ficha_unidad_dosis" ? "L/ha" : ""} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-lime-500/60" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* SKU + código de barras */}
              <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-3">
                <div className="min-w-0">
                  {(() => {
                    const skuEsAuto = !editingId && configSku.modo_codigo === "automatico";
                    return (
                      <>
                        <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                          SKU {skuEsAuto ? (
                            <span className="text-emerald-500 text-[10px] flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" /> auto</span>
                          ) : <span className="text-red-400">*</span>}
                        </label>
                        <input
                          value={skuEsAuto ? "" : formData.sku}
                          onChange={skuEsAuto ? undefined : e => setFormData(f => ({ ...f, sku: e.target.value }))}
                          readOnly={skuEsAuto}
                          placeholder={skuEsAuto ? "Se generará automáticamente" : "Código único"}
                          className={`w-full border rounded-lg px-3 py-2 text-sm font-body focus:outline-none ${
                            skuEsAuto
                              ? "bg-emerald-500/5 border-emerald-500/20 text-slate-500 cursor-not-allowed"
                              : "bg-white/5 border-white/10 text-white focus:border-cyan-500"
                          }`}
                        />
                      </>
                    );
                  })()}
                </div>
                <div className="min-w-0">
                  <label className="block text-slate-400 text-xs mb-1 font-body">Código de barras</label>
                  <p className="text-emerald-400 text-[10px] font-body mb-1">
                    Recomendado: {BARCODE_TYPES[barcodeConfig.barcode_recomendado]} · {barcodeConfig.barcode_recomendado_mensaje}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                    <input
                      ref={barcodeInputRef}
                      value={formData.codigo_barras}
                      onChange={e => handleBarcodeInput(e.target.value)}
                      onKeyDown={handleBarcodeKeyDown}
                      autoComplete="off"
                      inputMode="text"
                      placeholder="Opcional — escanear o escribir"
                      className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500"
                    />
                    {canGenerarBarcode && (
                      <button type="button"
                        onClick={() => setFormData(f => ({ ...f, codigo_barras: generateCode() }))}
                        className="flex items-center gap-1 px-3 py-2 bg-violet-600/20 text-violet-300 border border-violet-600/30 rounded-lg hover:bg-violet-600/30 transition-colors text-xs font-body whitespace-nowrap">
                        <ScanBarcode className="w-3.5 h-3.5" /> Generar
                      </button>
                    )}
                  </div>
                  {formData.codigo_barras && (
                    <div className="mt-2 p-2 bg-white rounded-lg text-center overflow-hidden">
                      {isValidEan13(formData.codigo_barras) ? (
                        <div dangerouslySetInnerHTML={{ __html: ean13Svg(formData.codigo_barras, { height: 46, module: 1, quietMm: barcodeConfig.barcode_quiet_zone_mm }) }} />
                      ) : (
                        <span style={{ fontFamily: "'Libre Barcode 128 Text', monospace", fontSize: "36px", lineHeight: 1, color: "#000", display: "block" }}>
                          {formData.codigo_barras}
                        </span>
                      )}
                      <p className="text-gray-600 text-[9px] font-mono mt-0.5">{formData.codigo_barras}</p>
                      <p className={`text-[9px] ${isValidEan13(formData.codigo_barras) ? "text-emerald-700" : "text-amber-700"}`}>
                        {isValidEan13(formData.codigo_barras) ? "EAN-13 válido" : "Manual / Code 128"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Categoría */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Categoría <span className="text-red-400">*</span></label>
                  <select value={formData.categoria} onChange={e => handleCategoriaChange(e.target.value)}
                    style={{ colorScheme: 'dark' }}
                    className={`w-full bg-arandu-dark border rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500 ${!formData.categoria ? "border-red-500/50" : "border-white/10"}`}>
                    <option value="" className="bg-arandu-dark text-slate-200">— Seleccioná una categoría —</option>
                    {(categoriasSelect.length > 0 ? categoriasSelect : []).map(c => (
                      <option key={c.id || c.nombre} value={c.nombre}
                        disabled={esCatServicio(c.nombre) && !puedeCrearServicio}
                        className="bg-arandu-dark text-slate-200">
                        {c.nombre}{esCatServicio(c.nombre) && !puedeCrearServicio ? " (sin permiso)" : ""}
                      </option>
                    ))}
                  </select>
                  {esServicio && !puedeCrearServicio && (
                    <p className="text-red-400 text-[10px] mt-1">No tenés permiso para crear servicios</p>
                  )}
                </div>
              </div>

              {/* Subcategoría — opcional, aparece si hay subcategorías para la categoría elegida */}
              {usaSubcategorias && (() => {
                const subsCategoria = subcategoriasActivas.filter(s => s.categoria === formData.categoria);
                if (subsCategoria.length === 0 && !formData.subcategoria_id) return null;
                return (
                  <div>
                    <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                      <Layers className="w-3 h-3" /> Subcategoría <span className="text-slate-600">(opcional)</span>
                    </label>
                    <select
                      value={formData.subcategoria_id || ""}
                      onChange={e => {
                        const newSubId = e.target.value || null;
                        setFormData(f => ({ ...f, subcategoria_id: newSubId }));
                        if (configSku.modo_codigo === "automatico" && newSubId && !editingId) {
                          loadSkuPreview(newSubId);
                        } else {
                          setSkuPreview(null);
                        }
                      }}
                      style={{ colorScheme: 'dark' }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500"
                    >
                      <option value="" className="bg-arandu-dark text-slate-400">— Sin subcategoría —</option>
                      {subsCategoria.map(s => (
                        <option key={s.id} value={s.id} className="bg-arandu-dark text-slate-200">
                          {s.nombre}{s.prefijo ? ` [${s.prefijo}]` : ""}
                        </option>
                      ))}
                    </select>
                    {configSku.modo_codigo === "automatico" && formData.subcategoria_id && !editingId && (
                      <div className="mt-1.5 flex items-center gap-2">
                        {loadingSkuPreview ? (
                          <span className="text-xs text-slate-500 font-body">Calculando SKU...</span>
                        ) : skuPreview ? (
                          <span className="flex items-center gap-1.5 text-xs font-body text-emerald-400">
                            <Zap className="w-3 h-3" />
                            Próximo SKU auto: <code className="font-mono bg-emerald-500/10 px-1.5 py-0.5 rounded">{skuPreview.sku}</code>
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Variantes de stock — se define antes de las características fijas */}
              {usaAtributos && !esServicio && atributosCategoria.length > 0 && (
                <div className="border border-emerald-500/20 rounded-xl p-4 space-y-3 bg-emerald-500/5">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.variantes_habilitadas ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/3"
                  }`}>
                    <input
                      type="checkbox"
                      checked={!!formData.variantes_habilitadas}
                      onChange={e => setFormData(f => ({
                        ...f,
                        variantes_habilitadas: e.target.checked,
                        variante_atributos: e.target.checked ? (f.variante_atributos || []) : [],
                        variantes: e.target.checked ? (f.variantes || []) : [],
                      }))}
                      className="mt-0.5 accent-emerald-500"
                    />
                    <div>
                      <p className="text-emerald-300 text-xs font-body font-semibold">Manejar stock por variantes</p>
                      <p className="text-slate-500 text-[11px] font-body mt-0.5">
                        Primero elegí si el producto divide stock por talle, color, marca u otra combinación.
                      </p>
                    </div>
                  </label>

                  {formData.variantes_habilitadas && (
                    <div className="space-y-3">
                      <div>
                        <p className="text-slate-400 text-xs font-body mb-2">Atributos que separan stock</p>
                        <div className="flex flex-wrap gap-2">
                          {atributosCategoria.map(attr => {
                            const checked = (formData.variante_atributos || []).includes(attr.id);
                            return (
                              <label key={attr.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-body ${
                                checked ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/3 text-slate-300"
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={e => toggleVarianteAtributo(attr.id, e.target.checked)}
                                  className="accent-emerald-500"
                                />
                                {attr.nombre}
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-slate-500 text-[10px] font-body mt-1.5">
                          Marcá solo los atributos que realmente separan stock o precio para este producto.
                        </p>
                      </div>

                      {(formData.variante_atributos || []).length > 0 && (
                        <div className="space-y-3">
                          {(formData.variante_atributos || []).map(attrId => {
                            const attr = atributosCategoria.find(a => a.id === attrId);
                            if (!attr) return null;
                            const selectedOptions = formData.variante_opciones?.[attrId] || [];
                            const optionFilter = variantOptionFilters[attrId] || "";
                            const opcionesActivas = (attr.opciones || []).filter(o => o.activo !== false);
                            const opcionesFiltradas = opcionesActivas.filter(o => {
                              const q = optionFilter.trim().toLowerCase();
                              return !q || (o.nombre || "").toLowerCase().includes(q);
                            });
                            return (
                              <div key={`opts-${attrId}`} className="border border-white/10 rounded-xl p-3 bg-white/3">
                                <div className="flex items-center justify-between gap-3 mb-2">
                                  <p className="text-slate-300 text-xs font-body">
                                    Opciones de <span className="text-emerald-300 font-semibold">{attr.nombre}</span> para este producto
                                  </p>
                                  <div className="flex items-center gap-2">
                                    {selectedOptions.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setFormData(f => {
                                            const varianteOpciones = { ...(f.variante_opciones || {}) };
                                            varianteOpciones[attrId] = [];
                                            return {
                                              ...f,
                                              variante_opciones: varianteOpciones,
                                              variantes: buildVariantRows(atributosCategoria, f.variante_atributos || [], f.variantes || [], {
                                                moneda: f.moneda,
          precio_costo: f.precio_costo,
                                                precio_venta: f.precio_venta,
                                                var_costo: f.var_costo_general,
                                                var_ganancia: f.var_ganancia_general,
                                              }, varianteOpciones),
                                            };
                                          });
                                        }}
                                        className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10 hover:text-white hover:bg-white/10"
                                      >
                                        Limpiar
                                      </button>
                                    )}
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                      {selectedOptions.length} seleccionada{selectedOptions.length !== 1 ? "s" : ""}
                                    </span>
                                  </div>
                                </div>
                                <div className="relative mb-2">
                                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                                  <input
                                    type="text"
                                    value={optionFilter}
                                    onChange={e => setVariantOptionFilters(filters => ({ ...(filters || {}), [attrId]: e.target.value }))}
                                    placeholder={`Buscar ${attr.nombre.toLowerCase()}...`}
                                    className="w-full bg-arandu-dark/60 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white font-body focus:outline-none focus:border-emerald-500 placeholder-slate-600"
                                  />
                                </div>
                                <div className="max-h-44 overflow-y-auto pr-1">
                                  <div className="flex flex-wrap gap-2">
                                  {opcionesFiltradas.map(opt => {
                                    const checkedOpt = selectedOptions.includes(opt.id);
                                    return (
                                      <label key={opt.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-body transition-colors ${
                                        checkedOpt ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-arandu-dark/40 text-slate-300 hover:bg-white/5"
                                      }`}>
                                        <input
                                          type="checkbox"
                                          checked={checkedOpt}
                                          onChange={e => toggleVarianteOpcion(attr.id, opt.id, e.target.checked)}
                                          className="accent-emerald-500"
                                        />
                                        {opt.nombre}
                                      </label>
                                    );
                                  })}
                                  </div>
                                </div>
                                {opcionesFiltradas.length === 0 && (
                                  <p className="text-slate-500 text-[10px] font-body mt-2">
                                    No hay opciones que coincidan con "{optionFilter}".
                                  </p>
                                )}
                                {selectedOptions.length === 0 && (
                                  <p className="text-amber-300 text-[10px] font-body mt-2">
                                    Elegí al menos una opción para generar variantes.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {(formData.variante_atributos || []).length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <p className="text-slate-400 text-xs font-body">Variantes generadas</p>
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1.5">
                                {["PYG", "USD", ...(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? ["BRL"] : [])].map(m => (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => setFormData(f => ({ ...f, moneda: m }))}
                                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-all ${
                                      (formData.moneda || "PYG") === m
                                        ? "border-cyan-500 bg-cyan-500/20 text-cyan-300"
                                        : "border-white/10 text-slate-400 hover:border-white/30"
                                    }`}
                                  >
                                    {MONEDA_SIMBOLO[m] || m}
                                  </button>
                                ))}
                              </div>
                              <button type="button" onClick={regenerateVariantes}
                                className="text-xs px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/20">
                                Regenerar
                              </button>
                            </div>
                          </div>
                          <p className="text-slate-500 text-[10px] font-body">Los precios de costo y venta de todas las variantes se cargan en la misma moneda elegida arriba.</p>
                          {(
                            <div className="flex items-end gap-3 flex-wrap border border-emerald-500/20 bg-emerald-500/5 rounded-xl p-3">
                              <div>
                                <label className="block text-slate-400 text-[10px] mb-1">Stock para todas</label>
                                <input type="number" min="0" step="any" value={formData.var_stock_general ?? ""}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setFormData(f => ({
                                      ...f, var_stock_general: val,
                                      variantes: val === "" ? f.variantes : (f.variantes || []).map(v => ({ ...v, stock_actual: val })),
                                    }));
                                  }}
                                  className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right text-xs" />
                              </div>
                              {puedeVerCostoCompra && (<>
                              <div>
                                <label className="block text-slate-400 text-[10px] mb-1">Costo para todas ({MONEDA_SIMBOLO[formData.moneda || "PYG"]})</label>
                                <input type="text" inputMode={(formData.moneda || "PYG") === "PYG" ? "numeric" : "decimal"}
                                  value={formatMoneyInput(formData.var_costo_general ?? "", formData.moneda)}
                                  onChange={e => setVarGeneral("costo", normalizeMoneyValue(e.target.value, formData.moneda))}
                                  {...costoBloqueadoProps}
                                  className="w-32 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right text-xs" />
                              </div>
                              <div>
                                <label className="block text-slate-400 text-[10px] mb-1">% Ganancia para todas</label>
                                <input type="number" min="0" step="any" value={formData.var_ganancia_general ?? ""}
                                  onChange={e => setVarGeneral("ganancia", e.target.value)}
                                  className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right text-xs" />
                              </div>
                              </>)}
                              <p className="text-slate-500 text-[10px] flex-1 min-w-[200px]">
                                Se aplica a todas las variantes. Si cambiás el costo o % de una variante a un valor distinto, queda marcada (<span className="text-amber-300">●</span>) y ya no sigue al general.
                              </p>
                            </div>
                          )}
                          <div className="overflow-x-auto border border-white/10 rounded-xl">
                            <table className="w-full text-xs font-body">
                              <thead className="bg-white/5 text-slate-400">
                                <tr>
                                  <th className="text-left px-3 py-2">Variante</th>
                                  <th className="text-right px-3 py-2">Stock</th>
                                  <th className="text-right px-3 py-2">Mín.</th>
                                  {puedeVerCostoCompra && <th className="text-right px-3 py-2">Costo ({MONEDA_SIMBOLO[formData.moneda || "PYG"]})</th>}
                                  {puedeVerCostoCompra && <th className="text-right px-3 py-2">% Gan.</th>}
                                  <th className="text-right px-3 py-2">Precio ({MONEDA_SIMBOLO[formData.moneda || "PYG"]})</th>
                                  {puedeVerCostoCompra && <th className="text-right px-3 py-2">Margen</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {(formData.variantes || []).map((v, idx) => {
                                  const margenVar = calcGananciaPct(v.precio_costo, v.precio_venta);
                                  return (
                                    <tr key={`${idx}-${v.atributos_texto}`} className="border-t border-white/5">
                                      <td className="px-3 py-2 text-slate-200 min-w-[180px]">
                                        {v.atributos_texto}
                                        {(v.costo_manual || v.ganancia_manual) && (
                                          <button type="button" title="Personalizada · clic para volver al general"
                                            onClick={() => restablecerVarianteGeneral(idx)}
                                            className="ml-2 text-amber-300 hover:text-amber-200">●</button>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">
                                        <input type="number" min="0" step="any" value={v.stock_actual ?? "0"}
                                          onChange={e => updateVarianteRow(idx, { stock_actual: e.target.value })}
                                          className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right" />
                                      </td>
                                      <td className="px-3 py-2">
                                        <input type="number" min="0" step="any" value={v.stock_minimo ?? "0"}
                                          onChange={e => updateVarianteRow(idx, { stock_minimo: e.target.value })}
                                          className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right" />
                                      </td>
                                      {puedeVerCostoCompra && (
                                        <td className="px-3 py-2">
                                          <input type="text" inputMode={(formData.moneda || "PYG") === "PYG" ? "numeric" : "decimal"} value={formatMoneyInput(v.precio_costo ?? "", formData.moneda)}
                                            onChange={e => updateVarianteCosto(idx, normalizeMoneyValue(e.target.value, formData.moneda))}
                                            {...costoBloqueadoProps}
                                            className="w-28 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right" />
                                        </td>
                                      )}
                                      {puedeVerCostoCompra && (
                                        <td className="px-3 py-2">
                                          <input type="number" min="0" step="any" value={v.ganancia_pct ?? ""}
                                            onChange={e => updateVarianteGanancia(idx, e.target.value)}
                                            className="w-20 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right" />
                                        </td>
                                      )}
                                      <td className="px-3 py-2">
                                        <input type="text" inputMode={(formData.moneda || "PYG") === "PYG" ? "numeric" : "decimal"} value={formatMoneyInput(v.precio_venta ?? "", formData.moneda)}
                                          onChange={e => updateVarianteVenta(idx, normalizeMoneyValue(e.target.value, formData.moneda))}
                                          className="w-28 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-right" />
                                      </td>
                                      {puedeVerCostoCompra && (
                                        <td className="px-3 py-2 text-right">
                                          {margenVar !== "" ? (
                                            <span className={`text-xs font-semibold ${Number(margenVar) >= 20 ? "text-emerald-300" : Number(margenVar) >= 0 ? "text-amber-300" : "text-red-300"}`}>
                                              {margenVar}%
                                            </span>
                                          ) : <span className="text-slate-600">-</span>}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Campos de trazabilidad — condicionales según la categoría */}
              {(() => {
                const catObj = categorias.find(c => c.nombre === formData.categoria);
                if (!catObj) return null;
                const hasCF = catObj.tiene_codigo_fabricante;
                const hasLote = catObj.tiene_lote;
                const hasVenc = catObj.tiene_vencimiento;
                if (!hasCF && !hasLote && !hasVenc) return null;
                return (
                  <div className="border border-cyan-500/20 rounded-xl p-3 space-y-3 bg-cyan-500/5">
                    <p className="text-cyan-400 text-xs font-body font-semibold flex items-center gap-1.5">
                      <FlaskConical className="w-3.5 h-3.5" /> Campos de trazabilidad
                    </p>
                    <div className={`grid gap-3 ${hasCF && (hasLote || hasVenc) ? "grid-cols-2" : "grid-cols-1"}`}>
                      {hasCF && (
                        <div>
                          <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                            <Factory className="w-3 h-3" /> Código de Fabricante
                          </label>
                          <input type="text"
                            value={formData.codigo_fabricante}
                            onChange={e => setFormData(f => ({ ...f, codigo_fabricante: e.target.value }))}
                            placeholder="Ej: MFR-2024-001"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                        </div>
                      )}
                      {hasLote && (
                        <div>
                          <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                            <FlaskConical className="w-3 h-3" /> Lote
                          </label>
                          <input type="text"
                            value={formData.lote}
                            onChange={e => setFormData(f => ({ ...f, lote: e.target.value }))}
                            placeholder="Número de lote"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                        </div>
                      )}
                      {hasVenc && (
                        <div>
                          <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                            <CalendarClock className="w-3 h-3" /> Fecha de Vencimiento
                          </label>
                          <DateInput
                            value={formData.fecha_vencimiento}
                            onChange={e => setFormData(f => ({ ...f, fecha_vencimiento: e.target.value }))}
                            style={{ colorScheme: "dark" }}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Alerta Servicios */}
              {esServicio && (
                <div className="bg-violet-500/10 border border-violet-500/25 rounded-lg px-3 py-2 text-violet-300 text-xs font-body">
                  Los servicios no afectan stock y no llevan stock mínimo.
                </div>
              )}

              {/* IVA */}
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Tipo de IVA
                </label>
                <div className="flex gap-2">
                  {IVA_OPCIONES.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setFormData(f => ({ ...f, iva_tipo: opt.value }))}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-body border transition-colors ${
                        formData.iva_tipo === opt.value
                          ? "bg-cyan-600/30 border-cyan-500 text-cyan-200"
                          : "bg-white/5 border-white/10 text-slate-400 hover:border-white/25"
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Descuento máximo */}
              {puedeGestionarDescuentos && (
              <div className="border border-amber-500/20 rounded-xl p-4 space-y-3 bg-amber-500/5">
                <p className="text-amber-400 text-xs font-body font-semibold flex items-center gap-1.5">
                  <span>%</span> Descuento permitido
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1 font-body">Descuento máximo (%)</label>
                    <input type="number" min="0" max="100" step="0.01"
                      value={formData.descuento_maximo}
                      onChange={e => setFormData(f => ({ ...f, descuento_maximo: e.target.value }))}
                      placeholder="0 = sin descuento"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1 font-body">Vence el (opcional)</label>
                    <DateInput
                      value={formData.descuento_vence}
                      onChange={e => setFormData(f => ({ ...f, descuento_vence: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-amber-500" />
                  </div>
                </div>
                {parseFloat(formData.descuento_maximo) > 0 && (
                  <p className="text-xs text-amber-400/70 font-body">
                    {formData.descuento_vence
                      ? `Descuento vigente hasta el ${formData.descuento_vence}. Después se ignora automáticamente.`
                      : "Descuento indefinido (sin fecha de vencimiento)."}
                  </p>
                )}
              </div>
              )}

              {/* Precios */}
              {!formData.variantes_habilitadas && (
              <div className="border border-white/10 rounded-xl p-4 space-y-3 bg-white/3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-slate-400 text-xs font-body font-semibold">Precios</p>
                  <div className="flex gap-1.5">
                    {["PYG", "USD", ...(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? ["BRL"] : [])].map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setFormData(f => ({ ...f, moneda: m }))}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-all ${
                          (formData.moneda || "PYG") === m
                            ? "border-cyan-500 bg-cyan-500/20 text-cyan-300"
                            : "border-white/10 text-slate-400 hover:border-white/30"
                        }`}
                      >
                        {MONEDA_SIMBOLO[m] || m}
                      </button>
                    ))}
                  </div>
                </div>
                {puedeVerCostoCompra && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Precio costo ({MONEDA_SIMBOLO[formData.moneda || "PYG"]})</label>
                      <input type="text" inputMode={(formData.moneda || "PYG") === "PYG" ? "numeric" : "decimal"} value={formatMoneyInput(formData.precio_costo, formData.moneda)}
                        onChange={e => handleCostoChange(normalizeMoneyValue(e.target.value, formData.moneda))}
                        {...costoBloqueadoProps}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">% Ganancia</label>
                      <input type="number" min="0" step="0.01" value={formData.ganancia_pct}
                        onChange={e => handleGananciaChange(e.target.value)}
                        placeholder="Ej: 30"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                    </div>
                  </div>
                )}
                {puedeVerCostoCompra && precioSugerido !== null && (
                  <div className="flex items-center gap-2 text-xs font-body">
                    <span className="text-slate-500">Precio sugerido:</span>
                    <span className="text-emerald-300 font-semibold">{fmtMoneda(precioSugerido, formData.moneda)}</span>
                    <button type="button"
                      onClick={() => setFormData(f => ({ ...f, precio_venta: String(precioSugerido) }))}
                      className="ml-auto text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 rounded hover:bg-emerald-500/25 transition-colors">
                      Aplicar
                    </button>
                  </div>
                )}
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Precio venta ({MONEDA_SIMBOLO[formData.moneda || "PYG"]})</label>
                  <input type="text" inputMode={(formData.moneda || "PYG") === "PYG" ? "numeric" : "decimal"} value={formatMoneyInput(formData.precio_venta, formData.moneda)}
                    onChange={e => setFormData(f => ({ ...f, precio_venta: normalizeMoneyValue(e.target.value, formData.moneda) }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                </div>
              </div>
              )}

              {/* Unidad de medida */}
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body flex items-center gap-1">
                  <Ruler className="w-3 h-3" /> Unidad de medida
                </label>
                <select
                  value={formData.unidad_medida_id || ""}
                  onChange={e => setFormData(f => ({ ...f, unidad_medida_id: e.target.value || null }))}
                  style={{ colorScheme: 'dark' }}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500"
                >
                  <option value="" className="bg-arandu-dark text-slate-400">— Unidad (por defecto) —</option>
                  {unidadesLista.map(u => (
                    <option key={u.id} value={u.id} className="bg-arandu-dark text-slate-200">
                      [{u.sigla}] {u.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Stock — ocultar para Servicios */}
              {!esServicio && !formData.variantes_habilitadas && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1 font-body">Stock mínimo</label>
                    <input type="number" min="0" value={formData.stock_minimo}
                      onChange={e => setFormData(f => ({ ...f, stock_minimo: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                  </div>
                  {!editingId && puedeStockInicial && (
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Stock inicial</label>
                      <input type="number" min="0" value={formData.stock_actual}
                        onChange={e => setFormData(f => ({ ...f, stock_actual: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                    </div>
                  )}
                  {!editingId && puedeStockInicial && dep.activo && (
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Depósito</label>
                      <select value={formData.deposito_id || ""} onChange={e => setFormData(f => ({ ...f, deposito_id: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500">
                        <option value="">{dep.cfg.modo === "por_compra" ? "Predeterminado de compras" : "Depósito principal"}</option>
                        {dep.depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                      </select>
                    </div>
                  )}
                  {editingId && (
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Stock inicial cargado</label>
                      <input type="text" readOnly value={formData.stock_inicial_view || "0"}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm font-body cursor-not-allowed" />
                    </div>
                  )}
                </div>
              )}

              {/* Descripción */}
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Descripción</label>
                <textarea value={formData.descripcion}
                  onChange={e => setFormData(f => ({ ...f, descripcion: e.target.value }))}
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500 resize-none" />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-5 py-2 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors font-body text-sm">
                  Cancelar
                </button>
                {editingId && (
                  <label className="flex items-center gap-2 ml-auto cursor-pointer">
                    <span className="text-slate-400 text-xs font-body">Activo</span>
                    <input type="checkbox" checked={formData.activo}
                      onChange={e => setFormData(f => ({ ...f, activo: e.target.checked }))}
                      className="rounded" />
                  </label>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Movimientos ───────────────────────────────────── */}
      {showMovModal && selectedProducto && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <h2 className="font-heading text-base text-white flex items-center gap-2">
                  <Package className="w-4 h-4 text-cyan-400" />
                  {selectedProducto.nombre}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-sm font-semibold ${
                    selectedProducto.stock_actual === 0 ? "text-red-300"
                    : (selectedProducto.stock_minimo > 0 && selectedProducto.stock_actual <= selectedProducto.stock_minimo) ? "text-orange-300"
                    : "text-emerald-300"
                  }`}>
                    Stock actual: {selectedProducto.stock_actual} {selectedProducto.unidad}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {puedeAjustarStock && (
                  <button onClick={toggleMovForm}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-xs">
                    <Plus className="w-3.5 h-3.5" /> Ajuste manual
                  </button>
                )}
                <button onClick={() => setShowMovModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {dep.activo && (
                <StockDepositosTabla token={token} productoId={selectedProducto.id} stockMinimo={selectedProducto.stock_minimo}
                  refreshKey={depRefresh + (selectedProducto.stock_actual || 0)}
                  puedeTransferir={hasPermission("inventario_productos.transferir_depositos")}
                  puedeEditarUbicacion={hasPermission("inventario_productos.gestionar_depositos") || hasPermission("inventario_productos.editar")}
                  onTransferir={(filas) => setDepTransf({ inicial: { producto_id: selectedProducto.id, origen_id: (filas.find(f => f.cantidad > 0) || filas[0])?.deposito_id } })} />
              )}
              {/* Form ajuste manual */}
              {showMovForm && (
                <form onSubmit={handleSaveMov} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                  <p className="text-slate-300 text-xs font-body font-semibold">Ajuste de stock manual</p>
                  <div className={`grid gap-3 ${selectedProducto?.variantes_habilitadas ? "grid-cols-4" : "grid-cols-3"}`}>
                    {selectedProducto?.variantes_habilitadas && (
                      <div>
                        <label className="block text-slate-400 text-xs mb-1 font-body">Variante *</label>
                        <select value={movForm.variante_id || ""}
                          onChange={e => {
                            const variante = (selectedProducto.variantes || []).find(v => v.id === e.target.value);
                            setMovForm(f => ({
                              ...f,
                              variante_id: e.target.value,
                              precio_unitario: variante?.precio_costo ? String(variante.precio_costo) : f.precio_unitario,
                            }));
                          }}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none">
                          <option value="">Seleccionar...</option>
                          {(selectedProducto.variantes || []).filter(v => v.activo !== false).map(v => (
                            <option key={v.id} value={v.id}>{v.atributos_texto}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Tipo</label>
                      <select value={movForm.tipo} onChange={e => setMovForm(f => ({ ...f, tipo: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none">
                        <option value="entrada">↑ Entrada</option>
                        <option value="salida">↓ Salida</option>
                        <option value="ajuste">= Ajuste absoluto</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Cantidad *</label>
                      <input required type="number" min={movForm.tipo === "ajuste" ? "0" : "0.01"} step="0.01" value={movForm.cantidad}
                        onChange={e => setMovForm(f => ({ ...f, cantidad: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Precio medio sugerido (₲)</label>
                      <input type="number" min="0" value={movForm.precio_unitario}
                        onChange={e => setMovForm(f => ({ ...f, precio_unitario: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    {dep.activo && (<>
                      <label className="block text-slate-400 text-xs mb-1 font-body">Depósito</label>
                      <select value={movForm.deposito_id || ""} onChange={e => setMovForm(f => ({ ...f, deposito_id: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none mb-3">
                        <option value="">Depósito principal</option>
                        {dep.depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                      </select>
                    </>)}
                    <label className="block text-slate-400 text-xs mb-1 font-body">Motivo</label>
                    <select value={movForm.motivo || "ajuste_manual"} onChange={e => setMovForm(f => ({ ...f, motivo: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none">
                      {MOTIVOS_AJUSTE_STOCK.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1 font-body">Notas</label>
                    <input value={movForm.notas} onChange={e => setMovForm(f => ({ ...f, notas: e.target.value }))}
                      placeholder="Ej: Corrección inventario físico..."
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none" />
                  </div>
                  {tiposFactura.length > 0 && (
                    <div>
                      <label className="block text-slate-400 text-xs mb-1 font-body">
                        Obra / Tipo de Factura {movForm.tipo === "salida" ? "(para imputar el costo del material)" : "(opcional)"}
                      </label>
                      <select value={movForm.tipo_factura_id || ""}
                        onChange={e => setMovForm(f => ({ ...f, tipo_factura_id: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm font-body focus:outline-none">
                        <option value="">Sin obra asignada</option>
                        {tiposFactura.map(t => (
                          <option key={t.id} value={t.id}>{t.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button type="submit" disabled={savingMov}
                      className="flex items-center gap-1.5 px-4 py-1.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-xs disabled:opacity-60">
                      <Save className="w-3.5 h-3.5" /> {savingMov ? "Guardando..." : "Registrar"}
                    </button>
                    <button type="button" onClick={() => setShowMovForm(false)}
                      className="px-4 py-1.5 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors font-body text-xs">
                      Cancelar
                    </button>
                  </div>
                </form>
              )}

              {/* Historial */}
              <div>
                <h3 className="text-slate-400 text-xs font-body font-semibold mb-2">Historial de movimientos</h3>
                {loadingMov ? (
                  <div className="text-center py-8 text-slate-500 animate-pulse font-body">Cargando...</div>
                ) : movimientos.length === 0 ? (
                  <div className="text-center py-8 text-slate-600 font-body text-sm">Sin movimientos registrados</div>
                ) : (
                  <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {movimientos.map(m => (
                      <button key={m.id} type="button" onClick={() => setSelectedMovimiento(m)}
                        className="w-full flex items-center justify-between text-left bg-white/5 border border-white/10 rounded-lg px-3 py-2 hover:bg-white/10 hover:border-cyan-500/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg ${
                            m.tipo === "entrada" ? "bg-emerald-500/20" : m.tipo === "salida" ? "bg-red-500/20" : "bg-blue-500/20"
                          }`}>
                            {m.tipo === "entrada"
                              ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                              : m.tipo === "salida"
                              ? <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                              : <RotateCcw className="w-3.5 h-3.5 text-blue-400" />
                            }
                          </div>
                          <div>
                            <p className="text-cyan-300 text-[10px] font-mono mb-0.5 flex items-center gap-1">
                              <FileText className="w-3 h-3" /> {comprobanteNumero(m)}
                            </p>
                            <p className="text-white text-xs font-body">
                              <span className={`font-semibold ${
                                m.tipo === "entrada" ? "text-emerald-300" : m.tipo === "salida" ? "text-red-300" : "text-blue-300"
                              }`}>
                                {m.tipo === "entrada" ? "+" : m.tipo === "salida" ? "-" : "="}{m.cantidad}
                              </span>
                              {" "}{selectedProducto.unidad}
                              {m.motivo && <span className="text-slate-500 ml-1.5">({motivoAjusteLabel(m.motivo)})</span>}
                            </p>
                            {m.variante_texto && <p className="text-emerald-300 text-[10px]">{m.variante_texto}</p>}
                            {m.tipo_factura_nombre && <p className="text-violet-300 text-[10px]">Obra: {m.tipo_factura_nombre}</p>}
                            {m.notas && <p className="text-slate-500 text-[10px]">{m.notas}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-400 text-xs">{m.stock_anterior} → <span className="text-white font-semibold">{m.stock_nuevo}</span></p>
                          <p className="text-slate-600 text-[10px]">{m.fecha} · {m.usuario_nombre || ""}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Comprobante de movimiento ─────────────────────────── */}
      {selectedMovimiento && selectedProducto && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-xl overflow-hidden">
            <div className="px-6 py-5 border-b border-white/10 bg-white/5 flex items-start justify-between gap-4">
              <div>
                <p className="text-cyan-300 text-xs font-mono">{comprobanteNumero(selectedMovimiento)}</p>
                <h2 className="font-heading text-lg text-white mt-1">Comprobante de Ajuste de Stock</h2>
                <p className="text-slate-400 text-xs mt-1">{selectedMovimiento.fecha} · {selectedMovimiento.usuario_nombre || "Usuario no registrado"}</p>
              </div>
              <button onClick={() => setSelectedMovimiento(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-white/10 rounded-xl p-3 bg-white/5">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wide">Producto</p>
                  <p className="text-white text-sm font-semibold mt-1">{productoComprobanteNombre(selectedMovimiento)}</p>
                  <p className="text-slate-500 text-xs mt-0.5">Producto del inventario</p>
                </div>
                <div className="border border-white/10 rounded-xl p-3 bg-white/5">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wide">SKU</p>
                  <p className="text-white text-sm font-semibold mt-1">{productoComprobanteSku(selectedMovimiento)}</p>
                  <p className="text-slate-500 text-xs mt-0.5">Código interno</p>
                </div>
                <div className="border border-white/10 rounded-xl p-3 bg-white/5">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wide">Tipo de movimiento</p>
                  <p className={`text-sm font-semibold mt-1 ${
                    selectedMovimiento.tipo === "entrada" ? "text-emerald-300"
                    : selectedMovimiento.tipo === "salida" ? "text-red-300"
                    : "text-blue-300"
                  }`}>
                    {selectedMovimiento.tipo === "entrada" ? "Entrada" : selectedMovimiento.tipo === "salida" ? "Salida" : "Ajuste absoluto"}
                  </p>
                  <p className="text-slate-500 text-xs mt-0.5">{motivoAjusteLabel(selectedMovimiento.motivo)}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3 bg-slate-500/10 border border-slate-500/20">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wide">Cantidad</p>
                  <p className="text-white text-lg font-semibold mt-1">{selectedMovimiento.cantidad}</p>
                </div>
                <div className="rounded-xl p-3 bg-amber-500/10 border border-amber-500/20">
                  <p className="text-amber-300/80 text-[10px] uppercase tracking-wide">Stock anterior</p>
                  <p className="text-amber-200 text-lg font-semibold mt-1">{selectedMovimiento.stock_anterior}</p>
                </div>
                <div className="rounded-xl p-3 bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-emerald-300/80 text-[10px] uppercase tracking-wide">Stock nuevo</p>
                  <p className="text-emerald-200 text-lg font-semibold mt-1">{selectedMovimiento.stock_nuevo}</p>
                </div>
              </div>

              {selectedMovimiento.tipo_factura_nombre && (
                <div className="rounded-xl p-3 bg-violet-500/10 border border-violet-500/20">
                  <p className="text-violet-300/80 text-[10px] uppercase tracking-wide">Obra / Tipo de Factura</p>
                  <p className="text-violet-200 text-sm font-semibold mt-1">{selectedMovimiento.tipo_factura_nombre}</p>
                </div>
              )}

              <div className="border border-white/10 rounded-xl p-3 bg-white/5 min-h-[70px]">
                <p className="text-slate-500 text-[10px] uppercase tracking-wide">Notas</p>
                <p className="text-slate-300 text-sm mt-1">{selectedMovimiento.notas || "Sin notas"}</p>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => printComprobante(selectedMovimiento)}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={() => setSelectedMovimiento(null)}
                  className="px-4 py-2 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors text-sm">
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Formulario Unidad de Medida ──────────────────────── */}
      {showUnidadForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-base text-white flex items-center gap-2">
                <Ruler className="w-4 h-4 text-cyan-400" />
                {editingUnidadId ? "Editar unidad de medida" : "Nueva unidad de medida"}
              </h2>
              <button onClick={() => setShowUnidadForm(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveUnidad} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Código *</label>
                  <input required type="number" min="1" value={unidadForm.codigo}
                    onChange={e => setUnidadForm(f => ({ ...f, codigo: e.target.value }))}
                    placeholder="Ej: 21"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Sigla *</label>
                  <input required value={unidadForm.sigla}
                    onChange={e => setUnidadForm(f => ({ ...f, sigla: e.target.value.toUpperCase() }))}
                    placeholder="Ej: KG"
                    maxLength={10}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body font-mono focus:outline-none focus:border-cyan-500" />
                </div>
              </div>
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Nombre *</label>
                <input required value={unidadForm.nombre}
                  onChange={e => setUnidadForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Kilogramo"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Cantidad (factor)</label>
                  <input type="number" min="0.000001" step="any" value={unidadForm.cantidad}
                    onChange={e => setUnidadForm(f => ({ ...f, cantidad: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1 font-body">Peso Kg (opcional)</label>
                  <input type="number" min="0" step="any" value={unidadForm.peso_kg}
                    onChange={e => setUnidadForm(f => ({ ...f, peso_kg: e.target.value }))}
                    placeholder="Ej: 1"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
                </div>
              </div>
              <div>
                <label className="block text-slate-400 text-xs mb-1 font-body">Código DNIT *</label>
                <input required type="number" min="1" value={unidadForm.cod_dnit}
                  onChange={e => setUnidadForm(f => ({ ...f, cod_dnit: e.target.value }))}
                  placeholder="Ej: 77"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-cyan-500" />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={savingUnidad}
                  className="flex items-center gap-2 px-5 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-body text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" /> {savingUnidad ? "Guardando..." : "Guardar"}
                </button>
                <button type="button" onClick={() => setShowUnidadForm(false)}
                  className="px-5 py-2 bg-white/10 text-slate-300 rounded-lg hover:bg-white/20 transition-colors font-body text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
